// Correction-form rendering + submission.
//
// Every renderable row gets a horizontal strip of buttons (one per kind it
// supports). Clicking a button reveals an inline form whose fields depend
// on the kind. Submit POSTs the event to the Worker and toggles the row
// into a "submitted" state — no client-side state graph beyond that.

import { ApiError, deleteJson, postJson } from "./api";
import { inFixtureMode } from "./fixture";
import { OPTIMISTIC_STATE_KINDS } from "./optimistic_feedback";
import { gateHintEvents } from "./session_events";
import { actionItemSlug, subcategoryKey, threadKey, workItemKey } from "./target_id";

// #74a: the wire kinds the Worker gates against the recipient's own run events
// (the close gates + the reverse blocker-adding/placement guards — see
// worker/src/routes/data.ts). A submit of one of these carries `client_events`
// so the gate replays a just-POSTed move-out that Cloudflare KV's eventually-
// consistent list() may not have indexed yet (the emptied-sub-category close
// blocker). Mirrors the worker's BLOCKER_ADDING_KINDS ∪ PLACEMENT_KINDS ∪ the
// three close kinds; over-inclusion is harmless (the Worker ignores the hint
// for non-gated kinds), under-inclusion would silently re-open the race.
const GATE_HINT_KINDS: ReadonlySet<string> = new Set([
  "thread_close",
  "work_item_close",
  "subcategory_close",
  "action_reopen",
  "work_item_reopen",
  "action_move_to_item",
  "item_move_to_category",
  "item_move_to_subcategory",
  "place_as_action",
  "place_from_unfiled",
]);

// Mirrors agent/config.py::slugify so the SPA can derive a thread slug
// from a recipient-typed display name without an extra round-trip.
// Idempotent. Empty input → "thread" (matches the agent fallback).
export function slugify(name: string): string {
  const s = name.toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return s || "thread";
}

// Pull the thread slug out of the SPA's thread-header targetId, which
// is encoded as "thread:<slug>" by target_id.ts::threadKey. Mirrors the
// absorber's _parse_thread_slug_from_target so the same input produces
// the same slug on both sides.
function threadSlugFromTargetId(targetId: string): string {
  if (targetId.startsWith("thread:")) {
    return targetId.slice("thread:".length).split("/")[0] ?? "";
  }
  return targetId;
}

// Reshape the raw form payload into the shape the agent absorber expects.
// Pure function so it's unit-testable without mocking the DOM / fetch.
// Returns a fresh object; does not mutate the input.
// The 4 recipient-settable priorities (Informational is synth-only — see
// FormControlSpec.priorityStatus). `Active` first so it's the default
// selection. Shared by the action_add / place_from_unfiled / place_as_action
// priority dropdowns.
const SETTABLE_PRIORITY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "Active", label: "Active" },
  { value: "Critical", label: "Critical" },
  { value: "Delay Potential", label: "Delay Potential" },
  { value: "On Hold", label: "On Hold" },
];

export function reshapePayloadForKind(
  kind: string,
  payload: Record<string, unknown>,
  targetId: string,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...payload };

  // thread_reject: form collects evidence_item_ids as a comma-separated
  // string; the absorber expects a list. Empty input → drop the key so
  // the absorber treats it as a project-level note with no per-item
  // reclassify. (Slice 8 Wave 3.)
  if (kind === "thread_reject") {
    const raw = typeof out.evidence_item_ids === "string"
      ? out.evidence_item_ids
      : "";
    const ids = raw.split(",").map((s) => s.trim()).filter((s) => s);
    if (ids.length) out.evidence_item_ids = ids;
    else delete out.evidence_item_ids;
  }

  // responsible_party_assign: empty email string → drop the key.
  // Absorber expects optional string, not empty string. (Slice 8 Wave 3.)
  // is_new_person: FormData only includes checkboxes when checked, so
  // presence of any value → true, absence → false. Absorber defaults
  // is_new_person to False already, but emitting it explicitly keeps
  // the wire payload faithful to the form state.
  if (kind === "responsible_party_assign") {
    if (typeof out.email === "string" && !out.email.trim()) {
      delete out.email;
    }
    out.is_new_person = "is_new_person" in payload;
  }

  // thread_rename: form collects { new_name }; absorber requires
  // { from_slug, to_name }. Source slug comes from the thread-header
  // row's targetId. Rename is name-only under the slug-decouple model —
  // the slug stays put as the stable identifier; slug-changing
  // operations go through thread_merge.
  if (kind === "thread_rename" && typeof out.new_name === "string") {
    out.from_slug = threadSlugFromTargetId(targetId);
    out.to_name = (out.new_name as string).trim();
    delete out.new_name;
  }

  // thread_merge: form collects { merge_into } (the operator picks the
  // destination by visible name via datalist; the input value is the
  // slug); absorber requires { source_slugs[], target_slug }. Source
  // slug comes from the thread-header row's targetId. Slugify-fallback
  // catches the case where the operator typed a human name that the
  // datalist didn't intercept (e.g., mobile browser without datalist
  // support, or a typo that didn't match an option).
  if (kind === "thread_merge" && typeof out.merge_into === "string") {
    out.source_slugs = [threadSlugFromTargetId(targetId)];
    out.target_slug = ensureSlug(out.merge_into as string);
    delete out.merge_into;
  }

  // thread_retag: form collects { to_thread } from a thread datalist.
  // Same slugify-fallback as thread_merge for mobile / typo cases.
  // Absorber already requires `to_thread` (no key remap needed).
  if (kind === "thread_retag" && typeof out.to_thread === "string") {
    out.to_thread = ensureSlug(out.to_thread as string);
  }

  // --- v4 (Task #9) reshapes -----------------------------------------
  //
  // The form's <input> elements all yield strings; the agent absorber
  // expects typed values for specific fields. Drop-empty + coerce
  // here so the wire payload matches what the Pydantic models accept.

  // action_open_question_resolve (#52 -> #52b): the picker's selected value is
  // prefixed so we can route it to the right absorber key — `qid:<hex>` for a
  // question carrying a stable identity (the preferred key, survives a synth
  // reword), or `text:<text>` for a legacy question that has none yet. A raw
  // free-text fallback (no prefix) stays `question_text`.
  if (kind === "action_open_question_resolve"
      && typeof out.question_text === "string") {
    const ref = out.question_text;
    if (ref.startsWith("qid:")) {
      out.qid = ref.slice(4);
      delete out.question_text;
    } else if (ref.startsWith("text:")) {
      out.question_text = ref.slice(5);
    }
  }

  // invoice_add: amount comes as string from the text input; absorber
  // coerces but rejects on parse failure. Drop empty strings so the
  // absorber treats amount as absent rather than failing to coerce
  // "".
  if (kind === "invoice_add"
      && typeof out.amount === "string"
      && !out.amount.trim()) {
    delete out.amount;
  }

  // action_set_due / action_add / action_priority_change: empty due
  // string → drop (clearing due is signaled with `null`, not `""`).
  // Decision 3C added `due` to action_priority_change for the
  // combined form path.
  if ((kind === "action_set_due"
        || kind === "action_add"
        || kind === "action_priority_change")
      && typeof out.due === "string"
      && !out.due.trim()) {
    out.due = null;
  }

  // A³/A² (2026-06-03): place_from_unfiled is now the **Place as Item**
  // tier only (create a new Item + required first Action). Derive the
  // Item slug from the name; slugify the Category picker value (datalist
  // gives a slug, ensureSlug tolerates typed names). source_ref +
  // unfiled_id are injected by the submit handler from spec.bucketContext.
  if (kind === "place_from_unfiled") {
    delete out.mode;
    delete out.target_work_item_slug;  // legacy place-under-existing mode → place_as_action
    if (typeof out.new_item_name === "string"
        && (out.new_item_name as string).trim()) {
      out.new_item_work_item_slug = slugify((out.new_item_name as string).trim());
    }
    if (typeof out.new_item_thread_slug === "string") {
      out.new_item_thread_slug = ensureSlug(out.new_item_thread_slug as string);
    }
    for (const k of [
      "new_item_thread_slug", "new_item_subcategory_slug",
      "new_item_work_item_slug",
      "new_item_name", "new_item_narrative", "new_item_action_text",
    ]) {
      if (typeof out[k] === "string" && !(out[k] as string).trim()) {
        delete out[k];
      }
    }
  }

  // A³/A² Place as Category: derive the Category slug from the name.
  if (kind === "place_as_category" && typeof out.name === "string"
      && (out.name as string).trim()) {
    out.thread_slug = slugify((out.name as string).trim());
  }

  // A³/A² Place as Action: derive the Action slug from the text; slugify
  // the target Item picker value (datalist → slug; ensureSlug tolerates names).
  if (kind === "place_as_action") {
    if (typeof out.text === "string" && (out.text as string).trim()) {
      // actionItemSlug (not slugify): mirror the agent's 60-char cap so the slug
      // matches what the absorber stores (SLUG-CAP-MISMATCH, 2026-10-06).
      out.action_item_slug = actionItemSlug((out.text as string).trim());
    }
    if (typeof out.target_work_item_slug === "string") {
      out.target_work_item_slug = ensureSlug(out.target_work_item_slug as string);
    }
  }

  // #60 PR4-v2: the Place tiers carry a `complete` checkbox (born-closed when
  // checked). FormData only includes a checkbox when checked → presence=true.
  if (kind === "place_as_category" || kind === "place_from_unfiled"
      || kind === "place_as_action") {
    out.complete = "complete" in payload;
  }

  // B8: slugify the destination Item value (datalist gives a slug; ensureSlug
  // tolerates a typed name).
  if (kind === "action_move_to_item"
      && typeof out.to_work_item_slug === "string") {
    out.to_work_item_slug = ensureSlug(out.to_work_item_slug as string);
  }

  // #60 PR4-v2 merge_into_existing: keep only the active tier's slug fields
  // (hidden rows already cleared; this prunes empties + the resolved action
  // pick helper), with ensureSlug fallback for free-typed values.
  if (kind === "merge_into_existing") {
    delete out.target_action_pick;  // resolved into the two slugs pre-reshape
    delete out.merge_scope;          // UI-only toggle, not a wire field
    for (const k of [
      "target_thread_slug", "target_work_item_slug", "target_action_item_slug",
    ]) {
      if (typeof out[k] !== "string") continue;
      const v = (out[k] as string).trim();
      if (!v) { delete out[k]; continue; }
      out[k] = ensureSlug(v);
    }
    if (typeof out.note === "string" && !(out.note as string).trim()) {
      delete out.note;
    }
  }

  // Decision 7B: silent slug derivation for the three v4 "Add"
  // affordances. The forms no longer surface a slug field; we
  // compute it from the human-facing name/text on submit.
  if (kind === "item_add" && typeof out.name === "string"
      && (out.name as string).trim()) {
    out.work_item_slug = slugify((out.name as string).trim());
  }
  if (kind === "category_add" && typeof out.name === "string"
      && (out.name as string).trim()) {
    out.thread_slug = slugify((out.name as string).trim());
    // N4b: the "(No sub-project)" option is an empty-string value; drop the key
    // so the payload matches a legacy event (no field) and the absorber's None
    // default → a genuine NULL sub_project, not "". Phase always has a real
    // value ("general" is the default band), so it rides through as-is.
    if (typeof out.sub_project_slug === "string"
        && !(out.sub_project_slug as string).trim()) {
      delete out.sub_project_slug;
    }
  }
  if (kind === "action_add" && typeof out.text === "string"
      && (out.text as string).trim()) {
    // actionItemSlug (not slugify): mirror the agent's 60-char cap so the slug
    // matches what the absorber stores (SLUG-CAP-MISMATCH, 2026-10-06).
    out.action_item_slug = actionItemSlug((out.text as string).trim());
  }

  // Decision 7A: item_move_to_category — the datalist value IS the
  // slug, but slugify any free-text typo so the absorber gets a
  // canonical form.
  if (kind === "item_move_to_category"
      && typeof out.to_thread_slug === "string") {
    out.to_thread_slug = ensureSlug(out.to_thread_slug as string);
  }
  // #76: action_to_item — derive the new Item's candidate slug from its name
  // (the absorber uniquifies it) + canonicalize the destination Category slug
  // (the <select> value is already a slug; ensureSlug guards a stray typo).
  if (kind === "action_to_item") {
    if (typeof out.name === "string") {
      out.name = (out.name as string).trim();
      out.new_work_item_slug = ensureSlug(out.name as string);
    }
    if (typeof out.to_thread_slug === "string") {
      out.to_thread_slug = ensureSlug(out.to_thread_slug as string);
    }
    // `a` (Ray 2026-07-22): canonicalize the optional Sub-category pick; drop
    // "(Ungrouped)" ("") so the wire carries a slug only when a real group was
    // chosen (the absorber reads new_item_subcategory_slug; a missing/empty value
    // files the promoted Item directly under the Category).
    if (typeof out.new_item_subcategory_slug === "string") {
      const sc = (out.new_item_subcategory_slug as string).trim();
      if (sc) out.new_item_subcategory_slug = ensureSlug(sc);
      else delete out.new_item_subcategory_slug;
    }
  }
  // B10: `also_close` is a UI-only flag (the submit handler fires a separate
  // work_item_close); it never belongs in the move payload.
  if (kind === "item_move_to_category") {
    delete out.also_close;
  }

  // Task 3: candidate_to_item — derive work_item_slug from the name
  // (client never types a slug), slugify the Category picker value, and
  // drop the first-action priority when no action text was entered.
  if (kind === "candidate_to_item") {
    if (typeof out.name === "string" && (out.name as string).trim()) {
      out.work_item_slug = slugify((out.name as string).trim());
    }
    if (typeof out.target_thread_slug === "string") {
      out.target_thread_slug = ensureSlug(out.target_thread_slug as string);
    }
    if (!(typeof out.initial_action_text === "string"
          && (out.initial_action_text as string).trim())) {
      delete out.initial_action_text;
      delete out.initial_action_priority;
    }
  }

  // Task 3: work_item_merge — normalize the multi-selected source slugs
  // (ensureSlug each, drop empties). Accept a bare string as well as a list:
  // the checkbox-list multiselect yields a single string for one selection,
  // and the absorber requires a list — coercing here (defense in depth with
  // the submit-path array re-collection) stops single-source merges dropping.
  if (kind === "work_item_merge" && out.source_work_item_slugs != null) {
    out.source_work_item_slugs = coerceSlugList(out.source_work_item_slugs);
  }

  // Open improvement #3: action_merge — normalize source action slugs.
  if (kind === "action_merge" && out.source_action_item_slugs != null) {
    out.source_action_item_slugs = coerceSlugList(out.source_action_item_slugs);
  }

  // B3: every Sub-project pre-filter <select> is UI-only (it just narrows a
  // picker) — strip them all so none ride any wire. Harmless when absent.
  //  - merge_sub_project / merge_item: the merge pickers;
  //  - promote_sub_project: action_to_item's Category filter;
  //  - filter_sub_project: the datalist pickers (move-to-item, place-as-action,
  //    candidate_to_item, merge_into_existing).
  // F2 (2026-09-24): the Sub-project → Category cascade adds two more UI-only
  // filter <select>s — filter_category (datalist pickers) and merge_category
  // (work_item_merge / item_merge_into). Strip them too.
  delete out.merge_sub_project;
  delete out.merge_item;
  delete out.merge_category;
  delete out.promote_sub_project;
  delete out.filter_sub_project;
  delete out.filter_item;
  delete out.filter_category;

  // --- #38 sub-category reshapes -------------------------------------------
  // subcategory_add: the submit handler mints `subcategory_slug` (collision-
  // suffixed against the parent's groups + session-pending adds, via
  // mintSubcategorySlug). Derive a plain slugify fallback here so a direct
  // reshape call (unit tests / no-spec path) still yields a slug; trim name.
  if (kind === "subcategory_add") {
    if (typeof out.name === "string") out.name = (out.name as string).trim();
    const minted = typeof out.subcategory_slug === "string"
      && (out.subcategory_slug as string).trim();
    if (!minted && typeof out.name === "string" && out.name) {
      out.subcategory_slug = slugify(out.name as string);
    }
  }
  // subcategory_rename: trim the new display name (slug is stable, parsed from
  // the target id absorber-side — no slug field on the wire).
  if (kind === "subcategory_rename" && typeof out.to_name === "string") {
    out.to_name = (out.to_name as string).trim();
  }
  // item_move_to_subcategory: the <select> submits the destination group slug,
  // or "" for the explicit "(ungrouped)" choice → null (the absorber's ungroup
  // sentinel). A real slug is ensureSlug'd for canonical form.
  if (kind === "item_move_to_subcategory"
      && typeof out.to_subcategory_slug === "string") {
    const trimmed = (out.to_subcategory_slug as string).trim();
    out.to_subcategory_slug = trimmed ? ensureSlug(trimmed) : null;
  }

  // Universal v4 hygiene: trim empty optionals on these kinds so the
  // absorber's `if not value` checks don't see whitespace.
  const trimEmpty: Record<string, string[]> = {
    item_add: [
      "narrative", "responsible_party_name",
      "responsible_party_email", "start_date",
    ],
    category_add: ["description"],
    action_add: [
      "narrative", "reason", "impact",
      "off_hold_condition", "assignee", "due",
    ],
    // Decision 3C: combined form may submit conditional fields;
    // trim empties so the absorber's optional-field checks behave.
    action_priority_change: ["reason", "impact", "off_hold_condition"],
    // Decision 7B: silent slugs — narrative/responsible/etc. trim.
    // C2 (Ray 2026-10-18): new_item_action_narrative trims too (optional first-
    // action narrative → absorber's `if not value` sees a real empty, not spaces).
    place_from_unfiled: ["new_item_narrative", "new_item_action_narrative"],
    // Place-as-Action narrative (Ray 2026-10-21): trim the optional Action narrative
    // so the absorber's `if not value` sees a real empty, not whitespace.
    place_as_action: ["narrative"],
    // A³/A² (2026-06-03): Place-as-Category optional description.
    place_as_category: ["description"],
    invoice_add: ["received_date", "due_date", "blocking_note", "status"],
    invoice_blocking_note_set: ["blocking_note"],
    action_open_question_add: ["raised_by"],
    // Task 3
    candidate_to_item: ["narrative", "initial_action_text"],
    work_item_merge: ["target_name"],
    // Open improvement #3
    action_merge: ["target_text"],
    // #38: drop an empty close/reopen reason (absent → None absorber-side).
    subcategory_close: ["reason"],
    subcategory_reopen: ["reason"],
    // #74b: delete carries an optional reason; drop it when blank.
    subcategory_delete: ["reason"],
  };
  const trimKeys = trimEmpty[kind];
  if (trimKeys) {
    for (const k of trimKeys) {
      if (typeof out[k] === "string" && !(out[k] as string).trim()) {
        delete out[k];
      }
    }
  }

  return out;
}

// Decision 3B (close-out 2026-05-25): kind→field map for "Add X" vs
// "Edit X" label switching. Keyed on the metadata-edit kinds where
// the populated state is meaningful; everything else uses KIND_LABEL.
const ADD_EDIT_FIELD_BY_KIND: Record<string, string> = {
  action_set_reason: "reason",
  action_set_impact: "impact",
  action_set_off_hold_condition: "off_hold_condition",
  action_set_due: "due",
};

function labelForKind(
  kind: string,
  populated?: ReadonlySet<string>,
  reportCascadeCount?: number,
  associationCascadeCount?: number,
  priorityCascadeCount?: number,
): string {
  // B (2026-09-16, PSU→item-only): the Item report control now mixes the Item's
  // OWN PSU mark with the "all N actions" Key Issue Log cascade, so the entry is a
  // plain "Report inclusion" — the checkboxes carry the per-report scope (D4).
  if (kind === "item_report_inclusion") {
    void reportCascadeCount;
    return "Report inclusion";
  }
  // A2 (Ray 2026-09-15): the Item-tier association control cascades to all the
  // Item's open Actions — name the scope + count, mirroring report inclusion.
  if (kind === "item_set_associations") {
    const n = associationCascadeCount ?? 0;
    return n === 1
      ? "Set associations — all 1 action"
      : `Set associations — all ${n} actions`;
  }
  // E-5 (Ray 2026-09-16): the Item-tier priority control cascades to all the
  // Item's open Actions — name the scope + count, mirroring associations.
  if (kind === "item_set_priority") {
    const n = priorityCascadeCount ?? 0;
    return n === 1
      ? "Set priority — all 1 action"
      : `Set priority — all ${n} actions`;
  }
  const field = ADD_EDIT_FIELD_BY_KIND[kind];
  if (!field) return KIND_LABEL[kind] ?? kind;
  const isSet = populated?.has(field) ?? false;
  const verb = isSet ? "Edit" : "Add";
  const noun = field === "off_hold_condition"
    ? "off-hold condition"
    : field === "due"
      ? "due date"
      : field;
  return `${verb} ${noun}`;
}

// Slugify-on-submit safety net: when the operator types a free-form name
// that didn't match a datalist option, fall back to slugify() so the
// resulting slug at least has a chance of matching agent-side. Already-
// well-formed slugs pass through unchanged.
function ensureSlug(value: string): string {
  const trimmed = value.trim();
  if (/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(trimmed)) return trimmed;
  return slugify(trimmed);
}

/** Coerce a merge form's source-slugs field to a clean slug array, accepting
 * a bare string (single checkbox selection) or a list. Empties/non-strings
 * dropped, each ensureSlug'd. Mirrors the absorber's `_coerce_slug_list` so
 * the SPA→absorber contract holds even if one side regresses. */
function coerceSlugList(raw: unknown): string[] {
  const items = Array.isArray(raw) ? raw : [raw];
  return items
    .filter((s): s is string => typeof s === "string" && s.trim().length > 0)
    .map((s) => ensureSlug(s.trim()));
}

/** #38: mint a collision-free sub-category slug from a recipient-typed name.
 * slugify, then suffix `-2`, `-3`, … against the parent Category's existing
 * group slugs. The `existing` set MUST span OPEN *and* CLOSED groups (the
 * per-(project, thread) UNIQUE space spans both, and the absorber's
 * idempotent name-refresh on a colliding slug would silently rename the wrong
 * group instead of creating a new one) AND this session's pending adds (else
 * two same-named adds in one sitting collapse via that same name-refresh
 * branch — the binding amendment). Pure → unit-testable. */
export function mintSubcategorySlug(
  name: string,
  existing: Iterable<string>,
): string {
  const base = slugify(name);
  const taken = new Set(existing);
  if (!taken.has(base)) return base;
  for (let i = 2; ; i += 1) {
    const candidate = `${base}-${i}`;
    if (!taken.has(candidate)) return candidate;
  }
}

/** #38 (operator feedback 2026-06-12): mint BOTH the collision-free slug and a
 * disambiguated DISPLAY name. When the slug had to be suffixed (`-2`, `-3`, …),
 * mirror the same number into the visible name ("Test Group" → "Test Group 2")
 * so two same-named groups in one session are visually distinct, not just
 * slug-distinct. No collision → name unchanged. Pure → unit-testable. */
export function mintSubcategory(
  name: string,
  existing: Iterable<string>,
): { slug: string; name: string } {
  const trimmed = name.trim();
  const base = slugify(trimmed);
  const slug = mintSubcategorySlug(trimmed, existing);
  if (slug === base) return { slug, name: trimmed };
  const suffix = slug.slice(base.length + 1);   // the `-N` tail → "N"
  return {
    slug,
    name: /^\d+$/.test(suffix) ? `${trimmed} ${suffix}` : trimmed,
  };
}

/** #63: the human label shown for an Item in a move/place picker —
 * "N.M Item name (Category)". Used BOTH to build the picker option and to map
 * the picked label back to its slug at submit, so the two never diverge. */
export function itemOptionLabel(i: ItemOption): string {
  return [
    i.display_number ? `${i.display_number}` : null,
    i.name,
    i.thread_name ? `(${i.thread_name})` : null,
  ].filter(Boolean).join(" ");
}

/** #63 follow-up (2026-06-09): the human label for a Category in a move/merge
 * picker — "N Category name" (the number when present; client asked for it).
 * Shared by the picker render and the submit-time slug resolution. */
export function threadOptionLabel(t: ThreadOption): string {
  return t.display_number ? `${t.display_number} ${t.name}` : t.name;
}

/** Phase key → label for the reorg toast. Inlined (not imported from
 * briefings.ts) to avoid a forms↔briefings import cycle; the four keys are
 * stable (mirrors briefings.PHASE_LABELS / hours_log.PHASE_LABELS). */
const PHASE_LABEL: Record<string, string> = {
  pre_development: "Pre-Development",
  pre_construction: "Pre-Construction",
  construction: "Construction",
  general: "General",
};

/** The pending-feedback banner text in the pencil popover's "Submitted" section.
 * Three tiers, keyed on signals the app already maintains (no drift), from the
 * kinds in the Submitted list (Ray 2026-09-17):
 *   1. report placement (PSU / Key Issue Log mark or 558 tag) — mutates the
 *      Worker's report snapshot immediately (kil_mutate.ts) + triggers
 *      refresh-on-natural-action report regeneration (sync_kil.ts) → takes effect
 *      in the recipient's REPORTS right away. Checked FIRST because these kinds
 *      are also optimistic, but the report-real-time framing is more specific.
 *   2. any other OPTIMISTIC kind (OPTIMISTIC_STATE_KINDS — close/reopen, moves,
 *      merges, priority, narrative, associations, subcategory ops…) echoes
 *      in-session NOW (the reorg family even shows a "Moved to X" toast).
 *   3. non-optimistic / queued (comment, flag_next_week, renames,
 *      responsible_party_assign, adds, invoices, placement, date_correction) —
 *      genuinely surfaces on the next cron, so the original line is accurate.
 * Empty list → the queued line (the section only renders with ≥1 event anyway).
 * Pure → unit-testable. */
const REPORT_PLACEMENT_KINDS: ReadonlySet<string> = new Set([
  "item_kil_mark", "item_kil_unmark",
  "action_kil_mark", "action_kil_unmark",
  "item_set_report_tags", "action_set_report_tags",
  "item_report_inclusion", "action_report_inclusion",
]);
export function pendingBannerText(kinds: readonly string[]): string {
  if (kinds.some((k) => REPORT_PLACEMENT_KINDS.has(k))) {
    return "Project Status Update / Key Issue Log placements take effect in your "
      + "reports right away; all changes are also confirmed on next week's briefing.";
  }
  const optimistic = new Set(OPTIMISTIC_STATE_KINDS);
  if (kinds.some((k) => optimistic.has(k))) {
    return "Shown here now — confirmed on next week's briefing.";
  }
  return "Pending — these changes will be reflected in next week's briefing.";
}

/** Reorg fan-out (Ray 2026-09-16): the confirmation-toast MESSAGE for a just-
 * submitted Reorganize op, keyed on wireKind (so item_merge_into→work_item_merge
 * and action_merge_into→action_merge already resolved). Names the target with
 * the right verb (Moved to / Merged into / Promoted to). Returns null for a
 * non-reorg kind or an unresolvable label (caller then keeps the generic
 * "Submitted"). Undo-safety is decided separately by the caller from its
 * composite flags. Pure → unit-testable. */
export function reorgToastMessage(
  wireKind: string,
  payload: Record<string, unknown>,
  opts: {
    itemOptions?: readonly ItemOption[];
    threadOptions?: readonly ThreadOption[];
    subProjectOptions?: readonly { value: string; label: string }[];
    subcategoryOptions?: readonly SubcategoryOption[];
    mergeTargetLabel?: string;
  } = {},
): string | null {
  const {
    itemOptions = [], threadOptions = [], subProjectOptions = [],
    subcategoryOptions = [], mergeTargetLabel,
  } = opts;
  const str = (v: unknown): string => (typeof v === "string" ? v : "");
  switch (wireKind) {
    case "action_move_to_item": {
      const o = itemOptions.find((i) => i.slug === str(payload.to_work_item_slug));
      return o ? `Moved to ${itemOptionLabel(o)}` : null;
    }
    case "item_move_to_category": {
      const o = threadOptions.find((t) => t.slug === str(payload.to_thread_slug));
      return o ? `Moved to ${threadOptionLabel(o)}` : null;
    }
    case "item_move_to_subcategory": {
      const sc = subcategoryOptions.find((s) => s.slug === str(payload.to_subcategory_slug));
      return sc ? `Moved to ${sc.name}` : null;
    }
    case "category_move_to_phase": {
      const ph = str(payload.to_phase);
      return ph ? `Moved to ${PHASE_LABEL[ph] ?? ph}` : null;
    }
    case "category_move_to_subproject": {
      const sp = subProjectOptions.find((s) => s.value === str(payload.to_sub_project_slug));
      return sp ? `Moved to ${sp.label}` : null;
    }
    case "work_item_merge":
    case "action_merge":
      return mergeTargetLabel ? `Merged into ${mergeTargetLabel}` : null;
    case "thread_merge": {
      const o = threadOptions.find((t) => t.slug === str(payload.target_slug));
      if (o) return `Merged into ${threadOptionLabel(o)}`;
      return mergeTargetLabel ? `Merged into ${mergeTargetLabel}` : null;
    }
    case "action_to_item": {
      const nm = str(payload.name);
      return nm ? `Promoted to new Item: ${nm}` : null;
    }
    default:
      return null;
  }
}

/** B7 core retract, module-level so both the per-row Undo (buildUndoControl,
 * inside the popover closure) and the inline move-confirmation Undo (inside the
 * submit handler, a separate closure) share ONE retract path and can't diverge.
 * DELETE the pending event from the Worker (skipped in fixture mode — the static
 * server 501s), then bubble `feedback-retracted` so the viewer (app.ts) drops it
 * from the shared feedback array, re-derives the optimistic layer, and re-renders
 * (a move reverts → the row returns to its source). Throws on a server error
 * (e.g. 409 already-absorbed) so callers can surface the message. The closure-
 * local Submitted-list bookkeeping stays with each caller. */
export async function retractFeedbackEvent(
  runId: string, eventId: string, kind: string, targetId: string,
): Promise<void> {
  if (!inFixtureMode()) {
    await deleteJson<{ retracted: string }>(
      `/feedback/${encodeURIComponent(runId)}/${encodeURIComponent(eventId)}`,
    );
  }
  document.dispatchEvent(
    new CustomEvent("feedback-retracted", {
      detail: { event_id: eventId, kind, targetId },
    }),
  );
}

/** P-5 fan-out (Ray 2026-09-16): a bottom snackbar for a completed move —
 * "Moved to X" + an Undo. Lives on document.body (NOT the popover, which the
 * move's re-render tears down) and auto-dismisses. One at a time (a second move
 * replaces the first, Gmail-style). `onUndo` runs the retract; a thrown ApiError
 * message (e.g. 409 already-absorbed → the inverse move is the path now) is
 * surfaced. `onUndo` is null for a COMPOSITE move (a deep move that also files a
 * sub-category, or a Move & Close): those fire more than one event, so a single-
 * event retract would only partially revert — the destination is still named,
 * but Undo is withheld (revert via the matching inverse action / per-row history
 * Undo). Exported for the unit test's DOM assertion. */
export function showMoveToast(
  message: string,
  onUndo: (() => Promise<void>) | null,
  dismissMs = 6000,
): void {
  if (typeof document === "undefined") return;
  document.querySelector(".move-toast")?.remove();
  const toast = document.createElement("div");
  toast.className = "move-toast";
  toast.setAttribute("role", "status");
  const text = document.createElement("span");
  text.className = "move-toast-text";
  text.textContent = message;
  if (!onUndo) {
    // Composite move — name the destination only, no (partial) Undo.
    toast.appendChild(text);
    document.body.appendChild(toast);
    window.setTimeout(() => toast.remove(), dismissMs);
    return;
  }
  const undo = document.createElement("button");
  undo.type = "button";
  undo.className = "move-toast-undo";
  undo.textContent = "Undo";
  undo.title = "Undo this move";
  let timer = 0;
  const dismiss = (): void => {
    if (timer) window.clearTimeout(timer);
    toast.remove();
  };
  undo.addEventListener("click", async () => {
    if (timer) window.clearTimeout(timer);
    undo.disabled = true;
    undo.textContent = "Undoing…";
    try {
      await onUndo();
      text.textContent = "Move undone.";
      undo.remove();
      timer = window.setTimeout(dismiss, 2500);
    } catch (err) {
      undo.remove();
      const serverMessage =
        err instanceof ApiError
        && typeof (err.body as { message?: unknown } | null)?.message === "string"
          ? (err.body as { message: string }).message
          : null;
      text.textContent = serverMessage
        ?? "Couldn't undo — reverse it with the matching move.";
      timer = window.setTimeout(dismiss, 5000);
    }
  });
  toast.appendChild(text);
  toast.appendChild(undo);
  document.body.appendChild(toast);
  timer = window.setTimeout(dismiss, dismissMs);
}

/** #63: the move/merge target pickers display the friendly Category name /
 * "N.M Item (Category)" label (the slug is a technical artifact the client
 * never needs to see). Resolve a picked label back to the STABLE slug here,
 * via the picker's own options — NOT slugify: a renamed Category keeps its
 * original slug, so `slug !== slugify(name)`. Mutates `raw` in place; a typed
 * value matching no option is left untouched so reshape's ensureSlug() still
 * handles the free-text path. Pure → unit-testable. */
export function resolvePickedTargetSlugs(
  raw: Record<string, unknown>,
  threadOptions: ThreadOption[] = [],
  itemOptions: ItemOption[] = [],
): void {
  const threadBySlug = new Set(threadOptions.map((t) => t.slug));
  // Accept either the full picker label ("N name") or the bare name (a user
  // who types just the name); both resolve to the stable slug.
  const threadByKey = new Map<string, string>();
  for (const t of threadOptions) {
    threadByKey.set(threadOptionLabel(t), t.slug);
    if (!threadByKey.has(t.name)) threadByKey.set(t.name, t.slug);
  }
  for (const f of [
    "merge_into", "to_thread_slug", "target_thread_slug", "new_item_thread_slug",
  ]) {
    const v = raw[f];
    if (typeof v !== "string") continue;
    if (threadBySlug.has(v)) continue;        // already a slug — leave it
    if (threadByKey.has(v)) raw[f] = threadByKey.get(v);
  }
  const itemBySlug = new Set(itemOptions.map((i) => i.slug));
  const itemByLabel = new Map(itemOptions.map((i) => [itemOptionLabel(i), i.slug]));
  for (const f of ["target_work_item_slug", "to_work_item_slug"]) {
    const v = raw[f];
    if (typeof v !== "string") continue;
    // Already a known slug (defensive — e.g. a programmatic value): leave it.
    if (itemBySlug.has(v)) continue;
    if (itemByLabel.has(v)) raw[f] = itemByLabel.get(v);
  }
}

/** #60 PR4-v2: resolve the place_new pseudo-kind to its real wire kind by the
 * `place_tier` select. Returns the wire kind; the per-tier raw fields are
 * already named to match that kind's reshape. Defaults to place_from_unfiled
 * (Item) — the most common pick — if the tier is somehow unset. */
export function resolvePlaceNewKind(raw: Record<string, unknown>): FeedbackKind {
  const tier = raw.place_tier;
  delete raw.place_tier;  // not a wire field
  delete raw.place_sub_project;  // B3: UI-only pre-filter, not a wire field
  if (tier === "category") return "place_as_category";
  if (tier === "action") return "place_as_action";
  return "place_from_unfiled";
}

/** #38 feedback 2.1: resolve the item_move pseudo-kind to its real wire kind
 * by the `move_tier` select, pruning the inactive tier's fields (a hidden
 * <select> still submits via FormData, and reshape copies every key — without
 * the prune a stray to_subcategory_slug would ride the Category-move wire
 * payload). Defaults to the Category tier if the select is somehow unset.
 * Mutates `raw` in place; pure → unit-testable. */
export function resolveItemMoveKind(raw: Record<string, unknown>): FeedbackKind {
  const tier = raw.move_tier;
  delete raw.move_tier;  // not a wire field
  if (tier === "subcategory") {
    delete raw.to_thread_slug;
    delete raw.also_close;   // B10 Move & Close is a Category-move concept
    return "item_move_to_subcategory";
  }
  delete raw.to_subcategory_slug;
  return "item_move_to_category";
}

/** #73 reverse merge: resolve the item_merge_into pseudo. The recipient is on a
 * CHILD Item (childTargetId) and picked an existing primary (raw.merge_primary_
 * item — a work_item slug). Mutates `raw` into the work_item_merge wire payload
 * (source = [this child]; the picker field dropped) and returns the POST target
 * id for the PRIMARY. The thread segment is cosmetic — the absorber's
 * _parse_work_item_target discards it and resolves the primary by work_item slug
 * project-wide — so the child's own thread is used and a cross-Category primary
 * still binds. Returns null if no primary was picked (the <select> is required,
 * so the empty case is already blocked client-side; the caller then keeps the
 * child target as a no-op fallback). Pure → unit-testable. */
export function resolveItemMergeInto(
  raw: Record<string, unknown>,
  childTargetId: string,
): string | null {
  const primary = String(raw.merge_primary_item ?? "").trim();
  delete raw.merge_primary_item;
  const childWi = (childTargetId.split("work_item:")[1] ?? "").trim();
  raw.source_work_item_slugs = childWi ? [childWi] : [];
  if (!primary) return null;
  const childThread = childTargetId.match(/^thread:([^/]+)/)?.[1] ?? "";
  return childThread
    ? `thread:${childThread}/work_item:${primary}`
    : `unthreaded/work_item:${primary}`;
}

/** Cascade-close (2026-07-23): the children still to close — the full list
 * minus those already closed by THIS form instance (a retry after a partial
 * failure must never re-POST a child that succeeded). Pure → unit-testable. */
export function pendingCascadeChildren(
  children: Array<{ target_id: string; label: string }> | undefined,
  alreadyClosed: ReadonlySet<string>,
): Array<{ target_id: string; label: string }> {
  return (children ?? []).filter((c) => !alreadyClosed.has(c.target_id));
}

/** Cascade-close: the fixed marker stamped on each child's action_close so the
 * Closed Archive shows WHY it closed ("Closed with Item 3.26"). The form's
 * optional reason field rides the ITEM close only. Pure → unit-testable. */
export function cascadeChildReason(itemDisplayNumber?: string | null): string {
  return itemDisplayNumber
    ? `Closed with Item ${itemDisplayNumber}`
    : "Closed with parent Item";
}

/** W3 CATEGORY-CASCADE-CLOSE: the marker stamped on each Item's work_item_close
 * when it closes as part of a Category cascade, so the Closed Archive shows WHY
 * ("Closed with Category 3"). The Category's own thread_close carries the form's
 * optional reason; its Items carry this; their Actions carry cascadeChildReason
 * (their parent Item). Pure → unit-testable. */
export function cascadeCategoryChildReason(categoryDisplayNumber?: string | null): string {
  return categoryDisplayNumber
    ? `Closed with Category ${categoryDisplayNumber}`
    : "Closed with parent Category";
}

/** B1 / #73 reverse merge (Action tier): resolve the action_merge_into pseudo.
 * The recipient is on a CHILD Action (childTargetId, a full action_item target
 * id) and picked an existing primary Action (raw.merge_primary_action — also a
 * full action_item target id, so the primary's Item is known for the POST
 * target). Mutates `raw` into the action_merge wire payload whose SOLE source
 * is the child's FULL target id (the picker field dropped) and returns the POST
 * target id = the picked primary. Passing the child's full id (not a bare
 * ai_slug) lets the existing submit-path cross-Item block re-parent the child
 * into the primary's Item before the merge, exactly as a forward action_merge
 * of a cross-Item source would. Returns null if no primary was picked (the
 * <select> is required, so the empty case is already blocked client-side; the
 * caller then keeps the child target as a no-op fallback). Pure → unit-testable. */
export function resolveActionMergeInto(
  raw: Record<string, unknown>,
  childTargetId: string,
): string | null {
  const primary = String(raw.merge_primary_action ?? "").trim();
  delete raw.merge_primary_action;
  // Source = the child's FULL target id (thread/work_item/action_item) so the
  // submit-path cross-Item re-parent sees the child's origin Item; the merge's
  // own ai_slug coercion (reshape) then narrows it. A child that already shares
  // the primary's Item simply skips the re-parent (wi === targetWi).
  raw.source_action_item_slugs = childTargetId ? [childTargetId] : [];
  if (!primary) return null;
  return primary;
}

/** Round-4 #2: decompose the action_edit_details pseudo-form into one wire
 * event per CHANGED field, judged against the Action's current values
 * (spec.current — the same source the form prefilled from):
 *  - reason / impact / off_hold_condition: submit only a NON-EMPTY changed
 *    value (clearing them isn't a wire operation — required metadata on
 *    Critical/Delay/On-Hold; "leave as-is to keep" is the documented contract);
 *  - due: "" with a prior value = an explicit CLEAR (action_set_due null,
 *    the existing wire semantics); a new date submits when changed.
 * Untouched prefilled fields produce NO event (no no-op corrections spamming
 * corrections.yaml). Pure → unit-testable. */
/**
 * N2/#117 — one-click "Accept proposed answer". Submits the recipient's
 * acceptance of the answer-proposer's suggestion by REUSING the existing
 * `action_open_question_resolve` wire kind (qid + answer) — no new kind, no
 * Worker change. Posts + dispatches `feedback-submitted` exactly like the tested
 * form submit, so the chip / undo / optimistic flow is identical. Direct-payload
 * shim (the plan's sanctioned path); throws on POST failure for the caller to
 * surface + re-enable its button.
 */
export async function acceptProposedAnswer(
  runId: string, actionTargetId: string, qid: string, answer: string,
): Promise<string> {
  const payload = { qid, answer };
  const res: { event_id: string } = inFixtureMode()
    ? { event_id: crypto.randomUUID() }
    : await postJson<{ event_id: string }>(
        `/feedback/${encodeURIComponent(runId)}`,
        {
          kind: "action_open_question_resolve",
          target: { type: "action_item", id: actionTargetId },
          payload,
        },
      );
  document.dispatchEvent(new CustomEvent("feedback-submitted", {
    detail: {
      event_id: res.event_id, kind: "action_open_question_resolve",
      payload, submitted_at: new Date().toISOString(),
      targetId: actionTargetId, targetType: "action_item",
    },
  }));
  // A1 (Ray 2026-09-13): unlike the general form path (which fires this when the
  // kind is in OPTIMISTIC_STATE_KINDS), the one-click Accept posts directly — so
  // dispatch feedback-state-changed here too. The applier writes the answer onto
  // the payload and renderViewer re-renders it, so the accepted answer is now
  // payload-backed and survives the next optimistic action's rebuild (previously
  // it was a DOM-only echo the caller applied, which got wiped).
  document.dispatchEvent(new CustomEvent("feedback-state-changed", {
    detail: {
      kind: "action_open_question_resolve",
      targetId: actionTargetId, payload,
    },
  }));
  return res.event_id;
}

// P4d-2 — one-click CONFIRM of the association-proposer's ghost `?` suggestion:
// submits the DEPLOYED P3 `action_set_associations` with the suggested tags (the
// Action was unplaced, so set == the suggestion). Mirrors acceptProposedAnswer.
export async function confirmProposedAssociations(
  runId: string, actionTargetId: string, associations: string[],
): Promise<string> {
  const payload = { association_tags: associations };
  const res: { event_id: string } = inFixtureMode()
    ? { event_id: crypto.randomUUID() }
    : await postJson<{ event_id: string }>(
        `/feedback/${encodeURIComponent(runId)}`,
        {
          kind: "action_set_associations",
          target: { type: "action_item", id: actionTargetId },
          payload,
        },
      );
  document.dispatchEvent(new CustomEvent("feedback-submitted", {
    detail: {
      event_id: res.event_id, kind: "action_set_associations",
      payload, submitted_at: new Date().toISOString(),
      targetId: actionTargetId, targetType: "action_item",
    },
  }));
  return res.event_id;
}

// S3 accept-assist (2026-10-29) — one-click ACCEPT of the agent's resolved merge
// suggestion on an unfiled row: folds the orphan line into the suggested existing
// Item via the DEPLOYED merge_into_existing (target work_item). Mirrors
// acceptProposedAnswer: POST IMMEDIATELY + dispatch feedback-submitted AND
// feedback-state-changed. merge_into_existing is in OPTIMISTIC_STATE_KINDS, so
// feedback-state-changed drives the DURABLE queue-removal (removeNeedsInputEntry) —
// the row leaves and STAYS gone across re-renders (#510 nit-1); the immediate POST
// means closing the tab doesn't silently drop it (#510 nit-2). The caller offers an
// Undo toast that RETRACTS the event (retractFeedbackEvent) within the window.
// `targetId` is the wire-contract `unfiled:<id>` (unfiledKey) — NOT the raw id
// (#510 nit-3) — matching the manual flow + the live events; `entry.id` (raw) is the
// payload entry_id. No new wire kind, no Worker. Throws on POST failure.
export async function acceptMergeSuggestion(
  runId: string,
  targetId: string,
  entry: { id: string; source_ref: string; text: string; targetWorkItemSlug: string },
): Promise<string> {
  const payload = {
    target_tier: "work_item",
    target_work_item_slug: entry.targetWorkItemSlug,
    text: entry.text,
    source_ref: entry.source_ref,
    entry_id: entry.id,
  };
  const res: { event_id: string } = inFixtureMode()
    ? { event_id: crypto.randomUUID() }
    : await postJson<{ event_id: string }>(
        `/feedback/${encodeURIComponent(runId)}`,
        { kind: "merge_into_existing", target: { type: "unfiled", id: targetId }, payload },
      );
  const detail = {
    event_id: res.event_id, kind: "merge_into_existing",
    payload, submitted_at: new Date().toISOString(),
    targetId, targetType: "unfiled",
  };
  document.dispatchEvent(new CustomEvent("feedback-submitted", { detail }));
  document.dispatchEvent(new CustomEvent("feedback-state-changed", {
    detail: { kind: "merge_into_existing", targetId, payload },
  }));
  return res.event_id;
}

// REPORT-TAG-ONE-CLICK (2026-10-18) — one-click PSU toggle on an Item. PSU is
// item-keyed (B, 2026-09-16 — `wi.kil_marked`), set via the DEPLOYED
// item_kil_mark / item_kil_unmark (target work_item, empty payload). Mirrors
// acceptProposedAnswer: direct POST + feedback-submitted. The PSU badge echo rides
// `kil-mark-changed` → applyKilEventToPayload, NOT feedback-state-changed:
// applyFeedbackEventToPayload has NO item_kil branch (R8, Ray 2026-09-22 — "says I
// updated it, PSU tag still showing"), despite the kind being in OPTIMISTIC_STATE_KINDS.
// This mirrors the item_report_inclusion cascade's PSU dispatch (forms.ts ~6582).
// No new wire kind, no Worker. Throws on POST failure for the caller to surface.
export async function toggleItemPsu(
  runId: string, itemTargetId: string, currentlyOn: boolean,
): Promise<string> {
  const kind: FeedbackKind = currentlyOn ? "item_kil_unmark" : "item_kil_mark";
  const res: { event_id: string } = inFixtureMode()
    ? { event_id: crypto.randomUUID() }
    : await postJson<{ event_id: string }>(
        `/feedback/${encodeURIComponent(runId)}`,
        { kind, target: { type: "work_item", id: itemTargetId }, payload: {} },
      );
  document.dispatchEvent(new CustomEvent("feedback-submitted", {
    detail: {
      event_id: res.event_id, kind, payload: {},
      submitted_at: new Date().toISOString(),
      targetId: itemTargetId, targetType: "work_item",
    },
  }));
  // The item-header PSU badge flips ONLY via applyKilEventToPayload (reached by
  // kil-mark-changed) — feedback-state-changed has no item_kil branch (R8).
  document.dispatchEvent(new CustomEvent("kil-mark-changed", {
    detail: { kind, targetId: itemTargetId },
  }));
  return res.event_id;
}

// REPORT-TAG-ONE-CLICK (2026-10-18) — one-click Key Issue Log (558) toggle on an
// Action. 558 is action-keyed (`report_tags ∋ 558`). REPLACE-WHOLE
// action_set_report_tags preserving the Action's OTHER tags (mirrors
// collectReportInclusionEdits). action_set_report_tags ∈ OPTIMISTIC_STATE_KINDS →
// the applier writes ai.report_tags onto the payload (payload-backed echo).
export async function toggleActionKil558(
  runId: string, actionTargetId: string, currentTags: string[],
): Promise<string> {
  const on = currentTags.includes("558");
  const payload = { report_tags: withReport558(currentTags, !on) };
  const kind: FeedbackKind = "action_set_report_tags";
  const res: { event_id: string } = inFixtureMode()
    ? { event_id: crypto.randomUUID() }
    : await postJson<{ event_id: string }>(
        `/feedback/${encodeURIComponent(runId)}`,
        { kind, target: { type: "action_item", id: actionTargetId }, payload },
      );
  document.dispatchEvent(new CustomEvent("feedback-submitted", {
    detail: {
      event_id: res.event_id, kind, payload,
      submitted_at: new Date().toISOString(),
      targetId: actionTargetId, targetType: "action_item",
    },
  }));
  document.dispatchEvent(new CustomEvent("feedback-state-changed", {
    detail: { kind, targetId: actionTargetId, payload },
  }));
  return res.event_id;
}

// P4d-2 — DISMISS one inferred association: submits the NEW WIRE KIND
// `action_dismiss_association_candidate`, recording a per-row suppression so the
// proposer never re-suggests that (row, association). One event per slug.
export async function dismissProposedAssociation(
  runId: string, actionTargetId: string, associationSlug: string,
): Promise<string> {
  const payload = { association_slug: associationSlug };
  const res: { event_id: string } = inFixtureMode()
    ? { event_id: crypto.randomUUID() }
    : await postJson<{ event_id: string }>(
        `/feedback/${encodeURIComponent(runId)}`,
        {
          kind: "action_dismiss_association_candidate",
          target: { type: "action_item", id: actionTargetId },
          payload,
        },
      );
  document.dispatchEvent(new CustomEvent("feedback-submitted", {
    detail: {
      event_id: res.event_id, kind: "action_dismiss_association_candidate",
      payload, submitted_at: new Date().toISOString(),
      targetId: actionTargetId, targetType: "action_item",
    },
  }));
  return res.event_id;
}

export function collectActionDetailEdits(
  raw: Record<string, unknown>,
  current: Record<string, string | null | undefined> | undefined,
): Array<{ kind: FeedbackKind; payload: Record<string, unknown> }> {
  const edits: Array<{ kind: FeedbackKind; payload: Record<string, unknown> }> = [];
  const cur = (k: string): string => {
    const v = current?.[k];
    return typeof v === "string" ? v.trim() : "";
  };
  const TEXT_FIELDS: Array<[string, FeedbackKind]> = [
    ["reason", "action_set_reason"],
    ["impact", "action_set_impact"],
    ["off_hold_condition", "action_set_off_hold_condition"],
  ];
  for (const [field, kind] of TEXT_FIELDS) {
    if (!(field in raw)) continue;            // field not on this priority's form
    const v = typeof raw[field] === "string" ? (raw[field] as string).trim() : "";
    if (v && v !== cur(field)) edits.push({ kind, payload: { [field]: v } });
  }
  if ("due" in raw) {
    const v = typeof raw.due === "string" ? (raw.due as string).trim() : "";
    const c = cur("due");
    if (v !== c && (v || c)) {
      edits.push({ kind: "action_set_due", payload: { due: v || null } });
    }
  }
  return edits;
}

/** REPORT-TAG-ONE-CLICK (2026-10-18, ultra nit): THE one replace-whole rule for an
 * Action's Key Issue Log (558) membership — drop 558, re-add it iff `want`, preserving
 * every other tag in order. Shared by the one-click KIL pill (toggleActionKil558) and
 * the action_report_inclusion form (collectReportInclusionEdits) so they can't drift.
 * Pure → unit-testable. */
export function withReport558(curTags: string[], want: boolean): string[] {
  const next = curTags.filter((t) => t !== "558");
  if (want) next.push("558");
  return next;
}

/** Slice 2 (2026-09-09) → B (client 2026-09-16, PSU→item-only): decompose the
 * `action_report_inclusion` pseudo-form. B drops PSU from the ACTION tier — the
 * PSU is now item-keyed (`item_report_inclusion`), so the Action control governs
 * ONLY the Key Issue Log (558):
 *  - Key Issue Log (report_tags ∋ 558): REPLACE-WHOLE action_set_report_tags with
 *    558 added/removed, PRESERVING any other tags the Action already carried.
 * Unchanged → NO event (no no-op corrections). Pure → unit-testable. */
export function collectReportInclusionEdits(
  raw: Record<string, unknown>,
  current: Record<string, string | null | undefined> | undefined,
): Array<{ kind: FeedbackKind; payload: Record<string, unknown> }> {
  const rawSel = raw.report_inclusion;
  const selected = new Set(
    (Array.isArray(rawSel) ? rawSel : rawSel != null ? [rawSel] : [])
      .map((v) => String(v)).filter(Boolean),
  );
  const curTagStr = current?.report_tags;
  const curTags = typeof curTagStr === "string" && curTagStr
    ? curTagStr.split(",").map((s) => s.trim()).filter(Boolean)
    : [];
  const wasKil = curTags.includes("558");
  const nowKil = selected.has("558");
  const edits: Array<{ kind: FeedbackKind; payload: Record<string, unknown> }> = [];
  if (nowKil !== wasKil) {
    edits.push({
      kind: "action_set_report_tags",
      payload: { report_tags: withReport558(curTags, nowKil) },
    });
  }
  return edits;
}

/** Slice 2 (2026-09-09) → B (client 2026-09-16, PSU→item-only): decompose the
 * `item_report_inclusion` pseudo-form. The Item pencil carries TWO checkboxes with
 * DIFFERENT scope (D4 — the label carries the meaning):
 *  - "Key issue log — all N actions" (558): a CASCADE — every open Action not already
 *    matching gets one `action_set_report_tags` (targetType `action_item`), 558
 *    added/removed, preserving the Action's other tags.
 *  - "Project status update" (PSU): the ITEM's OWN membership — a SINGLE
 *    `item_kil_mark`/`item_kil_unmark` on the ITEM (targetType `work_item`) when the
 *    selection differs from the Item's current mark. NOT a cascade (B: PSU is
 *    item-keyed; the Action tier no longer carries PSU).
 * Each event carries its own `targetType` + `target_id` (the two scopes hit
 * different targets). No change in a report → no event. Pure → unit-testable. */
export function collectItemReportCascade(
  selected: Set<string>,
  actions: ReadonlyArray<{ target_id: string; kil_marked: boolean; report_tags: string[] }>,
  item: { target_id: string; kil_marked: boolean },
): Array<{ targetType: "work_item" | "action_item"; target_id: string; kind: FeedbackKind; payload: Record<string, unknown> }> {
  const want558 = selected.has("558");
  const wantPsu = selected.has("psu");
  const out: Array<{ targetType: "work_item" | "action_item"; target_id: string; kind: FeedbackKind; payload: Record<string, unknown> }> = [];
  // Key Issue Log (558) — per-Action cascade.
  // R5 (Ray 2026-09-22): only run the cascade when the recipient actually
  // CHANGED the 558 box. Its pre-check is "checked iff EVERY open Action is
  // tagged" (forms.ts item_report_inclusion), so a PARTIALLY tagged Item
  // renders it UNCHECKED — indistinguishable from "none tagged". Without this
  // gate a PSU-only submit (558 left alone on a partial Item) reads
  // want558=false and STRIPS 558 from the recipient's tagged Actions — his "it
  // reset the report tagging each time", data-loss on 11 live Items. The box's
  // rendered initial state IS `initial558` (all open Actions tagged); matches
  // the PSU tier below, which already gates on wantPsu !== item.kil_marked.
  const initial558 = actions.length > 0
    && actions.every((a) => (a.report_tags ?? []).includes("558"));
  if (want558 !== initial558) {
    for (const a of actions) {
      const has558 = (a.report_tags ?? []).includes("558");
      if (want558 !== has558) {
        out.push({
          targetType: "action_item", target_id: a.target_id,
          kind: "action_set_report_tags",
          payload: { report_tags: withReport558(a.report_tags ?? [], want558) },
        });
      }
    }
  }
  // Project Status Update — the ITEM's own mark (item-keyed, B).
  if (wantPsu !== item.kil_marked) {
    out.push({
      targetType: "work_item", target_id: item.target_id,
      kind: wantPsu ? "item_kil_mark" : "item_kil_unmark", payload: {},
    });
  }
  return out;
}

/** E-5 (Ray 2026-09-16): whether a conditional form field is REQUIRED for a given
 * driver value. `requiredWhen` (when present) governs — required iff the current
 * driver value is in the list (an empty list = shown but never required) — and
 * OVERRIDES the static `required`. Without `requiredWhen`, falls back to `required`.
 * The visibility gate (`visibleWhen`) is applied separately by the caller (apply());
 * this is only the required-vs-optional decision so it can be unit-tested in the
 * node test env, where the DOM apply() closure isn't reachable. */
export function fieldRequiredForValue(
  field: { required?: boolean; requiredWhen?: readonly string[] },
  driverValue: string,
): boolean {
  return field.requiredWhen
    ? field.requiredWhen.includes(driverValue)
    : !!field.required;
}

/** A2 (Ray 2026-09-15): decompose the item pencil's "Set associations" into a
 * CASCADE of per-open-Action `action_set_associations` events — the Item-tier
 * "set the association for all this Item's Actions" affordance. `desired` is the
 * complete REPLACE-WHOLE set chosen at the Item tier; each open Action whose
 * current set differs gets one event assigning `desired` verbatim (an Action
 * already at the desired set produces none — no churn). Setting at the Item tier
 * therefore propagates to every open Action, and the Item chip (a pure rollup,
 * itemAssociationUnion) reflects it. Per-action refinement afterwards is via the
 * Action's own ✎; re-running this "set all" OVERWRITES those refinements (the
 * form carries a note saying so — Ray 2026-09-15 accepted it as a knowing
 * affordance). Pure → unit-testable. */
export function collectItemAssociationCascade(
  desired: string[],
  actions: ReadonlyArray<{ target_id: string; association_tags: string[] }>,
): Array<{ target_id: string; kind: FeedbackKind; payload: Record<string, unknown> }> {
  const want = [...new Set(desired)].sort();
  const wantKey = want.join(" ");
  const out: Array<{ target_id: string; kind: FeedbackKind; payload: Record<string, unknown> }> = [];
  for (const a of actions) {
    const have = [...new Set(a.association_tags ?? [])].sort();
    if (have.join(" ") === wantKey) continue; // already at the desired set
    out.push({
      target_id: a.target_id, kind: "action_set_associations",
      payload: { association_tags: want },
    });
  }
  return out;
}

/** E-5 (Ray 2026-09-16 meeting): decompose the item pencil's "Set priority" into a
 * CASCADE of per-open-Action `action_priority_change` events — the Item-tier "set
 * the priority for all this Item's Actions" affordance (dropdown-only; no reason/
 * impact/off-hold fields at the Item tier). `newPriority` is the single priority
 * chosen at the Item tier; each open Action whose current priority differs gets one
 * event carrying `{new_priority}` (an Action already at that priority produces none
 * — no churn). Setting On Hold at the Item tier therefore holds every open Action,
 * which satisfies Ray's "can't hold an Item while some actions aren't" by
 * construction. Unlike the association cascade the value is a scalar, so no set-join
 * compare is needed. Pure → unit-testable. */
export function collectItemPriorityCascade(
  newPriority: string,
  actions: ReadonlyArray<{ target_id: string; priority_status: string }>,
): Array<{ target_id: string; kind: FeedbackKind; payload: Record<string, unknown> }> {
  const out: Array<{ target_id: string; kind: FeedbackKind; payload: Record<string, unknown> }> = [];
  for (const a of actions) {
    // `|| "Active"` (not `??`) so a blank/missing status is treated as the
    // Active default — matches the DB default (priority_status NOT NULL DEFAULT
    // 'Active') and avoids a spurious Active→Active event on malformed data.
    if ((a.priority_status || "Active") === newPriority) continue; // already there
    out.push({
      target_id: a.target_id, kind: "action_priority_change",
      payload: { new_priority: newPriority },
    });
  }
  return out;
}

/** E-7a (Ray 2026-09-16): decompose a "move sub-category to another Category"
 * into an ORDERED sequence of EXISTING wire kinds (no new wire kind, no Worker
 * redeploy). Mirrors apply-order semantics — the destructive delete is LAST and
 * only ungroups, so a partial absorb is benign/recoverable:
 *   1. subcategory_add(target thread, same slug + name) — idempotent re-issue.
 *   2. per member Item: item_move_to_category(→target) THEN
 *      item_move_to_subcategory(→same slug). The move nulls the group pointer +
 *      moves the thread; the regroup re-sets it, now SAME-thread valid (the
 *      stamp's orphan sweep only clears a cross-thread pointer). The regroup's
 *      target_id uses the TARGET thread prefix — after the move the Item sits
 *      there, so both the optimistic applier and the ordered absorb find it.
 *   3. subcategory_delete(source thread, slug) — LAST; removes the source row +
 *      ungroups any straggler (never deletes Items).
 * Each event carries its own `targetType` (thread / work_item / subcategory) so
 * the sequential submit loop can POST the right target. The caller preserves
 * ORDER (sequential await) — subcategory_delete must not precede the moves.
 * Pure → unit-testable. */
export function collectSubcategoryMove(
  sourceThreadSlug: string,
  subcategorySlug: string,
  subcategoryName: string,
  toThreadSlug: string,
  members: ReadonlyArray<{ work_item_slug: string; former_display_number?: string }>,
): Array<{ target_id: string; targetType: TargetType; kind: FeedbackKind; payload: Record<string, unknown> }> {
  const out: Array<{ target_id: string; targetType: TargetType; kind: FeedbackKind; payload: Record<string, unknown> }> = [];
  // 1. recreate the group under the target Category (same slug + name).
  out.push({
    target_id: threadKey(toThreadSlug), targetType: "thread",
    kind: "subcategory_add",
    payload: { subcategory_slug: subcategorySlug, name: subcategoryName },
  });
  // 2. per member: move to the target Category, then re-group under the slug.
  for (const m of members) {
    out.push({
      target_id: workItemKey(sourceThreadSlug, m.work_item_slug),
      targetType: "work_item", kind: "item_move_to_category",
      payload: m.former_display_number
        ? { to_thread_slug: toThreadSlug, former_display_number: m.former_display_number }
        : { to_thread_slug: toThreadSlug },
    });
    out.push({
      target_id: workItemKey(toThreadSlug, m.work_item_slug),
      targetType: "work_item", kind: "item_move_to_subcategory",
      payload: { to_subcategory_slug: subcategorySlug },
    });
  }
  // 3. drop the now-empty source group (destructive step LAST; ungroup-only).
  out.push({
    target_id: subcategoryKey(sourceThreadSlug, subcategorySlug),
    targetType: "subcategory", kind: "subcategory_delete", payload: {},
  });
  return out;
}

/** #60 PR4-v2: resolve the merge_into_existing pickers' friendly labels back to
 * stable slugs (mirror of resolvePickedTargetSlugs, against the OPEN+CLOSED
 * merge option sources). The action tier resolves its single pick into BOTH
 * slugs the agent's probe needs. Unresolved free text is left for reshape's
 * ensureSlug() fallback. Mutates `raw` in place; pure → unit-testable. */
export function resolveMergeTargetPicks(
  raw: Record<string, unknown>,
  mergeThreadOptions: ThreadOption[] = [],
  mergeItemOptions: ItemOption[] = [],
  mergeActionOptions: ClosedActionOption[] = [],
): void {
  const t = raw.target_thread_slug;
  if (typeof t === "string") {
    const bySlug = new Set(mergeThreadOptions.map((o) => o.slug));
    if (!bySlug.has(t)) {
      const byKey = new Map<string, string>();
      for (const o of mergeThreadOptions) {
        byKey.set(threadOptionLabel(o), o.slug);
        if (!byKey.has(o.name)) byKey.set(o.name, o.slug);
      }
      if (byKey.has(t)) raw.target_thread_slug = byKey.get(t);
    }
  }
  const i = raw.target_work_item_slug;
  if (typeof i === "string") {
    const bySlug = new Set(mergeItemOptions.map((o) => o.slug));
    if (!bySlug.has(i)) {
      const byLabel = new Map(
        mergeItemOptions.map((o) => [itemOptionLabel(o), o.slug]),
      );
      if (byLabel.has(i)) raw.target_work_item_slug = byLabel.get(i);
    }
  }
  const a = raw.target_action_pick;
  if (typeof a === "string" && a.trim()) {
    const byLabel = new Map(
      mergeActionOptions.map((o) => [o.label, o]),
    );
    const hit = byLabel.get(a);
    if (hit) {
      raw.target_work_item_slug = hit.work_item_slug;
      raw.target_action_item_slug = hit.action_item_slug;
    } else {
      // Free-typed action text: best-effort slug; the parent Item is
      // unknown, so the absorber dead-letters with suggestions.
      raw.target_action_item_slug = ensureSlug(a);
    }
  }
}

export type FeedbackKind =
  | "thread_approve"
  | "thread_confirm"
  | "thread_promote"
  | "thread_reject"
  | "thread_rename"
  | "thread_merge"
  | "thread_retag"
  | "thread_close"
  | "thread_reopen"
  | "work_item_close"
  | "work_item_reopen"
  | "responsible_party_assign"
  | "review_clarification"
  | "date_correction"
  | "not_relevant"
  | "missing_item"
  | "comment"
  | "flag_next_week"
  // v4 (Task #9) — Category → Item → Action correction kinds.
  // Each binds against an agent-side Pydantic literal in
  // agent/corrections.py (Task #8) and a worker schema literal in
  // worker/src/schemas.ts. Strict ordering: agent shipped first.
  | "action_priority_change"
  | "action_set_reason"
  | "action_set_impact"
  | "action_set_off_hold_condition"
  | "action_set_due"
  | "action_open_question_add"
  | "action_open_question_resolve"
  // Client 2026-08-12: dispose of an open question without answering it.
  | "action_open_question_dismiss"
  | "action_kil_mark"
  | "action_kil_unmark"
  // Decision 12.8 (2026-05-28): Action lifecycle + closure-candidate.
  | "action_close"
  | "action_reopen"
  | "action_dismiss_closure_candidate"
  // Option Y (2026-06-03): dismiss an Item-level closure proposal.
  | "work_item_dismiss_closure_candidate"
  // B2 (2026-06-26): acknowledge a PLACED Item/Action ("seen, in-project, NOT
  // tracked") → status='acknowledged' (3rd terminal state). Distinct from close
  // (work done) + the Needs-Input informational_acknowledge. Mirror of
  // agent/portal.py FeedbackKind + worker/src/schemas.ts.
  | "work_item_acknowledge"
  | "action_acknowledge"
  // B2 Track-this: reverse an acknowledge (acknowledged -> open). Mirror of
  // agent/portal.py FeedbackKind + worker/src/schemas.ts.
  | "work_item_track"
  | "action_track"
  // B2 PR-1: reject a PLACED Item/Action as "not relevant" (not this project)
  // → status='not_relevant' (4th terminal state). The placed analogue of the
  // bucket `not_relevant`. Mirror of agent/portal.py + worker/src/schemas.ts.
  | "work_item_not_relevant"
  | "action_not_relevant"
  // R2-3: a recipient DIRECTLY edits an Item/Action narrative (verbatim).
  // Mirror of agent/portal.py + worker/src/schemas.ts.
  | "work_item_set_narrative"
  | "action_set_narrative"
  // v6.1 (R): set the report-inclusion tag-set ({558,summary}) on a placed
  // Item/Action. Replace-whole; payload carries `report_tags`. The editing
  // affordance (badge + bulk toggle) lands in P1r. Mirror of agent/portal.py +
  // worker/src/schemas.ts.
  | "item_set_report_tags"
  | "action_set_report_tags"
  // v6.1 (R P3): set the association tag-set on a placed Item/Action. Valid
  // members are the per-project config.associations[].slug. Replace-whole;
  // payload carries `association_tags`. The editing affordance (placement UX)
  // lands in P4. Mirror of agent/portal.py + worker/src/schemas.ts.
  | "item_set_associations"
  | "action_set_associations"
  // v6.1 (R P4d-2): dismiss an inferred association candidate on an Action.
  | "action_dismiss_association_candidate"
  // v6.1 (R 558): set the 558 "Point" (responsible firm/s) on an Item — a
  // per-issue editorial string. The editing affordance lands with the 558 UX.
  // Mirror of agent/portal.py + worker/src/schemas.ts.
  | "item_set_point"
  // R2-3 fast-follow: a recipient directly edits the executive summary (a
  // project-level singleton; targets `project:<slug>`). Mirror of
  // agent/portal.py + worker/src/schemas.ts.
  | "set_executive_summary"
  | "item_kil_mark"
  | "item_kil_unmark"
  | "item_add"
  | "category_add"
  | "action_add"
  | "place_from_unfiled"
  // A³/A² (2026-06-03): tier-explicit Place on Needs-Your-Input entries +
  // the informational "Dismiss (acknowledged)" disposition.
  | "place_as_category"
  | "place_as_action"
  | "informational_acknowledge"
  // #60 PR4-v2 (2026-06-11 review): the row's placement surface is TWO verbs.
  // `place_new` is an SPA-only PSEUDO-kind — its tier select resolves to the
  // real wire kind (place_as_category | place_from_unfiled | place_as_action)
  // at submit, with a `complete` flag for born-closed. `merge_into_existing`
  // (renamed from file_under_closed) folds a row into an existing entity.
  | "place_new"
  | "merge_into_existing"
  | "item_move_to_category"
  | "invoice_add"
  | "invoice_status_change"
  | "invoice_blocking_note_set"
  // Task 3: work-item structural affordances. Bind to agent-side
  // Pydantic literals (corrections.py) + worker schema (schemas.ts).
  | "candidate_to_item"
  | "work_item_merge"
  | "work_item_rename"
  // Open improvement #3: action-level merge.
  | "action_merge"
  // B6: action-level rename.
  | "action_rename"
  // B8: move an Action to a different Item.
  | "action_move_to_item"
  // #76: promote an Action to a new Item (+ relocate to a chosen Category).
  // Targets the action_item; the absorber creates a work_item + closes the
  // source. Mirror of agent/portal.py + worker/src/schemas.ts.
  | "action_to_item"
  // #38: sub-category grouping inside a Category (overlay tier). Mirror of
  // agent/portal.py FeedbackKind + worker/src/schemas.ts. add targets the
  // parent Category (thread); rename/close/reopen target the subcategory;
  // item_move_to_subcategory targets the work_item.
  | "subcategory_add"
  | "subcategory_rename"
  | "subcategory_close"
  | "subcategory_reopen"
  // #74b: delete (evaporate) a sub-category — ungroup members + drop the row
  // (no tombstone, no close-gate; distinct from subcategory_close).
  | "subcategory_delete"
  | "item_move_to_subcategory"
  // E-7a (Ray 2026-09-16): relocate a whole sub-category to a different Category.
  // SPA-only PSEUDO-kind (never reaches the wire — absent from portal.py /
  // schemas.ts; allow-listed in FORMS_PSEUDO_KINDS). At submit it decomposes into
  // existing wire kinds (collectSubcategoryMove): subcategory_add(target) +
  // per-member [item_move_to_category → item_move_to_subcategory] +
  // subcategory_delete(source). No new wire kind, no Worker redeploy.
  | "subcategory_move"
  // #37: re-band an open Category into one of the 4 fixed proposal phases.
  // Targets the parent thread (Category); payload {to_phase}. Pure attribute
  // move — no relocation, latest-wins on replay. Mirror of agent/portal.py +
  // agent/corrections.py CategoryMoveToPhase + worker/src/schemas.ts.
  | "category_move_to_phase"
  // P4d-3: migrate a Category to a different sub-project. Targets the parent
  // thread; payload {to_sub_project_slug}. Unlike category_move_to_phase,
  // sub_project is the numbering root (P4b), so the agent re-roots the thread
  // <letter> number + subtree on apply. Mirror of agent/portal.py +
  // agent/corrections.py CategoryMoveToSubproject + worker/src/schemas.ts.
  | "category_move_to_subproject"
  // #38 round-1 review (2026-06-12, feedback 2.1): `item_move` is an SPA-only
  // PSEUDO-kind (the place_new pattern) — ONE "Move to…" button whose tier
  // select resolves to item_move_to_category | item_move_to_subcategory at
  // submit via resolveItemMoveKind. Never reaches the wire; offered only when
  // BOTH destinations exist (open Item + ≥1 open group in the parent).
  | "item_move"
  // #73 (2026-06-16 call): `item_merge_into` is an SPA-only PSEUDO-kind on a
  // CHILD Item's menu — the reverse of work_item_merge (which folds others INTO
  // the open item). The recipient picks an existing primary to fold THIS Item
  // up into, without backtracking to the primary first; at submit it resolves
  // to a standard work_item_merge whose TARGET is the picked primary and whose
  // single source is this child. No new wire kind (feedback-spa-pseudo-kind-
  // pattern). Offered only when ≥1 other open Item exists (a valid primary).
  | "item_merge_into"
  // B1 / #73 (2026-06-23 Ray, his #1 merge pain): `action_merge_into` is the
  // ACTION-tier twin of item_merge_into — an SPA-only PSEUDO-kind on a CHILD
  // Action's menu, the reverse of action_merge (which folds others INTO the
  // open Action). The recipient picks an existing primary Action to fold THIS
  // Action up into, without backtracking to the primary and hunting the child
  // in a list. At submit it resolves to a standard action_merge whose TARGET is
  // the picked primary and whose single source is this child; a cross-Item pick
  // re-parents this child into the primary's Item first (the existing action_
  // merge cross-Item path). No new wire kind — inherits action_merge's absorb
  // (alias capture = the #66 ledger signal) + optimistic gray-out. Offered only
  // when ≥1 other open Action exists project-wide (a valid primary).
  | "action_merge_into"
  // Round-4 review (2026-06-12 #2): `action_edit_details` is an SPA-only
  // PSEUDO-kind flattening the per-field metadata edit buttons (reason /
  // impact / due / off-hold) into ONE form. Unlike the tier-select pseudos it
  // decomposes at submit into one EXISTING wire event per CHANGED field
  // (action_set_reason / _impact / _off_hold_condition / _due) via
  // collectActionDetailEdits — never reaches the wire itself. Offered only
  // when the priority surfaces ≥2 edit fields (Active keeps its lone
  // "Edit due date" button — the no-one-option-nesting rule).
  | "action_edit_details"
  // Cascade-close (Ray 2026-07-21, built 2026-07-23): `work_item_close_cascade`
  // is an SPA-only PSEUDO-kind replacing the greyed dead-end "Close Item" when
  // the Item still has OPEN child Actions — Ray's biggest confusion cluster
  // (the B9 gate greyed close and nothing cascaded). The form lists the open
  // children for confirmation; at submit it sequentially POSTs one EXISTING
  // `action_close` per child (each stamped "Closed with Item <N>") and then
  // rides the generic path as a plain `work_item_close` whose #74a
  // client_events carry the just-POSTed child closes past the Worker's
  // open-children gate (the proven B10 contingent-close pattern). No new wire
  // kind, no Worker change. Offered only when EVERY open child has an ai_slug
  // (else the old greyed close + notice remain — an untargetable child would
  // strand the composite).
  | "work_item_close_cascade"
  // W3 CATEGORY-CASCADE-CLOSE (2026-10-07): `thread_close_cascade` is the
  // Category-tier mirror of work_item_close_cascade — the SPA-only PSEUDO-kind
  // replacing the greyed dead-end "Close Category" when the Category still has
  // OPEN Items (Ray hit this on Quayside "Visioning RFP"). The form lists every
  // Item + Action it will close; at submit it POSTs, per open Item, one EXISTING
  // `action_close` per open Action (stamped "Closed with Item <N>") then one
  // `work_item_close` (stamped "Closed with Category <N>", client_events so the
  // item gate passes), and finally rides the generic path as a plain
  // `thread_close` whose #74a client_events carry the just-POSTed Item closes
  // past the Worker's open-items gate (effectiveOpenItemsUnderThread). No new
  // wire kind, no Worker change. Offered only when EVERY open Item is targetable
  // AND every open Action under them has an ai_slug AND the rendered open-Item
  // set equals the Worker's gated count (no judge-dup the cascade wouldn't close
  // — two-layer close-gate parity); else the greyed close + notice remain.
  | "thread_close_cascade"
  // W6 PM-ITEM-NO-ACTION (2026-10-08): `work_item_mark_completed` — the PM declares
  // an Item's tracked work DONE but keeps the Item OPEN for reporting visibility
  // ("Drop 9 completed"; the "completed" status is the PM's own narrative edit, not
  // an agent badge). SPA-only PSEUDO-kind: like work_item_close_cascade it POSTs one
  // EXISTING `action_close` per open child, but then rides the generic path as a
  // `work_item_dismiss_closure_candidate` (NOT work_item_close) so the Item stays
  // OPEN and the §6c "Agent proposes closing" nag is parked (synth_validator respects
  // closure_candidate_dismissed; the Item is re-injected open by enforce_entity_
  // persistence, verified surviving the cron). No new wire kind, no agent/Worker
  // change. Offered only when EVERY open child has an ai_slug (same gate as the
  // cascade — an untargetable child would strand the composite).
  | "work_item_mark_completed"
  // Slice 2 (2026-09-09, 558 = Key Issue Log): `action_report_inclusion` is an
  // SPA-only PSEUDO-kind consolidating the fragmented report-inclusion affordances
  // into ONE control per Action. Its form carries two checkboxes — Project Status
  // Update (PSU) + Key Issue Log (558) — pre-checked from the Action's current
  // kil_marked + report_tags. At submit it decomposes (collectReportInclusionEdits)
  // into the EXISTING wire kinds — action_kil_mark/unmark for the PSU toggle,
  // action_set_report_tags for the 558 set — never reaching the wire itself. No new
  // wire kind, no Worker change.
  | "action_report_inclusion"
  // Slice 2 (2026-09-09): `item_report_inclusion` is the ITEM-tier twin — one
  // "Report inclusion" control on the Item pencil whose checkboxes ("all actions
  // → Key Issue Log / PSU") CASCADE over the Item's open Actions at submit
  // (collectItemReportCascade → per-Action action_set_report_tags / action_kil_
  // mark/unmark). No item-level mark (respects #58); no new wire kind, no Worker.
  | "item_report_inclusion"
  // E-5 (Ray 2026-09-16 meeting): `item_set_priority` is the ITEM-tier priority
  // twin — one "Set priority" control on the Item pencil (dropdown-only) whose
  // chosen priority CASCADES over the Item's open Actions at submit
  // (collectItemPriorityCascade → per-Action action_priority_change). A pure
  // SPA-only PSEUDO-kind: unlike item_set_associations there is NO item-level
  // priority state (work_items has no priority column), so it never reaches the
  // wire and needs no agent/Worker applier — it decomposes to the existing
  // action_priority_change (already in OPTIMISTIC_STATE_KINDS → free echo).
  // Offered on ALL projects (priority is universal, not axis-gated), open Items
  // only. No new wire kind, no Worker change.
  | "item_set_priority";

export type TargetType =
  | "thread"
  | "item"
  | "section"
  | "extraction"
  | "work_item"
  // #38: sub-category targets — `thread:<t>/subcategory:<sc>`
  // (subcategory_rename / _close / _reopen). add targets the parent thread;
  // item_move_to_subcategory targets the work_item.
  | "subcategory"
  // v4: action-item-scoped events (priority change, KIL mark,
  // question add/resolve, invoice ops on a specific action_item)
  // and unfiled-row events (place_from_unfiled).
  | "action_item"
  | "unfiled"
  // A³/A² (2026-06-03): informational-bucket-row events — `informational:<id>`.
  | "informational"
  // v4 (Decision 4.3): project-shell-scoped events. Currently only
  // used by `category_add` from the "+ Add Category" affordance at
  // the top of the briefing view.
  | "project";

export interface SubmittedEvent {
  event_id: string;
  kind: string;
  payload: Record<string, unknown>;
  submitted_at: string;
  // B7: false once poll-portal has absorbed the event — then Retract is
  // hidden (the inverse correction is the path). Undefined/true → retractable.
  pending?: boolean;
}

export interface FormControlSpec {
  runId: string;
  targetType: TargetType;
  targetId: string;
  kinds: string[];
  submitted?: SubmittedEvent[];
  // Optional advisory shown inside the popover when the user opens it
  // (above the action picker). Used by the thread header to explain
  // "Close N open work items before closing this thread" without
  // polluting the always-on header chrome. Free-form short string.
  notice?: string;
  // Slice 8 Item #5: project participants — used by the
  // responsible_party_assign form to populate a <datalist> for the
  // Owner name field. Sourced from the briefing (see
  // collectParticipantOptions in briefings.ts). When picking a name
  // that has an associated email, the form auto-fills the email field.
  participantOptions?: ParticipantOption[];
  // Contract-drift batch 2 — Finding 5: known threads on this briefing,
  // used to populate a <datalist> for the thread_merge "Merge into" and
  // thread_retag "New thread" forms. The operator picks by visible name;
  // the form submits the slug. Sourced from collectThreadOptions in
  // briefings.ts. Free-text fallback: reshapePayloadForKind slugifies
  // any value that doesn't already look like a slug.
  threadOptions?: ThreadOption[];
  // Decision 7A (close-out 2026-05-25): Items in scope when an Unfiled
  // row's "place under existing Item" path is exercised. Renders as a
  // <datalist> on `place_from_unfiled.target_work_item_slug`.
  itemOptions?: ItemOption[];
  // P4d-1b (R association axis): the project's association axis, as {slug,label}
  // — the option set for the item/action_set_associations multiselect. Sourced
  // from projectAssociationSlugs(payload) in briefings.ts; empty on a flat
  // project (the kind is not offered there). Mirrors report_tags' hardcoded
  // {558,summary}, but per-project so it must be threaded in.
  associationOptions?: Array<{ value: string; label: string }>;
  // KIL-POINT (Ray 2026-10-28): the project's distinct existing 558 "Points"
  // (e.g. "Scale / KAMM"), for the item_set_point <datalist> autocomplete. Sourced
  // from the payload's Items in briefings.ts; empty → no datalist (free text only).
  // Non-restrictive: a Point not in the list still submits (open vocab).
  pointOptions?: string[];
  // P4d-3 (R sub-project tier): the project's sub-projects, as {slug,name} —
  // the option set for the category_move_to_subproject <select>. Sourced from
  // sortedSubProjects(payload.sub_projects) in briefings.ts; empty on a flat
  // project (the kind is not offered there, so the pencil menu stays
  // byte-identical). Threaded in for the same reason as associationOptions.
  subProjectOptions?: Array<{ value: string; label: string }>;
  // B3 (2026-09-15): the moving/placing entity's current sub-project slug — the
  // default selection for the move/place Sub-project pre-filter <select> (so the
  // common case, reorganise within the same sub-project, is a single glance).
  // For an Item move this is the parent Category's sub_project_slug; unset on
  // Needs-Input placement (there is no "current" → defaults to the first
  // offered sub-project). Ignored on a flat project (no pre-filter rendered).
  currentSubProjectSlug?: string | null;
  // #38: the parent Category's sub-categories. Drives the
  // item_move_to_subcategory picker (open groups + "(ungrouped)") and the
  // subcategory_add slug-collision set (open + closed). Fed from the
  // optimistically-mutated payload so session-pending adds are visible
  // (the pending-aware picker mechanism — the cited "#60 pattern" never
  // existed; this is the real one, per the binding amendment).
  subcategoryOptions?: SubcategoryOption[];
  // Open improvement #3: sibling Actions under the parent Item, used by
  // the action_merge multiselect (the source Actions to fold into this
  // one). The target Action is excluded by the caller / form.
  actionOptions?: ActionOption[];
  // Decision 7A: invoices on the parent Item, used for invoice_status_change
  // and invoice_blocking_note_set instead of free-text invoice number.
  invoiceOptions?: InvoiceOption[];
  // Dogfood feedback 3: the Action's UNANSWERED open questions, so the
  // action_open_question_resolve form lets the recipient pick the question by
  // its TEXT (a <select>) instead of typing a cryptic 0-based index. value =
  // the question's index in open_questions_v4 (what the absorber expects).
  openQuestionOptions?: { value: string; label: string }[];
  // Client 2026-08-12 — the per-QUESTION ✎ menu. When set, the controls belong
  // to one specific question rather than to the Action as a whole, so the
  // "Which question" picker is dropped entirely and `qid` is injected at submit
  // (mirroring how `bucketContext` injects `source_ref` / `unfiled_id`).
  //
  // This is what let the Action-level `action_open_question_resolve` picker go.
  // 14 of 45 live Actions carry more than one question (one carries five), so
  // answering meant opening the Action's menu and re-identifying the question in
  // a dropdown — while the question itself was on screen the whole time.
  questionContext?: QuestionContext;
  // Decision 3A: current priority of the Action target — drives
  // kindsForActionV4 filtering at the briefings.ts caller. Echoed
  // through to allow form-level "Add/Edit X" label adjustments.
  // Decision 12.5: Informational is a current-state value (synth-only,
  // not manually settable). The form's priority dropdown still lists
  // only the 4 settable priorities — Informational is rendered as
  // current state but not as a pick-from option.
  priorityStatus?: "Critical" | "Delay Potential" | "Active" | "On Hold" | "Informational";
  // Decision 3B: populated-field state on the Action target. The
  // single-field forms (action_set_reason / impact / off_hold_condition /
  // due) flip their button label to "Add" vs "Edit" based on this.
  populatedFields?: ReadonlySet<string>;
  // Current values on the target, keyed by field name (reason / impact /
  // off_hold_condition / due / text). Edit forms pre-fill from this so the
  // recipient edits the existing value rather than starting blank. Only the
  // edit kinds in PREFILL_KINDS consume it (never the "add new" forms).
  current?: Record<string, string | null | undefined>;
  // REPORT-TAG-ONE-CLICK (2026-10-18): the one-click report-tag pill rendered at
  // the TOP of the ✎ menu (Ray: one-touch assign/dismiss). `report: "psu"` on a
  // work_item toggles the Item's PSU membership (item-keyed `wi.kil_marked` since
  // B/2026-09-16 — item_kil_mark/unmark); `report: "kil"` on an action toggles its
  // Key Issue Log membership (report_tags ∋ 558 — action_set_report_tags). `on` =
  // current membership. Undefined on closed targets / tiers with no report toggle →
  // no pill. Fires the one-click helpers directly (toggleItemPsu/toggleActionKil558),
  // mirroring acceptProposedAnswer — no new wire kind, no Worker.
  reportToggle?: { report: "psu" | "kil"; on: boolean };
  // N1 (2026-07-14): the agent's matter merge suggestion(s) for an Item — drives
  // the orange Accept/Dismiss proposal banner at the top of the picker (showPicker),
  // mirroring the closure_candidate affordance. `targets` holds every resolved
  // candidate (default the first); ≥2 → the banner shows a dropdown to pick which.
  // Accept opens the pre-filled item_merge_into form (merge_primary_item set to the
  // chosen target); Dismiss hides the banner (session-local). Absent → no banner.
  // #66 D1-D3 (2026-07-23): `reason` = the dedup-consult overlap tell, shown
  // as a muted line under the banner lead; `review` swaps the one-click
  // auto-submit for a VISIBLE prefilled item_merge_into form (whole-Item fold
  // scope guard — set when folding a multi-action Item on an overlap signal).
  mergeProposal?: {
    targets: Array<{ slug: string; label: string }>;
    reason?: string;
    review?: boolean;
  };
  // #66 D1-D3: overlap-aware localStorage dismiss key for the banner + the
  // inline ◇ notes (briefings.ts mergeBannerDismissKey). Defaults to targetId.
  mergeProposalDismissKey?: string;
  // A①: the merge target's display label ("1.1 Hearing preparation"),
  // threaded into the work_item_merge / action_merge event payload so the
  // optimistic "Recently Merged → into X" treatment can name the target and
  // survive reload (the label rides the POSTed event, not the payload).
  mergeTargetLabel?: string;
  // B10 (2026-06-03): whether the Item is closeable (0 open Actions). Drives
  // the "Also close this Item" checkbox in the Move-to-Category form — enabled
  // when closeable, disabled + B9 note otherwise. Set by the Item-header caller.
  itemCloseable?: boolean;
  // Cascade-close (2026-07-23): the Item's OPEN child Actions, threaded by the
  // Item-header caller when it offers work_item_close_cascade. Drives the
  // form's read-only confirmation list + the submit pre-loop's per-child
  // action_close POSTs. Every entry has a resolvable action_item target id
  // (the caller only offers the cascade when ALL open children have ai_slugs).
  cascadeOpenActions?: Array<{ target_id: string; label: string }>;
  // W3 CATEGORY-CASCADE-CLOSE (2026-10-07): the Category's OPEN Items (each with
  // its OPEN Action target ids), threaded by the Category-header caller when it
  // offers thread_close_cascade. Drives the form's read-only confirmation list +
  // the submit pre-loop's per-Item { action_close × Actions ; work_item_close }
  // POSTs. Every Item + Action has a resolvable target id (the caller only offers
  // the cascade when ALL are targetable — see the parity gate in briefings.ts).
  cascadeOpenItems?: Array<{
    target_id: string;
    label: string;
    display_number?: string | null;
    open_action_targets: Array<{ target_id: string; label: string }>;
  }>;
  // Slice 2 (2026-09-09): the Item's open Actions (target id + current report
  // membership) for the item_report_inclusion cascade — the submit fans out
  // action_set_report_tags / action_kil_mark over these per the checkbox delta.
  reportCascadeActions?: Array<{
    target_id: string; kil_marked: boolean; report_tags: string[];
  }>;
  // A2 (Ray 2026-09-15): the Item's open Actions (target id + current
  // association set) for the item_set_associations CASCADE — the submit fans out
  // one REPLACE-WHOLE action_set_associations per open Action (only where the set
  // actually changes). Setting at the Item tier propagates to all its Actions;
  // the Item chip is then a pure rollup of them (itemAssociationUnion).
  associationCascadeActions?: Array<{
    target_id: string; association_tags: string[];
  }>;
  // E-5 (Ray 2026-09-16): the Item's open Actions (target id + current priority)
  // for the item_set_priority CASCADE — the submit fans out one
  // action_priority_change per open Action whose priority differs from the chosen
  // one (collectItemPriorityCascade). Dropdown-only; no reason/impact/off-hold at
  // the Item tier. Present on ALL projects (priority is universal).
  priorityCascadeActions?: Array<{
    target_id: string; priority_status: string;
  }>;
  // E-7a (Ray 2026-09-16): the sub-category's member Items (open + closed), for
  // the subcategory_move fan-out. Each carries its work_item_slug + current
  // display_number (for the item_move_to_category breadcrumb). The submit
  // decomposes the move into subcategory_add + per-member
  // [item_move_to_category → item_move_to_subcategory] + subcategory_delete
  // (collectSubcategoryMove). Threaded from renderSubcategoryGroup.
  subcategoryMoveMembers?: Array<{
    work_item_slug: string; former_display_number?: string;
  }>;
  // A³/A² (2026-06-03): the Needs-Your-Input bucket entry's stable identity,
  // threaded from renderUnfiledV4 / renderInformationalV4. The Place tiers
  // (place_from_unfiled / place_as_category / place_as_action) need the
  // `source_ref` (for next-run bucket suppression) + the entry `id`; neither
  // is a visible form field, so the submit handler injects them from here.
  bucketContext?: { sourceRef: string; entryId: string;
    // #60: the entry's display text + narrative — prefill the Place-as-New
    // name/text and narrative fields (editable); text snapshots the Merge note.
    entryText?: string;
    entryNarrative?: string;
    // #60 PR4-v2 (#1.4): set on rows folded in from a candidate thread, so a
    // Place/Merge submit retires the inferred thread (the #57 exit).
    candidateSlug?: string };
  // #60 PR4-v2 Merge-Into-Existing pickers — existing entities at each tier,
  // OPEN and CLOSED (the row may dedup against either). Separate option
  // sources from threadOptions/itemOptions (those stay open-only for the
  // Place pickers, per the 2026-06-09 closed/candidate exclusion). Items
  // union the briefing blob with the all-ages Closed-Items snapshot.
  mergeThreadOptions?: ThreadOption[];
  mergeItemOptions?: ItemOption[];
  mergeActionOptions?: ClosedActionOption[];
  // Decision 3.6 breadcrumb: the moving entity's current path notation
  // ("3.7" for an Item, "3.7.2" for an Action), threaded into the
  // item_move_to_category / action_move_to_item event as
  // `former_display_number` so next week's render can show
  // "(formerly 3.7, recategorized DATE)". Not a visible form field — the
  // submit handler injects it from here (like mergeTargetLabel).
  movingFromDisplayNumber?: string | null;
}

export interface ParticipantOption {
  name: string;
  email?: string | null;
}

export interface ThreadOption {
  slug: string;
  name: string;
  display_number?: string | null;
  // #60 PR4-v2: set on Merge-Into-Existing option sources so the Open/Closed
  // toggle can filter. Unused by the open-only Place pickers.
  closed?: boolean;
  // #75: this Category's OPEN sub-categories, for the deep-move destination
  // picker ("Category › Sub-category" in one step). Mirrors ThreadOptionView.
  subcategories?: { slug: string; name: string }[];
  // B3 (2026-09-15): the Category's sub-project, for the Sub-project → Category
  // pre-filter in the move/place pickers. Mirrors ThreadOptionView; null on a
  // flat project or an unassigned Category.
  sub_project_slug?: string | null;
}

/** #1a UX (2026-06-19): the deep-move picker is a CASCADE — first pick a
 * Category, then a second <select> surfaces THAT Category's sub-categories.
 * `moveCascadeCategoryOptions` builds the first <select> (open Categories,
 * bare `<thread_slug>` values). Replaces the single flattened "Category ›
 * Sub-category" list (functional but undiscoverable when moving fast —
 * operator feedback 2026-06-19). */
export function moveCascadeCategoryOptions(
  threadOptions: ThreadOption[],
  // B3: when set, restrict to one sub-project's Categories — a real slug, or
  // SUBPROJECT_UNASSIGNED for Categories with no (or an unknown) sub-project.
  // Omitted (or when the project is flat) → the full flat list (unchanged).
  subProjectFilter?: string,
  knownSubProjectSlugs?: ReadonlySet<string>,
): { value: string; label: string }[] {
  return threadOptions
    .filter((t) => !t.closed)
    .filter((t) => threadInSubProject(t, subProjectFilter, knownSubProjectSlugs))
    .map((t) => ({ value: t.slug, label: threadOptionLabel(t) }));
}

/** B3 (2026-09-15): the sentinel value for the "(No sub-project)" bucket in the
 * move/place Sub-project pre-filter — Categories with no `sub_project_slug`, or
 * one the project's sub-project registry does not know (config drift). Mirrors
 * briefings.isUnassignedThread. Deliberately not a valid slug (`[a-z0-9-]+`) so
 * it can never collide with a real sub-project. */
export const SUBPROJECT_UNASSIGNED = "__no_subproject__";

/** B3-merge: the "All sub-projects" sentinel for a MERGE pre-filter — the empty
 * string, treated by matchesSubProject as "no filter". Merges include this
 * (moves don't) because a duplicate may live in any sub-project and the
 * recipient may be searching rather than filing to a known place. */
export const SUBPROJECT_ALL = "";

/** B3: does an entity's `sub_project_slug` match the picked Sub-project filter?
 * The one primitive shared by every tier (Category / Item / Action):
 *  - filter unset → yes (the flat list, unchanged behaviour);
 *  - filter === SUBPROJECT_UNASSIGNED → yes iff no sub-project, or one not in
 *    `known` (mirrors isUnassignedThread — config drift lands in the bucket);
 *  - a real slug → yes iff it matches. */
export function matchesSubProject(
  subProjectSlug: string | null | undefined,
  subProjectFilter?: string,
  known?: ReadonlySet<string>,
): boolean {
  // null/undefined OR the SUBPROJECT_ALL sentinel ("") → no filter (all match).
  if (subProjectFilter == null || subProjectFilter === SUBPROJECT_ALL) return true;
  const sp = subProjectSlug ?? null;
  if (subProjectFilter === SUBPROJECT_UNASSIGNED) {
    return !sp || (known != null && !known.has(sp));
  }
  return sp === subProjectFilter;
}

function threadInSubProject(
  t: ThreadOption,
  subProjectFilter?: string,
  known?: ReadonlySet<string>,
): boolean {
  return matchesSubProject(t.sub_project_slug, subProjectFilter, known);
}

/** B3-merge: the leading Sub-project `<select>` options for ANY tier's pre-filter
 * — one entry per sub-project (registry order) with ≥1 non-closed entity, plus a
 * trailing "(No sub-project)" when any unassigned entity exists. EMPTY on a flat
 * project (no `subProjectOptions`) → the caller omits the pre-filter entirely.
 * Generalises moveSubProjectFilterOptions to Items/Actions (any carrier of
 * `sub_project_slug`). */
export function subProjectFilterOptions(
  entities: Array<{ sub_project_slug?: string | null; closed?: boolean }>,
  subProjectOptions: Array<{ value: string; label: string }> = [],
): { value: string; label: string }[] {
  if (!subProjectOptions.length) return [];
  const known = new Set(subProjectOptions.map((s) => s.value));
  const populated = new Set<string>();
  let hasUnassigned = false;
  for (const e of entities) {
    if (e.closed) continue;
    const sp = e.sub_project_slug ?? null;
    if (sp && known.has(sp)) populated.add(sp);
    else hasUnassigned = true;
  }
  const out = subProjectOptions.filter((sp) => populated.has(sp.value));
  if (hasUnassigned) {
    out.push({ value: SUBPROJECT_UNASSIGNED, label: "(No sub-project)" });
  }
  return out;
}

/** B3-merge: leading Sub-project `<select>` options for a MERGE picker — "All
 * sub-projects" first, then each populated sub-project + "(No sub-project)".
 * EMPTY on a flat project (caller omits the pre-filter). */
export function mergeSubProjectFilterOptions(
  entities: Array<{ sub_project_slug?: string | null; closed?: boolean }>,
  subProjectOptions: Array<{ value: string; label: string }> = [],
): { value: string; label: string }[] {
  const base = subProjectFilterOptions(entities, subProjectOptions);
  if (!base.length) return [];
  return [{ value: SUBPROJECT_ALL, label: "All sub-projects" }, ...base];
}

/** B3-merge: the data-sub-project bucket for an option — the real slug when the
 * registry knows it, else SUBPROJECT_UNASSIGNED (no/unknown sub-project). Lets
 * the client-side checkbox filter match by simple string equality (no registry
 * lookup): show iff the select value is SUBPROJECT_ALL or === this bucket. */
export function resolveSubProjectBucket(
  sub_project_slug: string | null | undefined,
  known?: ReadonlySet<string>,
): string {
  const sp = sub_project_slug ?? null;
  return sp && (known == null || known.has(sp)) ? sp : SUBPROJECT_UNASSIGNED;
}

/** B3-merge: the "All items" sentinel for the Action-merge second-level Item
 * filter (a non-slug value → no item filter). */
export const MERGE_ITEM_ALL = "__all_items__";

/** B3-merge: the primary-Item `<select>` options for item_merge_into, filtered
 * to `subProjectValue` (SUBPROJECT_ALL = no filter), self excluded. Shared by
 * the form build and the mount cascade so the label never diverges. */
export function mergeIntoItemOptions(
  spec: FormControlSpec, subProjectValue: string,
  // F2 (2026-09-24): an optional dependent Category filter (CATEGORY_ALL = none).
  categoryValue: string = CATEGORY_ALL,
): { value: string; label: string }[] {
  const selfWi = (spec.targetId.split("work_item:")[1] ?? "").trim();
  const known = new Set((spec.subProjectOptions ?? []).map((s) => s.value));
  return (spec.itemOptions ?? [])
    .filter((i) => i.slug !== selfWi)
    .filter((i) => matchesSubProject(i.sub_project_slug, subProjectValue, known))
    .filter((i) => matchesCategory(i.thread_slug, categoryValue))
    .map((i) => ({
      value: i.slug,
      label: [i.display_number, i.name, i.thread_name ? `· ${i.thread_name}` : ""]
        .filter(Boolean).join(" "),
    }));
}

/** B3-merge: the primary-Action `<select>` options for action_merge_into,
 * filtered to `subProjectValue` and `itemValue` (SUBPROJECT_ALL / MERGE_ITEM_ALL
 * = no filter on that axis), self excluded. Shared by the form build and the
 * two-level mount cascade. */
export function mergeIntoActionOptions(
  spec: FormControlSpec, subProjectValue: string, itemValue: string,
): { value: string; label: string }[] {
  const known = new Set((spec.subProjectOptions ?? []).map((s) => s.value));
  return (spec.actionOptions ?? [])
    .filter((a) => (a.target_id ?? a.ai_slug) !== spec.targetId && a.ai_slug !== "")
    .filter((a) => matchesSubProject(a.sub_project_slug, subProjectValue, known))
    .filter((a) => itemValue === MERGE_ITEM_ALL || a.work_item_slug === itemValue)
    .map((a) => {
      const base = [a.display_number, a.text].filter(Boolean).join(" ");
      return {
        value: a.target_id ?? a.ai_slug,
        label: a.item_label ? `${base} · in ${a.item_label}` : base,
      };
    });
}

/** B3-merge: the Item `<select>` options for the Action-merge second level —
 * distinct Items (value = work_item_slug, label = item_label) among the Action
 * options that fall in the picked sub-project, led by "All items". `known` is
 * the sub-project registry (so SUBPROJECT_UNASSIGNED resolves). */
export function mergeItemOptionsForSubProject(
  actionOptions: Array<{
    sub_project_slug?: string | null;
    work_item_slug?: string; item_label?: string;
  }>,
  subProjectValue: string,
  known?: ReadonlySet<string>,
): { value: string; label: string }[] {
  const seen = new Set<string>();
  const out = [{ value: MERGE_ITEM_ALL, label: "All items" }];
  for (const a of actionOptions) {
    if (!matchesSubProject(a.sub_project_slug, subProjectValue, known)) continue;
    const wi = a.work_item_slug;
    if (!wi || seen.has(wi)) continue;
    seen.add(wi);
    out.push({ value: wi, label: a.item_label ?? wi });
  }
  return out;
}

/** B3: options for the leading Sub-project `<select>` that pre-filters the
 * move/place Category picker. One entry per sub-project (registry order) that
 * has ≥1 open Category, plus a trailing "(No sub-project)" when any unassigned
 * Category exists. EMPTY when the project is flat (no `subProjectOptions`) — the
 * caller then omits the whole pre-filter and the Category `<select>` stays the
 * flat list, so a flat project renders byte-identically. */
export function moveSubProjectFilterOptions(
  threadOptions: ThreadOption[],
  subProjectOptions: Array<{ value: string; label: string }> = [],
): { value: string; label: string }[] {
  return subProjectFilterOptions(threadOptions, subProjectOptions);
}

/** B3: the sub-project value to pre-select in the leading `<select>` — the
 * moving/placing entity's current sub-project when it is one of the offered
 * options, else the first offered option. Keeps the common case (reorganise
 * within the same sub-project) a single glance while leaving every other
 * sub-project one pick away. Callers pass the parent Category's
 * `sub_project_slug` (moves) or null (Needs-Input placement → first). */
export function defaultMoveSubProject(
  filterOptions: Array<{ value: string; label: string }>,
  currentSubProjectSlug?: string | null,
): string {
  if (!filterOptions.length) return "";
  const cur = currentSubProjectSlug
    && filterOptions.some((o) => o.value === currentSubProjectSlug)
    ? currentSubProjectSlug
    : filterOptions[0]!.value;
  return cur;
}

/** B3: assemble the move/place Sub-project pre-filter for a form spec. ONE owner
 * of the flat-vs-multi-axis branch so all three call sites (item_move_to_category,
 * item_move, place_new) behave identically:
 *  - flat project (no sub-projects) → `filterOptions` empty; the caller omits the
 *    leading <select> and `categoryOptions` is the full flat list → byte-identical;
 *  - multi-axis → `filterOptions` populated; `categoryOptions` is filtered to
 *    `defaultSubProject` (the entity's current sub-project, else the first). */
export function buildMovePreFilter(spec: FormControlSpec): {
  filterOptions: { value: string; label: string }[];
  defaultSubProject: string;
  categoryOptions: { value: string; label: string }[];
} {
  const threadOptions = spec.threadOptions ?? [];
  const filterOptions =
    moveSubProjectFilterOptions(threadOptions, spec.subProjectOptions);
  if (!filterOptions.length) {
    return {
      filterOptions, defaultSubProject: "",
      categoryOptions: moveCascadeCategoryOptions(threadOptions),
    };
  }
  const defaultSubProject =
    defaultMoveSubProject(filterOptions, spec.currentSubProjectSlug);
  const known = new Set((spec.subProjectOptions ?? []).map((s) => s.value));
  return {
    filterOptions, defaultSubProject,
    categoryOptions:
      moveCascadeCategoryOptions(threadOptions, defaultSubProject, known),
  };
}

/** B3: the Category options for a picked sub-project value — used by the mount
 * wiring to repopulate the Category <select> when the leading Sub-project
 * <select> changes. Mirrors buildMovePreFilter's multi-axis branch. */
export function moveCategoryOptionsForSubProject(
  spec: FormControlSpec, subProjectValue: string,
): { value: string; label: string }[] {
  const known = new Set((spec.subProjectOptions ?? []).map((s) => s.value));
  return moveCascadeCategoryOptions(
    spec.threadOptions ?? [], subProjectValue || undefined, known,
  );
}

/** F2 (2026-09-24): the "All categories" sentinel for a Sub-project → Category
 * pre-filter over an Item picker — the empty string (matchesCategory treats it
 * as "no filter"). Kept distinct from SUBPROJECT_ALL for readability. */
export const CATEGORY_ALL = "";

/** F2: does an entity's parent Category slug match the picked Category filter?
 *  - filter unset / CATEGORY_ALL → yes (no category filter);
 *  - a real slug → yes iff the entity's parent Category matches. */
export function matchesCategory(
  threadSlug: string | null | undefined,
  categoryFilter?: string,
): boolean {
  if (!categoryFilter || categoryFilter === CATEGORY_ALL) return true;
  return (threadSlug ?? null) === categoryFilter;
}

/** F2: the Category `<select>` options for the dependent Category pre-filter —
 * "All categories" first, then the open Categories of the picked sub-project (or
 * the whole flat list when subProjectValue is SUBPROJECT_ALL / a flat project).
 * Reuses moveCascadeCategoryOptions so the labels match the move pickers. */
export function categoryFilterOptions(
  threadOptions: ThreadOption[],
  subProjectValue: string,
  known?: ReadonlySet<string>,
): { value: string; label: string }[] {
  const cats = moveCascadeCategoryOptions(
    threadOptions, subProjectValue || undefined, known);
  return [{ value: CATEGORY_ALL, label: "All categories" }, ...cats];
}

/** F2: whether to OFFER the Category pre-filter at all — only when ≥2 open
 * Categories exist among `threadOptions` (nothing to narrow → no select, so a
 * single-Category project renders byte-identically). */
export function categoryFilterHasChoice(threadOptions: ThreadOption[]): boolean {
  return threadOptions.filter((t) => !t.closed).length > 1;
}

/** F2: filter an Item option list by BOTH the picked sub-project and the picked
 * Category. Shared by the datalist and merge pickers so the scope rule is one
 * place. A closed-Item option (merge_into_existing) carries its thread_slug too. */
export function scopeItemOptions(
  items: readonly ItemOption[],
  subProjectValue: string,
  categoryValue: string,
  known?: ReadonlySet<string>,
): ItemOption[] {
  return items
    .filter((i) => matchesSubProject(i.sub_project_slug, subProjectValue, known))
    .filter((i) => matchesCategory(i.thread_slug, categoryValue));
}

/** #1a UX: the dependent second <select> — the picked Category's destinations.
 * Always leads with a "(directly under the Category)" option (the bare
 * `<thread_slug>`), then one entry per open sub-category
 * (`<thread_slug>/<subcategory_slug>`, slugs never contain "/"). The encoded
 * value is the SAME composite the flattened list produced, so the submit path
 * (planDeepMoveDestination) and the wire are unchanged. */
export function moveCascadeSubcategoryOptions(
  threadOptions: ThreadOption[],
  categorySlug: string,
): { value: string; label: string }[] {
  const cat = threadOptions.find((t) => t.slug === categorySlug && !t.closed);
  if (!cat) return [];
  const out = [{ value: cat.slug, label: "(Ungrouped)" }];
  for (const sc of cat.subcategories ?? []) {
    out.push({ value: `${cat.slug}/${sc.slug}`, label: sc.name });
  }
  return out;
}

/** #1b deep-placement: the place-form's dependent Sub-category <select> for the
 * picked Category. Unlike moveCascadeSubcategoryOptions (which encodes the
 * Category into the value for the move composite), the place payload carries
 * new_item_thread_slug + new_item_subcategory_slug as SEPARATE fields, so each
 * option value is the BARE sub-category slug — or "" for "(Ungrouped)" (place
 * directly under the Category; reshape strips the empty value). */
export function placeSubcategoryOptions(
  threadOptions: ThreadOption[],
  categorySlug: string,
): { value: string; label: string }[] {
  const cat = threadOptions.find((t) => t.slug === categorySlug && !t.closed);
  const out = [{ value: "", label: "(Ungrouped)" }];
  for (const sc of cat?.subcategories ?? []) {
    out.push({ value: sc.slug, label: sc.name });
  }
  return out;
}

/** #1a/#1b: wire a Category → Sub-category cascade — on the driver <select>'s
 * change, repopulate the dependent <select> via `optionsFor(driverValue)`,
 * defaulting to the first option. A no-op when either <select> isn't in this
 * form (so the same call is safe across every form). Mirrors the merge-scope
 * repopulate pattern. */
function wireSubcategoryCascade(
  form: HTMLFormElement,
  driverName: string,
  dependentName: string,
  optionsFor: (driverValue: string) => { value: string; label: string }[],
): void {
  const driver = form.querySelector<HTMLSelectElement>(
    `select[name="${driverName}"]`,
  );
  const dependent = form.querySelector<HTMLSelectElement>(
    `select[name="${dependentName}"]`,
  );
  if (!driver || !dependent) return;
  const repopulate = (): void => {
    dependent.replaceChildren();
    for (const o of optionsFor(driver.value)) {
      const opt = document.createElement("option");
      opt.value = o.value;
      opt.label = o.label;
      opt.textContent = o.label;
      dependent.appendChild(opt);
    }
    dependent.selectedIndex = 0;
  };
  driver.addEventListener("change", repopulate);
  repopulate();
}

/** B3 (2026-09-15): wire the Sub-project → Category pre-filter — on the leading
 * Sub-project <select>'s change, repopulate the Category <select> to that
 * sub-project's Categories, then DISPATCH a change on it so the existing
 * Category → Sub-category cascade re-fires (chained). No immediate call: the
 * field render already seeds the Category list from the default sub-project, and
 * the Category → Sub-category wire seeds the third level. Register AFTER that
 * wire so its change listener exists. A no-op when either <select> is absent (a
 * flat project, or a form without the pre-filter). */
function wireSubProjectCascade(
  form: HTMLFormElement,
  driverName: string,
  categoryName: string,
  optionsFor: (driverValue: string) => { value: string; label: string }[],
): void {
  const driver = form.querySelector<HTMLSelectElement>(
    `select[name="${driverName}"]`,
  );
  const dependent = form.querySelector<HTMLSelectElement>(
    `select[name="${categoryName}"]`,
  );
  if (!driver || !dependent) return;
  driver.addEventListener("change", () => {
    dependent.replaceChildren();
    for (const o of optionsFor(driver.value)) {
      const opt = document.createElement("option");
      opt.value = o.value;
      opt.label = o.label;
      opt.textContent = o.label;
      dependent.appendChild(opt);
    }
    dependent.selectedIndex = 0;
    // Chain the Category → Sub-category cascade (registered before this).
    dependent.dispatchEvent(new Event("change"));
  });
}

/** B3-merge: replace a <select>'s options and select `selectedValue` (or the
 * first option when it isn't present). */
function setSelectOptions(
  sel: HTMLSelectElement,
  options: { value: string; label: string }[],
  selectedValue?: string,
): void {
  sel.replaceChildren();
  for (const o of options) {
    const opt = document.createElement("option");
    opt.value = o.value;
    opt.label = o.label;
    opt.textContent = o.label;
    sel.appendChild(opt);
  }
  if (selectedValue !== undefined
      && options.some((o) => o.value === selectedValue)) {
    sel.value = selectedValue;
  } else {
    sel.selectedIndex = 0;
  }
}

/** B3-merge: wire the Sub-project (+ optional Item) pre-filter over a merge
 * MULTISELECT (work_item_merge — 1 level; action_merge — 2 levels). Hides each
 * `.multiselect-option` that doesn't match the picked scope, BUT keeps any
 * CHECKED option visible so a cross-scope selection never disappears, and shows
 * a live "N selected" count. On a sub-project change it also rebuilds the Item
 * <select> (action_merge). No-op unless both the Sub-project <select> and the
 * multiselect list are present.
 * F2 (2026-09-24): if a `merge_category` <select> is present (work_item_merge),
 * add it as a third show/hide axis (data-category) and repopulate its options to
 * the picked sub-project's Categories on a sub-project change. Absent → no-op. */
function wireMergeMultiselectFilter(
  form: HTMLFormElement, spec: FormControlSpec,
): void {
  const list = form.querySelector<HTMLElement>(".multiselect-list");
  if (!list) return;
  const spSel = form.querySelector<HTMLSelectElement>(
    'select[name="merge_sub_project"]');
  const itemSel = form.querySelector<HTMLSelectElement>(
    'select[name="merge_item"]');
  const catSel = form.querySelector<HTMLSelectElement>(
    'select[name="merge_category"]');
  // F2 (2026-09-24): bail only when there is NO filter select at all — a flat
  // project has no merge_sub_project but CAN have merge_category, and the old
  // `!spSel` guard silently skipped that whole axis. (Byte-identical on a
  // single-Category flat project: neither select is emitted → return here.)
  if (!spSel && !catSel) return;
  const known = new Set((spec.subProjectOptions ?? []).map((s) => s.value));

  // A live "N selected" count, so a checked-but-scrolled-off pick is never a
  // silent surprise. Inserted just above the list.
  const count = document.createElement("p");
  count.className = "form-helper merge-selected-count";
  list.parentElement?.insertBefore(count, list);

  const apply = (): void => {
    const spVal = spSel ? spSel.value : SUBPROJECT_ALL;
    const itemVal = itemSel ? itemSel.value : MERGE_ITEM_ALL;
    const catVal = catSel ? catSel.value : CATEGORY_ALL;
    let checkedCount = 0;
    for (const opt of Array.from(
      list.querySelectorAll<HTMLElement>(".multiselect-option"))) {
      const cb = opt.querySelector<HTMLInputElement>('input[type="checkbox"]');
      const checked = !!cb?.checked;
      if (checked) checkedCount += 1;
      const spMatch = !spSel || spVal === SUBPROJECT_ALL
        || opt.dataset.subProject === spVal;
      const itMatch = !itemSel || itemVal === MERGE_ITEM_ALL
        || opt.dataset.item === itemVal;
      const catMatch = !catSel || catVal === CATEGORY_ALL
        || opt.dataset.category === catVal;
      // Keep a checked option visible regardless of scope.
      opt.hidden = !(checked || (spMatch && itMatch && catMatch));
    }
    count.textContent = checkedCount
      ? `${checkedCount} selected` : "";
  };

  spSel?.addEventListener("change", () => {
    if (itemSel) {
      setSelectOptions(
        itemSel,
        mergeItemOptionsForSubProject(spec.actionOptions ?? [], spSel.value, known),
        MERGE_ITEM_ALL,
      );
    }
    if (catSel) {
      setSelectOptions(
        catSel,
        categoryFilterOptions(spec.threadOptions ?? [], spSel.value, known),
        CATEGORY_ALL,
      );
    }
    apply();
  });
  itemSel?.addEventListener("change", apply);
  catSel?.addEventListener("change", apply);
  list.addEventListener("change", apply); // recount + re-pin on check/uncheck
  apply();
}

/** B3-merge: wire the two-level Sub-project → Item pre-filter over the
 * action_merge_into single <select> (merge_primary_action). Rebuilds the Item
 * <select> on a sub-project change and the primary <select> on either change.
 * No-op unless merge_sub_project and merge_primary_action are both present. */
function wireActionMergeIntoCascade(
  form: HTMLFormElement, spec: FormControlSpec,
): void {
  const spSel = form.querySelector<HTMLSelectElement>(
    'select[name="merge_sub_project"]');
  const primary = form.querySelector<HTMLSelectElement>(
    'select[name="merge_primary_action"]');
  if (!spSel || !primary) return;
  const itemSel = form.querySelector<HTMLSelectElement>(
    'select[name="merge_item"]');
  const known = new Set((spec.subProjectOptions ?? []).map((s) => s.value));
  const rebuildPrimary = (): void => {
    setSelectOptions(primary, mergeIntoActionOptions(
      spec, spSel.value, itemSel ? itemSel.value : MERGE_ITEM_ALL));
  };
  spSel.addEventListener("change", () => {
    if (itemSel) {
      setSelectOptions(
        itemSel,
        mergeItemOptionsForSubProject(spec.actionOptions ?? [], spSel.value, known),
        MERGE_ITEM_ALL,
      );
    }
    rebuildPrimary();
  });
  itemSel?.addEventListener("change", rebuildPrimary);
}

/** F2 (2026-09-24): wire the Sub-project → Category → primary-Item cascade over
 * the item_merge_into single <select> (merge_primary_item). On a sub-project
 * change, repopulate the Category <select> to that sub-project's Categories THEN
 * rebuild the primary <select>; on a Category change, rebuild the primary. Either
 * of merge_sub_project / merge_category may be absent (flat / ≤1-Category) — the
 * cascade degrades to whatever axes are present, and the field's emitted options
 * stand when neither is present. No-op unless merge_primary_item is present.
 * Replaces the single-level wireSubProjectCascade for item_merge_into. */
function wireItemMergeIntoCascade(
  form: HTMLFormElement, spec: FormControlSpec,
): void {
  const primary = form.querySelector<HTMLSelectElement>(
    'select[name="merge_primary_item"]');
  if (!primary) return;
  const spSel = form.querySelector<HTMLSelectElement>(
    'select[name="merge_sub_project"]');
  const catSel = form.querySelector<HTMLSelectElement>(
    'select[name="merge_category"]');
  const known = new Set((spec.subProjectOptions ?? []).map((s) => s.value));
  const rebuildPrimary = (): void => {
    setSelectOptions(primary, mergeIntoItemOptions(
      spec,
      spSel ? spSel.value : SUBPROJECT_ALL,
      catSel ? catSel.value : CATEGORY_ALL,
    ));
  };
  spSel?.addEventListener("change", () => {
    if (catSel) {
      setSelectOptions(
        catSel,
        categoryFilterOptions(spec.threadOptions ?? [], spSel.value, known),
        CATEGORY_ALL,
      );
    }
    rebuildPrimary();
  });
  catSel?.addEventListener("change", rebuildPrimary);
}

/** B3 (2026-09-15): scope a `<datalist>`-backed text picker by a leading
 * `filter_sub_project` <select> — rebuild the datalist options via
 * `optionsFor(spValue)` on the select's change (and once initially). The text
 * input's typeahead still works; the filter only narrows the suggestions. When
 * the select is absent (flat project) the datalist shows the full list. */
function wireDatalistFilter(
  form: HTMLFormElement,
  datalist: HTMLDataListElement,
  optionsFor: (spValue: string) => { value: string; label: string }[],
): void {
  const sel = form.querySelector<HTMLSelectElement>(
    'select[name="filter_sub_project"]');
  const rebuild = (): void => {
    datalist.replaceChildren();
    for (const o of optionsFor(sel ? sel.value : SUBPROJECT_ALL)) {
      const opt = document.createElement("option");
      opt.value = o.value;
      opt.label = o.label;
      opt.textContent = o.label;
      datalist.appendChild(opt);
    }
  };
  rebuild();
  sel?.addEventListener("change", rebuild);
}

/** F2 (2026-09-24): scope a `<datalist>`-backed Item picker by BOTH a leading
 * `filter_sub_project` <select> AND a dependent `filter_category` <select>. On a
 * sub-project change, repopulate the Category <select> to that sub-project's
 * Categories (led by "All categories") THEN rebuild the datalist; on a Category
 * change, rebuild the datalist. Either <select> may be absent (a flat project has
 * no Sub-project select; a ≤1-Category project has no Category select) — the
 * datalist then falls back to the wider scope. Generalises wireDatalistFilter
 * with the Category axis; the itemOptionsFor closure applies both filters. */
function wireSubProjectCategoryDatalist(
  form: HTMLFormElement,
  datalist: HTMLDataListElement,
  categoryOptionsFor: (spValue: string) => { value: string; label: string }[],
  itemOptionsFor: (
    spValue: string, catValue: string,
  ) => { value: string; label: string }[],
): void {
  const spSel = form.querySelector<HTMLSelectElement>(
    'select[name="filter_sub_project"]');
  const catSel = form.querySelector<HTMLSelectElement>(
    'select[name="filter_category"]');
  const rebuild = (): void => {
    const spVal = spSel ? spSel.value : SUBPROJECT_ALL;
    const catVal = catSel ? catSel.value : CATEGORY_ALL;
    datalist.replaceChildren();
    for (const o of itemOptionsFor(spVal, catVal)) {
      const opt = document.createElement("option");
      opt.value = o.value;
      opt.label = o.label;
      opt.textContent = o.label;
      datalist.appendChild(opt);
    }
  };
  spSel?.addEventListener("change", () => {
    // Re-scope the Category select to the picked sub-project, then the datalist.
    if (catSel) {
      setSelectOptions(catSel, categoryOptionsFor(spSel.value), CATEGORY_ALL);
    }
    rebuild();
  });
  catSel?.addEventListener("change", rebuild);
  rebuild();
}

/** #75: decode the deep-move destination picked in `to_thread_slug` (a bare
 * thread slug, or `thread/subcat`) and rewrite `raw` into the right wire move.
 * Returns the resolved wireKind plus an optional grouping step to chain after a
 * cross-Category move. Mutates `raw`; pure → unit-testable.
 *  - bare destination → item_move_to_category (current behavior);
 *  - dest is a sub-category of ANOTHER Category → item_move_to_category now +
 *    chain item_move_to_subcategory (the destination Category is where the
 *    group lives);
 *  - dest is a sub-category of the CURRENT Category → just item_move_to_subcategory
 *    (no Category move, so no bogus "(formerly …)" breadcrumb). */
export function planDeepMoveDestination(
  raw: Record<string, unknown>,
  currentThreadSlug: string,
  wireKind: FeedbackKind,
): { wireKind: FeedbackKind; chainSubcategory: string | null; chainThread: string } {
  // #1a UX: the cascade's Category <select> is UI-only — the picked Category is
  // already encoded in to_thread_slug's composite. Prune it so it never rides
  // the wire payload (reshape copies every key).
  delete raw.to_category_pick;
  // B3: the Sub-project pre-filter <select> is UI-only (it just narrows the
  // Category list). Prune it on every path — this runs for both move forms
  // regardless of wireKind, so it never leaks onto any move wire.
  delete raw.move_sub_project;
  if (wireKind !== "item_move_to_category") {
    return { wireKind, chainSubcategory: null, chainThread: "" };
  }
  const dest = String(raw.to_thread_slug ?? "");
  const slash = dest.indexOf("/");
  if (slash === -1) {
    return { wireKind, chainSubcategory: null, chainThread: "" };
  }
  const destThread = dest.slice(0, slash);
  const subcat = dest.slice(slash + 1);
  if (destThread === currentThreadSlug) {
    // Same Category — group in place; drop the Category-move-only fields so no
    // spurious move/breadcrumb/close rides along.
    delete raw.to_thread_slug;
    delete raw.also_close;
    delete raw.former_display_number;
    raw.to_subcategory_slug = subcat;
    return { wireKind: "item_move_to_subcategory", chainSubcategory: null, chainThread: "" };
  }
  raw.to_thread_slug = destThread;
  return { wireKind: "item_move_to_category", chainSubcategory: subcat, chainThread: destThread };
}

// #60: one existing Action offered as a Merge-Into-Existing target. The picker
// needs BOTH slugs (the agent's action-tier probe keys on work_item +
// action_item), so the label resolves back to the pair at submit.
export interface ClosedActionOption {
  work_item_slug: string;
  action_item_slug: string;
  // Pre-built display label: "N.N.N <action text> (<Item name>)".
  label: string;
  closed?: boolean;
  // B3 (2026-09-15): parent Category's sub-project + the parent Item's display
  // label, for the merge_into_existing Action picker's Sub-project → Item filter.
  sub_project_slug?: string | null;
  item_label?: string;
}

export interface ItemOption {
  slug: string;
  name: string;
  // Optional display number ("3.1") shown alongside the name in the
  // picker. Decision 6 numeric ordering applies; SPA passes this for
  // operator convenience.
  display_number?: string | null;
  thread_name?: string | null;
  closed?: boolean;
  // B3-merge (2026-09-15): the parent Category's sub_project_slug, so the
  // Item-merge pickers can offer a Sub-project pre-filter over the project-wide
  // Item list. null on a flat project or an unassigned Category.
  sub_project_slug?: string | null;
  // F2 (2026-09-24): the parent Category's slug, so the Item pickers can add a
  // Sub-project → Category cascade that narrows the flat Item list to one
  // Category. null when the source thread had no slug (never in practice).
  thread_slug?: string | null;
}

// #38: one sub-category of the parent Category, offered to the
// item_move_to_subcategory picker (open groups + "(ungrouped)") and used as
// the subcategory_add slug-collision set (open AND closed). Fed from the
// (optimistically mutated) blob in briefings.ts, so session-pending adds are
// already present — the binding amendment's pending-aware picker mechanism.
export interface SubcategoryOption {
  slug: string;
  name: string;
  // true when status === "closed": excluded from the move picker, still
  // counted for slug collision (the UNIQUE space spans open + closed).
  closed?: boolean;
}

export interface ActionOption {
  ai_slug: string;
  text: string;
  display_number?: string | null;
  // B8 cross-Item action_merge: when the merge picker offers Actions from
  // OTHER Items, each option carries its full target id (thread/work_item/
  // action_item) + its parent Item slug + a human label. The submit handler
  // re-parents a cross-Item source into the target's Item (action_move_to_item)
  // before merging. Absent for the sibling-only case (same-Item merge).
  target_id?: string;
  work_item_slug?: string;
  item_label?: string;
  // B3-merge (2026-09-15): the parent Category's sub_project_slug, for the
  // Action-merge pickers' Sub-project → Item two-level pre-filter over the
  // project-wide Action list. null on a flat project / unassigned Category.
  sub_project_slug?: string | null;
}

export interface InvoiceOption {
  number: string;
  // Short label combining number + amount + status to disambiguate
  // entries in the dropdown.
  label: string;
}

// Decision 12.17 (2026-05-28, operator follow-up): CTAs carry the
// entity noun ("Close Category", "Close Item", "Close Action") so
// the recipient knows what they're closing. Retires the internal-
// vocab "thread" leak — surfaces as "Category" everywhere per
// [[role-terminology]]. Invoice labels capitalize the noun for
// parity with Category/Item/Action.
const KIND_LABEL: Record<string, string> = {
  thread_approve: "Approve",
  thread_confirm: "Confirm Category",
  thread_promote: "Promote to Category",
  thread_reject: "Reject as Not Relevant",
  thread_rename: "Rename Category",
  thread_merge: "Merge this Category into another",
  thread_retag: "Move to another Category",
  thread_close: "Close Category",
  // W3: the composite offered when the Category has open children.
  thread_close_cascade: "Close Category & its Items & Actions",
  thread_reopen: "Reopen Category",
  work_item_close: "Close Item",
  // Cascade-close: the one-click composite offered when open children exist.
  work_item_close_cascade: "Close Item & its open Actions",
  // W6: close the Actions but KEEP the Item open ("completed" = the PM's narrative).
  work_item_mark_completed: "Close all Actions (keep Item open)",
  work_item_reopen: "Reopen Item",
  responsible_party_assign: "Assign owner",
  review_clarification: "Clarify",
  date_correction: "Edit date",
  not_relevant: "Not relevant",
  // Round-4 (2026-06-12 #1): was "Add missing Item", a near-synonym of
  // item_add's "Add Item" — the recipient couldn't tell self-serve creation
  // from describe-it-for-the-operator. The verb now carries the distinction.
  missing_item: "Report something missing",
  comment: "Comment",
  flag_next_week: "Flag next week",
  // v4 (Task #9). Action affordances render as a flat picker per
  // Action row; the labels lean active-voice and short so the
  // popover stays tight.
  action_priority_change: "Change priority",
  action_set_reason: "Edit reason",
  action_set_impact: "Edit impact",
  action_set_off_hold_condition: "Edit off-hold condition",
  action_set_due: "Edit due date",
  action_open_question_add: "Add open question",
  action_open_question_resolve: "Answer question",
  action_open_question_dismiss: "Dismiss question",
  action_kil_mark: "Mark for Project Status Update",
  action_kil_unmark: "Unmark Project Status Update",
  // Slice 2 (2026-09-09): the consolidated per-Action report-inclusion control.
  action_report_inclusion: "Report inclusion",
  // Slice 2 (2026-09-09): the Item-tier "all actions → …" cascade control.
  item_report_inclusion: "Report inclusion",
  // Decision 12.8 (2026-05-28). action_close handles both manual
  // close (no candidate present) and "approve closing" (candidate
  // present). Same correction kind, same downstream effect.
  action_close: "Close Action",
  action_reopen: "Reopen Action",
  action_dismiss_closure_candidate: "Keep open (dismiss proposal)",
  // Option Y (2026-06-03): parks an action-less Item open instead of closing.
  work_item_dismiss_closure_candidate: "Keep Item open (dismiss proposal)",
  // B2 (2026-06-26): the disposition vocab's "seen, not tracked" verb — drops
  // the row from the active briefing into the Closed Archive's "Acknowledged /
  // Not Tracked" section, NEVER recording it as completed work.
  work_item_acknowledge: "Acknowledge (not tracked)",
  action_acknowledge: "Acknowledge (not tracked)",
  // B2 Track-this: bring an acknowledged entry back into active tracking.
  work_item_track: "Track this",
  action_track: "Track this",
  // B2 PR-1: the disposition vocab's "not this project" verb — rejects a placed
  // entity (a misclassification), dropping it from the briefing for good (not
  // archived, not resurfaceable). Records a `rejected` training signal.
  work_item_not_relevant: "Not relevant",
  action_not_relevant: "Not relevant",
  // R2-3: direct narrative edit (the structured alternative to a comment).
  work_item_set_narrative: "Edit narrative",
  action_set_narrative: "Edit narrative",
  // v6.1 (R): set the report-inclusion tag-set ({558,summary}).
  item_set_report_tags: "Set report inclusion",
  action_set_report_tags: "Set report inclusion",
  // v6.1 (R P3): set the association tag-set on a placed Item/Action.
  item_set_associations: "Set associations",
  action_set_associations: "Set associations",
  // v6.1 (R P4d-2): dismiss an inferred association candidate (ghost `?` chip).
  action_dismiss_association_candidate: "Dismiss suggestion",
  // v6.1 (R 558): set the KIL "Point" (responsible firm/s) on an Item. Label is
  // "Set KIL Point" for now (Ray 2026-10-27) — revisit once the report's name is
  // revised away from "KIL"/"558".
  item_set_point: "Set KIL Point",
  // R2-3 fast-follow: direct edit of the executive summary.
  set_executive_summary: "Edit summary",
  item_kil_mark: "Mark Item for Project Status Update",
  item_kil_unmark: "Unmark Item Project Status Update",
  item_add: "Add Item",
  category_add: "Add Category",
  action_add: "Add Action",
  place_from_unfiled: "Place as Item",
  // A³/A² (2026-06-03): tier-explicit Place on Needs-Your-Input entries.
  place_as_category: "Place as Category",
  place_as_action: "Place as Action",
  informational_acknowledge: "Dismiss (acknowledged)",
  place_new: "Place as New (Category · Item · Action)",
  merge_into_existing: "Merge Into Existing (Category · Item · Action)",
  item_move_to_category: "Move to",
  invoice_add: "Add Invoice",
  invoice_status_change: "Change Invoice status",
  invoice_blocking_note_set: "Set Invoice blocking note",
  // Task 3: work-item structural affordances.
  candidate_to_item: "Place as Item under Category",
  // Merge direction disambiguated by SUBJECT (Ray 6/30 dogfood: "Merge
  // Items" vs "Merge this Item into…" read the same). "other … into this
  // one" = THIS survives, others fold in; "this … into another" = THIS is
  // folded away into a picked primary.
  work_item_merge: "Merge other Items into this one",
  // #73: reverse merge — fold THIS Item up into a picked existing primary.
  item_merge_into: "Merge this Item into another",
  work_item_rename: "Rename Item",
  // Open improvement #3: action-level merge.
  action_merge: "Merge other Actions into this one",
  // B1 / #73: reverse merge — fold THIS Action up into a picked existing primary.
  action_merge_into: "Merge this Action into another",
  // B6: action-level rename.
  action_rename: "Rename Action",
  action_move_to_item: "Move to another Item",
  // #76: promote this Action up to its own Item.
  action_to_item: "Promote to Item",
  // #38: sub-category grouping. "Sub-category" is the client-facing label
  // (GLOSSARY.md); internal slug stays `subcategory`.
  subcategory_add: "Add Sub-category",
  subcategory_rename: "Rename Sub-category",
  subcategory_close: "Close Sub-category",
  // #74b: delete (evaporate) — distinct from Close (which tombstones).
  subcategory_delete: "Delete Sub-category",
  subcategory_reopen: "Reopen Sub-category",
  // E-7a (Ray 2026-09-16): relocate the whole sub-category to another Category.
  subcategory_move: "Move to another Category",
  item_move_to_subcategory: "Move to Sub-category",
  // #37: re-band the Category under a different proposal phase.
  category_move_to_phase: "Move to Phase",
  category_move_to_subproject: "Move to Sub-project",
  // Feedback 2.1: the combined Move button (pseudo-kind; shown only when both
  // destinations exist — otherwise the plain "Move to Category" renders).
  item_move: "Move to…",
  // Round-4 #2: the flattened metadata-edit button (pseudo-kind; shown when
  // the priority surfaces ≥2 edit fields — Active keeps "Edit due date").
  action_edit_details: "Edit details",
};

// Menu grouping (2026-07, R2 + #93). Ray 6/30: the feedback menus are "a
// little unwieldy" — a loaded Action pencil is a flat wall of up to ~19
// buttons, and he couldn't spot "Keep open (dismiss proposal)" in it. The
// picker below sections the flat kind list into labeled groups (static
// headings — no collapse, so zero added clicks). This is IN-PLACE
// sectioning of the SAME popover, not a relocated panel (the close-panel
// Ray rejected 6/19 — feedback-inline-over-separate-panel). The kind
// arrays (kindsFor*V4) are unchanged; grouping is a render-only concern,
// and within-section order preserves the operator-tuned sequence.
//
// One `id` per semantic cluster; SECTION_ORDER fixes their top-to-bottom
// order (only non-empty sections render). "" label = a heading-less
// trailing bucket so an unmapped/new kind still shows (never disappears).
export const MENU_SECTIONS: ReadonlyArray<{ id: string; label: string }> = [
  // Ray 2026-10-14: "Status should be the first selection" + "rename Place to
  // Reorganize for consistency." The old "place" section (NYI triage verbs) is
  // folded into "reorganize" (see SECTION_BY_KIND) — placement and move/merge
  // never co-occur on one entry (NYI = place-only; placed Items/Actions =
  // move-only), so one "Reorganize" section reads consistently everywhere, and
  // with no "place" section preceding it, "Status" now leads the NYI menu.
  { id: "status", label: "Status" },
  { id: "edit", label: "Edit" },
  { id: "reorganize", label: "Reorganize" },
  { id: "add", label: "Add" },
  // Slice 2 (2026-09-09): the consolidated per-Action "Report inclusion" control
  // (PSU + Key Issue Log) lives in its own section. The legacy "psu" section
  // stays mapped for back-compat but no longer renders (its standalone toggles are
  // no longer offered — `action_report_inclusion` replaces them).
  { id: "reports", label: "Reports" },
  { id: "psu", label: "Project status update" },
  { id: "invoices", label: "Invoices" },
  { id: "notes", label: "Notes" },
  { id: "other", label: "" },
];

const SECTION_BY_KIND: Record<string, string> = {
  // Reorganize (NYI triage half) — surface an unplaced row into the tree or
  // reject it. Folded from the former "place" section into "reorganize" (Ray
  // 2026-10-14 — one consistent "Reorganize" label; Status leads the NYI menu).
  // Never co-occurs with the move/merge half below (an unplaced row can't be
  // moved), so the section renders only one family per entry.
  place_new: "reorganize", merge_into_existing: "reorganize",
  place_from_unfiled: "reorganize",
  place_as_category: "reorganize", place_as_action: "reorganize",
  candidate_to_item: "reorganize",
  thread_promote: "reorganize", thread_approve: "reorganize",
  thread_confirm: "reorganize", thread_reject: "reorganize",
  // Status — lifecycle (close / reopen / keep-open) + the B2 disposition
  // verbs (acknowledge / not relevant / track) + sub-category delete.
  thread_close: "status", thread_close_cascade: "status", thread_reopen: "status",
  work_item_close: "status", work_item_close_cascade: "status",
  work_item_mark_completed: "status",
  work_item_reopen: "status",
  work_item_dismiss_closure_candidate: "status",
  work_item_acknowledge: "status", work_item_not_relevant: "status",
  work_item_track: "status",
  action_close: "status", action_reopen: "status",
  action_dismiss_closure_candidate: "status",
  action_acknowledge: "status", action_not_relevant: "status",
  action_track: "status",
  subcategory_close: "status", subcategory_reopen: "status",
  subcategory_delete: "status",
  informational_acknowledge: "status", not_relevant: "status",
  // Edit — narrative / rename / priority / per-field metadata / assign
  // owner / resolve open question / exec-summary.
  thread_rename: "edit",
  work_item_set_narrative: "edit", work_item_rename: "edit",
  responsible_party_assign: "edit",
  action_set_narrative: "edit", action_rename: "edit",
  // P1r (R reports): report-inclusion placement is an edit affordance.
  item_set_report_tags: "edit", action_set_report_tags: "edit",
  // P4d-1b (R association axis): association placement is an edit affordance.
  item_set_associations: "edit", action_set_associations: "edit",
  // P4d-2: dismissing an inferred-association suggestion is a status action.
  action_dismiss_association_candidate: "status",
  action_priority_change: "edit", action_set_reason: "edit",
  // E-5 (Ray 2026-09-16): the Item-tier "Set priority — all N actions" cascade
  // is an edit affordance, mirroring the action tier — NOT a trailing standalone
  // entry (the drift guard below now exercises hasOpenActions to keep it here).
  item_set_priority: "edit",
  // KIL-POINT (Ray 2026-10-28): "Set KIL Point" is an edit affordance (mirrors
  // the association/priority item-tier edits); gated to 558 Items in kindsForItemV4.
  item_set_point: "edit",
  action_set_impact: "edit", action_set_off_hold_condition: "edit",
  action_set_due: "edit", action_edit_details: "edit",
  action_open_question_resolve: "edit", action_open_question_add: "edit",
  action_open_question_dismiss: "edit",
  subcategory_rename: "edit", set_executive_summary: "edit",
  review_clarification: "edit", date_correction: "edit",
  // Reorganize — merge / move / promote / re-band.
  thread_merge: "reorganize", thread_retag: "reorganize",
  category_move_to_phase: "reorganize",
  category_move_to_subproject: "reorganize",
  work_item_merge: "reorganize", item_merge_into: "reorganize",
  item_move: "reorganize", item_move_to_category: "reorganize",
  item_move_to_subcategory: "reorganize",
  // E-7a (Ray 2026-09-16): "Move to another Category" on the sub-category header
  // — a reorganize affordance (pseudo-kind; decomposes at submit).
  subcategory_move: "reorganize",
  action_merge: "reorganize", action_merge_into: "reorganize",
  action_move_to_item: "reorganize", action_to_item: "reorganize",
  // Add — create a child / report a gap.
  item_add: "add", missing_item: "add", action_add: "add",
  subcategory_add: "add", category_add: "add",
  // Reports — the consolidated report-inclusion controls (Slice 2): per-Action,
  // and the Item-tier "all actions → …" cascade.
  action_report_inclusion: "reports",
  item_report_inclusion: "reports",
  // Project status update — the legacy standalone PSU (KIL) mark toggles. Kept
  // mapped for back-compat; no longer offered on the menu (Slice 2 replaced them
  // with action_report_inclusion).
  action_kil_mark: "psu", action_kil_unmark: "psu",
  item_kil_mark: "psu", item_kil_unmark: "psu",
  // Invoices — the aggregate-Action billing ops.
  invoice_add: "invoices", invoice_status_change: "invoices",
  invoice_blocking_note_set: "invoices",
  // Notes — free-text comment / flag.
  comment: "notes", flag_next_week: "notes",
};

/** The menu section a feedback kind renders under. Unmapped kinds fall to
 *  the heading-less "other" bucket so a new/legacy kind is never dropped. */
export function sectionForKind(kind: string): string {
  return SECTION_BY_KIND[kind] ?? "other";
}

// #93 close-suggestion pairing: when a closure_candidate is live, the Close
// verb and its "Keep open (dismiss proposal)" counterpart for the SAME
// entity render adjacently in a highlighted mini-group (both responses to
// the proposal, together). Adjacency, not a two-step control — 0 added
// clicks. Keyed close-kind → its dismiss-kind.
export const CLOSURE_PAIR_BY_CLOSE: Record<string, string> = {
  work_item_close: "work_item_dismiss_closure_candidate",
  // Cascade-close: approving an agent close proposal on an Item that still
  // has open children (Ray's main flow) pairs the CASCADE verb with the same
  // dismiss — without this key the "Close suggested" box dead-ends again.
  work_item_close_cascade: "work_item_dismiss_closure_candidate",
  action_close: "action_dismiss_closure_candidate",
};

// Below this many kinds a menu is already scannable — render it flat (no
// headings) to avoid over-structuring small pickers (sub-category, closed
// tombstones). D4 (2026-10-16): the flag_next_week sweep dropped every menu by
// one kind, so the threshold drops 6→5 to preserve the SAME grouping decisions
// (Needs-Input 5 + sub-category-with-move 5 stay sectioned; a 4-kind menu stays
// flat). Category (~7) / Item (~13) / Action (~18) / Needs-Input (5) clear it.
export const MENU_GROUPING_MIN_KINDS = 5;

// Popover header title — tier-specific so the recipient sees WHAT they're
// giving feedback on ("Item feedback" / "Action feedback") rather than a
// generic "Add feedback" (Ray 6/30 dogfood). The domain nouns
// (Category / Item / Action / Sub-category) are capitalized to match the
// KIND_LABEL house style; "feedback" stays lowercase (sentence case).
export function feedbackPopoverTitle(targetType: TargetType): string {
  switch (targetType) {
    case "thread":
      return "Category feedback";
    case "work_item":
    case "item":
      return "Item feedback";
    case "action_item":
      return "Action feedback";
    case "subcategory":
      return "Sub-category feedback";
    default:
      // project shell / unfiled / informational / legacy tiers.
      return "Add feedback";
  }
}

// One rendered row in a menu group: either a single kind button, or the
// #93 close-suggestion pair (Close + its dismiss counterpart, boxed).
export type MenuRow = { kind: string } | { pair: [string, string] };
export interface MenuGroup {
  /** MENU_SECTIONS id — keys the #107 collapse default + persistence. "" for
   *  the flat / trailing bucket, which has no heading and never collapses. */
  id: string;
  /** Section heading; "" renders no heading (flat menu / trailing bucket). */
  label: string;
  rows: MenuRow[];
}

// #107 / client item B (2026-08-12) — EVERY labeled section now starts
// COLLAPSED. Ray asked for the menu to roll up fully; the operator's call is to
// ship that and refine once he has used it, so this set is deliberately left in
// place (rather than deleted) — re-opening a section by default is a one-entry
// change here, which is the refinement path he expects.
//
// ⚠️ This supersedes the 7/07 shape, where Status / Edit / Place stayed open as
// "the everyday path". Noah's discoverability caveat still holds and is still
// satisfied: every header is one click either way and nothing is hidden
// permanently — the cost of the change is one extra click on the common verbs.
const DEFAULT_OPEN_SECTIONS: ReadonlySet<string> = new Set<string>();

/** #107: whether a section is expanded by default (before any stored pref). */
export function defaultSectionOpen(sectionId: string): boolean {
  return DEFAULT_OPEN_SECTIONS.has(sectionId);
}

const MENU_SECTION_PREF_PREFIX = "es_portal_menu_section_";

/** Client item B (2026-08-12): a section's open state, from the default ALONE.
 *
 *  The stored per-section preference is gone. Ray's actual complaint was that
 *  the menu "persists whatever state he last left them in, so he has to
 *  re-collapse each visit" — so leaving the read in place while flipping the
 *  default would have changed nothing for him: his own stored "open" values
 *  would have kept winning. The persistence WAS the bug, not the default.
 *
 *  In-session toggling is unaffected — it is DOM-local state in the menu
 *  renderer, so a section still opens on click; it simply starts collapsed
 *  again next time the menu opens. */
export function menuSectionIsOpen(sectionId: string): boolean {
  return defaultSectionOpen(sectionId);
}

/** Client item B (2026-08-12): drop any stored section preferences.
 *
 *  Deleted, not merely ignored — the same reasoning as the "Show New only"
 *  cleanup. The operator intends to REFINE this once Ray has used it, and if
 *  persistence is ever re-enabled, months-old values silently springing back to
 *  life would be a genuine surprise. Purging makes the re-enable start clean.
 *  Node-safe; best-effort. */
export function purgeMenuSectionPrefs(): void {
  try {
    if (typeof localStorage === "undefined") return;
    const stale: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(MENU_SECTION_PREF_PREFIX)) stale.push(k);
    }
    for (const k of stale) localStorage.removeItem(k);
  } catch {
    /* private-mode / disabled storage — nothing was persisted to begin with */
  }
}

const MERGE_PROPOSAL_DISMISS_PREFIX = "es_portal_merge_proposal_dismissed_";

/** N1 (2026-07-14): whether the recipient dismissed a specific Item→target merge
 *  suggestion. Session-local (localStorage): the matter consult is surface-only and
 *  re-derives every run, so a persistent/agent-trained dismissal is a follow-up —
 *  this just stops the banner re-nagging within the week. Node-safe. */
export function mergeProposalDismissed(key: string): boolean {
  try {
    if (typeof localStorage !== "undefined") {
      return localStorage.getItem(MERGE_PROPOSAL_DISMISS_PREFIX + key) === "1";
    }
  } catch {
    /* disabled storage — treat as not dismissed */
  }
  return false;
}

/** N1: persist a merge-suggestion dismissal so it doesn't re-appear on re-open. */
export function dismissMergeProposal(key: string): void {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(MERGE_PROPOSAL_DISMISS_PREFIX + key, "1");
    }
  } catch {
    /* best-effort */
  }
}

const PROPOSED_ANSWER_DISMISS_PREFIX = "es_portal_proposed_answer_dismissed_";

/** N2/#117: whether the recipient dismissed a specific proposed answer.
 *  localStorage-only (mirrors the N1 merge-proposal dismiss): the candidate
 *  stays in state (never shown on the download PSU / narrative until accepted),
 *  this just hides the suggestion for this recipient. A durable, cross-device
 *  dismiss is a backlogged follow-up (would need a new wire kind + Worker). */
export function proposedAnswerDismissed(key: string): boolean {
  try {
    if (typeof localStorage !== "undefined") {
      return localStorage.getItem(PROPOSED_ANSWER_DISMISS_PREFIX + key) === "1";
    }
  } catch {
    /* disabled storage — treat as not dismissed */
  }
  return false;
}

/** N2/#117: persist a proposed-answer dismissal so it doesn't re-appear. */
export function dismissProposedAnswer(key: string): void {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(PROPOSED_ANSWER_DISMISS_PREFIX + key, "1");
    }
  } catch {
    /* best-effort */
  }
}

// Pair a live close-suggestion (Close + "Keep open") into one boxed row,
// leaving every other kind as its own row. Order preserved; the dismiss
// kind is consumed where its close partner sits.
function toRows(kinds: string[]): MenuRow[] {
  const rows: MenuRow[] = [];
  const paired = new Set<string>();
  for (const kind of kinds) {
    if (paired.has(kind)) continue;
    const dismiss = CLOSURE_PAIR_BY_CLOSE[kind];
    if (dismiss && kinds.includes(dismiss)) {
      rows.push({ pair: [kind, dismiss] });
      paired.add(kind);
      paired.add(dismiss);
      continue;
    }
    rows.push({ kind });
  }
  return rows;
}

/** Pure layout planner for the feedback picker. Small menus (< MIN kinds)
 *  render flat (one unlabeled group); larger ones split into the ordered
 *  MENU_SECTIONS (only non-empty sections appear). Within-section order is
 *  the input order (the operator-tuned kindsFor*V4 sequence). The DOM
 *  render is a thin consumer of this plan — keeping the grouping logic
 *  node-testable without a DOM. */
export function planMenuLayout(kinds: string[]): MenuGroup[] {
  if (kinds.length < MENU_GROUPING_MIN_KINDS) {
    return [{ id: "", label: "", rows: toRows(kinds) }];
  }
  const groups: MenuGroup[] = [];
  for (const section of MENU_SECTIONS) {
    const inSection = kinds.filter((k) => sectionForKind(k) === section.id);
    if (inSection.length === 0) continue;
    // The heading-less trailing bucket (label "") keeps id "" so the render
    // treats it as flat (no collapse toggle).
    groups.push({ id: section.label ? section.id : "", label: section.label, rows: toRows(inSection) });
  }
  return groups;
}

function helperTextForKind(
  kind: string,
  targetType: FormControlSpec["targetType"],
): string {
  // Scope-specific: missing_item means different things on a Category
  // vs an Item, so split. Round-4 #1: each helper now names the OTHER
  // affordance so the create-now vs describe-for-triage split is explicit.
  if (kind === "missing_item") {
    if (targetType === "thread") {
      return "Describe an Item that should sit under this Category but isn't here. Nothing is created yet — the operator files it. Ready to name it yourself? Use Add Item instead.";
    }
    return "Describe an Action or fact that should sit under this Item but isn't here. Nothing is created yet — the operator triages it.";
  }
  if (kind === "item_add") {
    return "Creates the Item right away — you name it and it appears in the briefing. Only want to describe what's missing? Use Report something missing instead.";
  }
  if (kind === "action_edit_details") {
    return "Edit this Action's details in one place. Only the fields you change are submitted; leave a field as-is to keep it.";
  }
  if (kind === "work_item_close_cascade") {
    return "Closes this Item AND every open Action listed below in one step. Each Action is recorded as closed with the Item.";
  }
  if (kind === "work_item_mark_completed") {
    return "Closes every open Action listed below but KEEPS the Item open for reporting — nothing left to do. Record the “completed” status in the Item's narrative (Edit narrative).";
  }
  if (kind === "thread_close_cascade") {
    return "Closes this Category AND every open Item and Action listed below in one step. Each is recorded as closed with its parent.";
  }
  if (kind === "comment") {
    return "Free-form note attached to this entry. Operator-visible; doesn't change the briefing on its own.";
  }
  if (kind === "flag_next_week") {
    return "Asks the operator to surface this in next week's briefing even if nothing new arrives.";
  }
  if (kind === "subcategory_add") {
    return "Create an unnumbered grouping inside this Category (e.g. a contractor or area split). Items keep their numbers; you then move Items into it.";
  }
  if (kind === "subcategory_delete") {
    return "Removes this sub-category grouping. Its Items are NOT closed or lost — they stay in the Category, just un-grouped (their numbers don't change). Use this to undo a grouping you didn't want. Unlike Close, nothing is tombstoned — the group simply goes away.";
  }
  if (kind === "item_move_to_subcategory") {
    return "Group this Item under one of this Category's sub-categories, or choose “(ungrouped)” to take it out of a group. The Item's number doesn't change.";
  }
  if (kind === "item_move") {
    return "Move this Item to a different Category, or group it under one of this Category's sub-categories.";
  }
  if (kind === "category_move_to_phase") {
    return "Place this Category under a different proposal phase (Pre-Development, Pre-Construction, Construction, or General). Grouping only — the Category keeps its number and all its Items. The re-band shows here right away and is confirmed on next week's briefing.";
  }
  if (kind === "category_move_to_subproject") {
    return "File this Category under a different sub-project. Because the sub-project sets the Category's number letter, the Category and all its Items and Actions are re-numbered under the new sub-project (e.g. A5.1.1 → B5.1.1); you'll see a “(formerly …)” note for the one week the numbers change. The move shows here right away and is confirmed on next week's briefing.";
  }
  if (kind === "thread_reject") {
    return "Tells the operator this Category is off-topic for the project. The Category is removed and its evidence items are reclassified.";
  }
  // #60 PR4-v2 — the two consolidated placement verbs.
  if (kind === "place_new") {
    return "Create this as a new Category, Item, or Action where it belongs. Tick \"Already complete\" to file it as closed (a done item, not active work). Reflected in next week's briefing.";
  }
  // honest copy — Merge never reopens or adds active work; only the Item tier
  // produces a Closed-Items-visible record (per-field notes scope that claim).
  if (kind === "merge_into_existing") {
    return "For something that's the same as an existing item — folds this into it and stops it reappearing here. It won't show in the weekly briefing, and never reopens or adds active work.";
  }
  // Task 3 — say plainly what each structural action does + when it lands.
  if (kind === "candidate_to_item") {
    return "Creates an Item under the Category you choose, seeded from this suggestion, and removes the suggestion from Needs Your Input. Reflected in next week's briefing.";
  }
  if (kind === "work_item_merge") {
    return "Folds the Items you pick into this one. This Item stays the primary — it keeps its name and description (rename it below if you like); the others' Actions move under it and the merged Items drop off the briefing right away — you'll find them afterwards under Closed Items. Next week's briefing re-describes this Item from the combined work — nothing is blended into the title.";
  }
  if (kind === "item_merge_into") {
    return "Folds THIS Item up into the one you pick — the reverse of “Merge other Items into this one”. The picked Item becomes the primary (keeps its name and description); this Item's Actions move under it and this Item drops off the briefing right away — you'll find it afterwards under Closed Items. Use it when you're looking at a duplicate and want to roll it into the right Item without leaving this row.";
  }
  if (kind === "action_merge") {
    return "Folds the Actions you pick — from this or any other Item — into this one. This Action stays the primary (keep its wording, or rename it below); the others are marked merged with a 'from …' breadcrumb and drop off the briefing right away — you'll find them afterwards under Closed Items. Reflected in next week's briefing.";
  }
  if (kind === "action_merge_into") {
    return "Folds THIS Action up into the one you pick — the reverse of “Merge other Actions into this one”. The picked Action becomes the primary (keeps its wording); this Action is marked merged with a 'from …' breadcrumb and drops off the briefing right away — you'll find it afterwards under Closed Items. Use it when you're looking at a duplicate and want to roll it into the right Action without leaving this row.";
  }
  if (kind === "work_item_rename") {
    return "Changes this Item's display name only (its history is preserved). Reflected in next week's briefing.";
  }
  if (kind === "action_rename") {
    return "Changes this Action's text only (its history is preserved). Reflected in next week's briefing.";
  }
  if (kind === "action_to_item") {
    return "For an Action the agent should have tracked as its own Item. Creates a NEW Item (named below — defaults to this Action's text) under the Category you pick, and closes this Action with a \"promoted to Item\" note (it stays visible in Recently Closed). Use it on assumed/new rows — nothing is double-counted; the new Item appears here right away and is confirmed on next week's briefing.";
  }
  return "";
}

function describePayload(kind: string, payload: Record<string, unknown>): string {
  const pickStr = (k: string): string | null => {
    const v = payload[k];
    return typeof v === "string" && v.trim() ? v.trim() : null;
  };
  switch (kind) {
    case "responsible_party_assign": {
      const name = pickStr("name");
      return name ? `→ ${name}` : "";
    }
    case "thread_rename": {
      const to = pickStr("to_name");
      return to ? `New name: ${to}` : "";
    }
    case "thread_merge": {
      const tgt = pickStr("target_slug");
      return tgt ? `Merge into: ${tgt}` : "";
    }
    case "thread_retag": {
      const to = pickStr("to_thread");
      return to ? `New thread: ${to}` : "";
    }
    case "item_move_to_category": {
      const to = pickStr("to_thread_slug");
      return to ? `→ ${to}` : "";
    }
    case "item_move_to_subcategory": {
      const to = pickStr("to_subcategory_slug");
      return to ? `→ ${to}` : "→ (ungrouped)";
    }
    case "subcategory_add": {
      return pickStr("name") ?? "";
    }
    case "subcategory_rename": {
      const to = pickStr("to_name");
      return to ? `New name: ${to}` : "";
    }
    case "date_correction": {
      const field = pickStr("field") ?? "date";
      const value = pickStr("new_value") ?? "(not set)";
      return `${field} → ${value}`;
    }
    case "review_clarification": {
      const v = pickStr("verdict");
      return v ? `Verdict: ${v}` : "";
    }
    case "action_priority_change": {
      const np = pickStr("new_priority");
      const due = pickStr("due");
      const reason = pickStr("reason");
      const parts: string[] = [];
      if (np) parts.push(`→ ${np}`);
      if (due) parts.push(`due ${due}`);
      if (reason) parts.push(`reason: ${reason}`);
      return parts.join(", ");
    }
    case "action_set_due": {
      const due = pickStr("due");
      return due ? `due ${due}` : "(cleared)";
    }
    case "action_set_reason": {
      const v = pickStr("reason");
      return v ? `reason: ${v}` : "";
    }
    case "action_set_impact": {
      const v = pickStr("impact");
      return v ? `impact: ${v}` : "";
    }
    case "action_set_off_hold_condition": {
      const v = pickStr("off_hold_condition");
      return v ? `off-hold: ${v}` : "";
    }
    case "action_add": {
      const title = pickStr("text");
      const priority = pickStr("priority_status");
      const due = pickStr("due");
      const meta: string[] = [];
      if (priority) meta.push(priority);
      if (due) meta.push(`due ${due}`);
      const suffix = meta.length ? ` (${meta.join(", ")})` : "";
      return title ? `${title}${suffix}` : "";
    }
    case "item_add":
    case "category_add": {
      return pickStr("name") ?? "";
    }
    case "candidate_to_item": {
      const name = pickStr("name");
      const tgt = pickStr("target_thread_slug");
      const act = pickStr("initial_action_text");
      const parts: string[] = [];
      if (name && tgt) parts.push(`"${name}" → ${tgt}`);
      else if (name) parts.push(`"${name}"`);
      if (act) parts.push(`+ action: ${act}`);
      return parts.join(" ");
    }
    case "work_item_merge": {
      const srcs = payload["source_work_item_slugs"];
      const n = Array.isArray(srcs) ? srcs.length : 0;
      return n ? `merge ${n} Item${n === 1 ? "" : "s"} in` : "";
    }
    case "action_merge": {
      const srcs = payload["source_action_item_slugs"];
      const n = Array.isArray(srcs) ? srcs.length : 0;
      const rename = pickStr("target_text");
      const base = n ? `merge ${n} Action${n === 1 ? "" : "s"} in` : "";
      return rename ? `${base}${base ? ", " : ""}→ "${rename}"` : base;
    }
    case "work_item_rename": {
      const to = pickStr("to_name");
      return to ? `New name: ${to}` : "";
    }
    case "action_rename": {
      const to = pickStr("to_text");
      return to ? `New text: ${to}` : "";
    }
    case "thread_close":
    case "work_item_close":
    case "work_item_close_cascade":
    case "work_item_mark_completed":
    case "action_close":
    case "thread_reopen":
    case "work_item_reopen":
    case "action_reopen":
    case "subcategory_close":
    case "subcategory_reopen":
    case "subcategory_delete":
    case "thread_reject":
    case "work_item_acknowledge":
    case "action_acknowledge":
    // B2 PR-1: placed not-relevant rejection — optional reason, same shape.
    case "work_item_not_relevant":
    case "action_not_relevant": {
      const reason = pickStr("reason");
      return reason ? `Reason: ${reason}` : "";
    }
    // R2-3: direct narrative edit — show the (truncated) new text.
    case "work_item_set_narrative":
    case "action_set_narrative": {
      const narrative = pickStr("narrative");
      if (!narrative) return "Narrative cleared";
      return narrative.length > 80 ? `${narrative.slice(0, 80)}…` : narrative;
    }
    // R2-3 fast-follow: direct exec-summary edit — show the (truncated) new text.
    case "set_executive_summary": {
      const summary = pickStr("summary");
      if (!summary) return "Summary cleared";
      return summary.length > 80 ? `${summary.slice(0, 80)}…` : summary;
    }
    // KIL-POINT (Ray 2026-10-28): the 558 Point edit — show the new firm string.
    case "item_set_point": {
      const point = pickStr("point");
      return point ? `KIL Point: ${point}` : "KIL Point cleared";
    }
    case "action_open_question_add": {
      const text = pickStr("text");
      return text ? `Q: ${text}` : "";
    }
    case "action_open_question_resolve": {
      const answer = pickStr("answer");
      return answer ? `A: ${answer}` : "";
    }
    case "action_open_question_dismiss": {
      // Show WHICH question was dismissed — the chip is the only place the
      // recipient can still see it, since the question itself is gone from the
      // render the moment this lands.
      const q = pickStr("question_text");
      return q ? `Dismissed: ${q}` : "Dismissed";
    }
    case "action_kil_mark":
    case "action_kil_unmark":
    case "item_kil_mark":
    case "item_kil_unmark":
    case "action_dismiss_closure_candidate":
    case "work_item_dismiss_closure_candidate":
    case "informational_acknowledge":
    case "work_item_track":
    case "action_track":
      // Pure toggles / confirmations — no meaningful payload.
      return "";
  }
  // Generic fallbacks for kinds with a free-text field.
  const text = pickStr("text");
  if (text) return text;
  const note = pickStr("note");
  if (note) return note;
  return "";
}

function relativeTime(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return iso;
  const deltaSec = Math.round((Date.now() - then) / 1000);
  if (deltaSec < 30) return "just now";
  if (deltaSec < 60) return `${deltaSec}s ago`;
  if (deltaSec < 3600) return `${Math.round(deltaSec / 60)}m ago`;
  if (deltaSec < 86400) return `${Math.round(deltaSec / 3600)}h ago`;
  return `${Math.round(deltaSec / 86400)}d ago`;
}

const PENCIL_SVG =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M12 20h9"/>' +
  '<path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>' +
  "</svg>";

export interface PopoverPlacement {
  /** True → dock the menu ABOVE the trigger (CSS `.open-up`). */
  openUp: boolean;
  /** Cap the menu to this px height so it always fits the chosen side and
   *  scrolls internally instead of pushing the page. */
  maxHeight: number;
}

/**
 * #106: place the feedback popover collision-aware so opening a menu on a low
 * item never scrolls the whole briefing.
 *
 * The popover opens DOWN by default (CSS `top: calc(100% + 4px)`). On an item
 * low in the viewport that pushed the menu past the document bottom, forcing the
 * page to scroll. Rule:
 *  - Prefer DOWN. Flip UP only when the menu can't fit below AND there's more
 *    room above (near the top we stay down even if neither side fully fits).
 *  - Cap `maxHeight` to the available space on the chosen side (minus an 8px
 *    gap) so a menu taller than that side scrolls INSIDE itself and can't
 *    overflow either viewport edge — this is what removes the residual
 *    top-clip when a near-full-height menu flips up.
 *
 * All inputs are viewport-relative (getBoundingClientRect / innerHeight).
 */
export function computePopoverPlacement(
  triggerTop: number,
  triggerBottom: number,
  viewportHeight: number,
  menuHeight: number,
  gap = 8,
): PopoverPlacement {
  const spaceBelow = viewportHeight - triggerBottom - gap;
  const spaceAbove = triggerTop - gap;
  const openUp = menuHeight > spaceBelow && spaceAbove > spaceBelow;
  return { openUp, maxHeight: Math.max(0, openUp ? spaceAbove : spaceBelow) };
}

/** Client 2026-08-12 — identifies the ONE question a per-question ✎ acts on. */
export interface QuestionContext { qid: string; text: string }

/** The kinds a per-question ✎ offers. Answering and dismissing are the two
 *  dispositions of a question; both are scoped to a single qid, so neither
 *  form shows a "which question" picker. */
export const QUESTION_SCOPED_KINDS: readonly string[] = [
  "action_open_question_resolve",
  "action_open_question_dismiss",
];

/**
 * Stamp the per-question ✎'s identity onto an outgoing payload.
 *
 * Extracted from the submit handler so the DECISION is node-testable and the
 * DOM render stays a thin consumer — the same split `planMenuLayout` /
 * `sectionForKind` already use, and necessary here because this package has no
 * DOM test environment (`vitest.config.ts` → `environment: "node"`).
 *
 * `qid` is the load-bearing key (#52b): it survives the synthesizer re-wording
 * the question between runs, which the older text key did not. `question_text`
 * rides along only as the absorber's documented fallback for a legacy row
 * minted before qids existed. Mutates in place; returns whether it stamped.
 */
/**
 * What a question form should ASK for, given its kind and whether it is scoped
 * to one question. Node-testable decision behind the `buildForm` switch.
 *
 *  - `"none"`        — dismiss: nothing to type. A dismissal that demanded a
 *                      reason would recreate the friction it removes.
 *  - `"answer-only"` — scoped resolve: the question is already identified.
 *  - `"pick-then-answer"` — unscoped resolve (legacy / no context): the old
 *                      picker path. No caller offers this since the per-question
 *                      ✎ landed, but the branch stays for an older blob whose
 *                      questions carry no qid.
 */
export function questionFormShape(
  kind: string, ctx: QuestionContext | undefined,
): "none" | "answer-only" | "pick-then-answer" {
  if (kind === "action_open_question_dismiss") return "none";
  if (kind !== "action_open_question_resolve") return "pick-then-answer";
  return ctx ? "answer-only" : "pick-then-answer";
}

export function injectQuestionContext(
  payload: Record<string, unknown>,
  wireKind: string,
  ctx: QuestionContext | undefined,
): boolean {
  if (!ctx || !QUESTION_SCOPED_KINDS.includes(wireKind)) return false;
  payload.qid = ctx.qid;
  payload.question_text = ctx.text;
  // A scoped form never renders the legacy positional picker, and an index left
  // over from a prefill would out-rank nothing but could still confuse an
  // absorber fallback. Drop it so qid is unambiguously the key.
  delete payload.question_index;
  return true;
}

// REPORT-TAG-ONE-CLICK (2026-10-18): build the one-click report-tag pill for the
// TOP of the ✎ menu. Item → PSU, Action → KIL (558). Gray/off ↔ accent/on. On click
// it fires the wire kind directly (toggleItemPsu / toggleActionKil558). The helper's
// dispatched event (kil-mark-changed for PSU, feedback-state-changed for 558) runs
// applyKil/applyFeedback → renderViewer, which rebuilds the briefing (innerHTML="")
// and DISMISSES this popover — so the row's chip flips immediately and the pill shows
// its new state on the next menu open. There is deliberately no in-place pill
// re-render or spec patch here (it would run on the detached, torn-down popover, and
// a partial spec.current patch would desync the kept item_report_inclusion form's
// prefill — independent review finding 1, 2026-10-18). Only the error path (POST
// failed, no event dispatched, menu still open) touches the DOM.
export function buildReportTogglePill(
  spec: FormControlSpec,
): HTMLElement {
  const rt = spec.reportToggle!;
  const full = rt.report === "psu" ? "Project status update" : "Key issue log";
  const chip = rt.report === "psu" ? "PSU" : "KIL";
  const row = document.createElement("div");
  row.className = "popover-report-toggle-row";
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = rt.on ? "popover-report-toggle on" : "popover-report-toggle";
  btn.textContent = chip;
  btn.setAttribute("aria-pressed", String(rt.on));
  btn.setAttribute(
    "aria-label",
    rt.on ? `${full}: on. Click to remove.` : `${full}: off. Click to add.`,
  );
  const hint = document.createElement("span");
  hint.className = "popover-report-toggle-hint";
  hint.textContent = full;
  const curTags = (): string[] =>
    typeof spec.current?.report_tags === "string" && spec.current.report_tags
      ? spec.current.report_tags.split(",").map((s) => s.trim()).filter(Boolean)
      : [];
  btn.addEventListener("click", async () => {
    if (btn.disabled) return;
    btn.disabled = true;
    row.querySelector(".popover-report-toggle-error")?.remove();
    try {
      if (rt.report === "psu") {
        await toggleItemPsu(spec.runId, spec.targetId, rt.on);
      } else {
        await toggleActionKil558(spec.runId, spec.targetId, curTags());
      }
      // Success: the helper already dispatched the echo event, which tears down this
      // popover via renderViewer (see the function doc). Nothing to re-render here.
    } catch {
      btn.disabled = false;
      const err = document.createElement("p");
      err.className = "popover-report-toggle-error";
      err.textContent = "Couldn't update the tag. Try again.";
      row.appendChild(err);
    }
  });
  row.append(btn, hint);
  return row;
}

export function renderFormControls(spec: FormControlSpec): HTMLElement {
  const wrapper = document.createElement("div");
  wrapper.className = "controls";

  // Local mutable copy so optimistic adds after a submit are visible
  // without a refetch.
  const submitted: SubmittedEvent[] = [...(spec.submitted ?? [])];

  const trigger = document.createElement("button");
  trigger.type = "button";
  trigger.className = "btn-edit";
  trigger.innerHTML = PENCIL_SVG;
  const countBadge = document.createElement("span");
  countBadge.className = "btn-edit-count";
  trigger.appendChild(countBadge);

  function refreshTrigger(): void {
    if (submitted.length > 0) {
      trigger.classList.add("has-feedback");
      trigger.title = `${submitted.length} feedback item${submitted.length === 1 ? "" : "s"}`;
      trigger.setAttribute("aria-label", `Review feedback (${submitted.length})`);
      countBadge.textContent = String(submitted.length);
    } else {
      trigger.classList.remove("has-feedback");
      trigger.title = "Add feedback";
      trigger.setAttribute("aria-label", "Add feedback");
      countBadge.textContent = "";
    }
  }
  refreshTrigger();

  const popoverTitle = feedbackPopoverTitle(spec.targetType);

  const popover = document.createElement("div");
  popover.className = "popover hidden";
  popover.setAttribute("role", "dialog");
  popover.setAttribute("aria-label", popoverTitle);

  const header = document.createElement("div");
  header.className = "popover-header";
  const title = document.createElement("span");
  title.className = "popover-title";
  title.textContent = popoverTitle;
  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "btn-close";
  closeBtn.title = "Close";
  closeBtn.setAttribute("aria-label", "Close");
  closeBtn.textContent = "×";
  header.appendChild(title);
  header.appendChild(closeBtn);

  const body = document.createElement("div");
  body.className = "popover-body";

  popover.appendChild(header);
  popover.appendChild(body);

  let outsideHandler: ((e: MouseEvent) => void) | null = null;
  let escHandler: ((e: KeyboardEvent) => void) | null = null;

  function close(): void {
    popover.classList.add("hidden");
    body.innerHTML = "";
    if (outsideHandler) {
      document.removeEventListener("click", outsideHandler, true);
      outsideHandler = null;
    }
    if (escHandler) {
      document.removeEventListener("keydown", escHandler);
      escHandler = null;
    }
  }

  function showPicker(): void {
    body.innerHTML = "";

    // REPORT-TAG-ONE-CLICK (2026-10-18): the one-click report-tag pill at the very
    // TOP of the menu (Item → PSU, Action → KIL). A toggle dismisses the menu (its
    // echo event re-renders the briefing); the row's chip flips immediately.
    if (spec.reportToggle) {
      body.appendChild(buildReportTogglePill(spec));
    }

    // N1 (2026-07-14): agent merge-suggestion proposal banner at the TOP of the
    // menu — an orange-bounded Accept/Dismiss, mirroring the closure_candidate
    // affordance. Renders ABOVE the #107 sections (composes with the roll-up).
    // Accept opens the pre-filled item_merge_into form (target already selected
    // via spec.current.merge_primary_item → confirm); Dismiss hides it for the
    // session (the surface-only matter hint re-derives next run).
    const targets = spec.mergeProposal?.targets ?? [];
    const primary = targets[0];
    if (primary) {
      // Item-level key: dismissing hides this Item's whole merge proposal (banner
      // + inline note) for the session, regardless of how many candidates it named.
      // #66 D1-D3: overlap-bearing proposals carry a distinct key (briefings.ts
      // mergeBannerDismissKey) so a new overlap resurfaces past an old dismissal.
      const dismissKey = spec.mergeProposalDismissKey ?? spec.targetId;
      if (!mergeProposalDismissed(dismissKey)) {
        const banner = document.createElement("section");
        banner.className = "proposal-banner";
        const msg = document.createElement("p");
        msg.className = "proposal-banner-msg";
        const lead = document.createElement("span");
        lead.textContent = "Agent suggests merging this Item into ";
        msg.appendChild(lead);
        // One candidate → a bold label; several → a dropdown (default the first)
        // so the recipient picks WHICH to fold into before accepting.
        let getSelectedSlug: () => string;
        if (targets.length === 1) {
          const strong = document.createElement("strong");
          strong.textContent = primary.label;
          msg.appendChild(strong);
          getSelectedSlug = () => primary.slug;
        } else {
          const sel = document.createElement("select");
          sel.className = "proposal-target-select";
          for (const t of targets) {
            const opt = document.createElement("option");
            opt.value = t.slug;
            opt.textContent = t.label;
            sel.appendChild(opt);
          }
          sel.value = primary.slug;
          msg.appendChild(sel);
          getSelectedSlug = () => sel.value;
        }
        banner.appendChild(msg);
        // #66 D1-D3: the overlap tell as a muted context line ("Narrative
        // notes an overlap with …") so the recipient sees WHY before acting.
        if (spec.mergeProposal?.reason) {
          const why = document.createElement("p");
          why.className = "proposal-banner-reason";
          why.textContent = spec.mergeProposal.reason;
          banner.appendChild(why);
        }
        const actions = document.createElement("div");
        actions.className = "proposal-banner-actions";
        const accept = document.createElement("button");
        accept.type = "button";
        accept.className = "btn-action proposal-accept";
        // #66 D1-D3 review gate: folding a multi-action Item on an overlap
        // signal opens the prefilled form for review instead of auto-submitting
        // (the accept merges EVERY action the Item carries — show scope first).
        const reviewMode = Boolean(spec.mergeProposal?.review);
        accept.textContent = reviewMode ? "Review & merge…" : "Accept & merge";
        accept.addEventListener("click", () => {
          // One-click accept: build the item_merge_into form pre-targeted to the
          // chosen primary and submit it programmatically — reuses the EXACT
          // tested submit handler (FormData → reshape → resolveItemMergeInto →
          // work_item_merge → optimistic fold) with NO second click, NO flash.
          // Two things make a hidden programmatic submit reliable:
          //  • `noValidate` — a display:none form aborts requestSubmit()'s
          //    constraint validation on the non-focusable <select> even when it's
          //    valid; skipping validation lets the submit event fire. (The value
          //    is set explicitly below, so we don't need the browser's check.)
          //  • set the <select> value directly — belt-and-suspenders so the
          //    FormData carries the chosen primary regardless of pre-fill timing.
          const chosen = getSelectedSlug();
          spec.current = { ...(spec.current ?? {}), merge_primary_item: chosen };
          const form = buildForm(
            spec, "item_merge_into", onSubmitted, showPicker,
          ) as HTMLFormElement;
          const sel = form.querySelector<HTMLSelectElement>(
            'select[name="merge_primary_item"]',
          );
          if (sel) sel.value = chosen;
          body.innerHTML = "";
          body.appendChild(form);
          if (reviewMode) {
            // Visible prefilled form — the recipient confirms via its own
            // submit (scope guard for a multi-action whole-Item fold).
            return;
          }
          form.noValidate = true;
          form.hidden = true;
          form.requestSubmit();
        });
        const dismiss = document.createElement("button");
        dismiss.type = "button";
        dismiss.className = "btn-action proposal-dismiss";
        dismiss.textContent = "Dismiss";
        dismiss.addEventListener("click", () => {
          dismissMergeProposal(dismissKey);
          // operator 2026-07-14: dismissing clears the inline "◇ Possibly part
          // of…" note on the Item title too, not just the banner. The briefing
          // doesn't re-render on dismiss, so remove it directly here; the
          // render-time mergeProposalDismissed guard (briefings.ts) keeps it gone
          // on any later re-render / reload this session. #66 D1-D3: an Item can
          // carry BOTH a matter note and an overlap note — clear ALL of them
          // (action-row overlap notes use a different class, `action-overlap-
          // note`, and are inline-only advisories with no dismiss affordance).
          body.closest(".work-item")
            ?.querySelectorAll(".item-matter-note")
            .forEach((el) => el.remove());
          showPicker();
        });
        actions.append(accept, dismiss);
        banner.appendChild(actions);
        body.appendChild(banner);
      }
    }

    if (submitted.length > 0) {
      const history = document.createElement("section");
      history.className = "popover-history";
      const heading = document.createElement("h3");
      heading.className = "popover-section-heading";
      heading.textContent = "Submitted";
      history.appendChild(heading);

      // Lifecycle hint: recipient feedback sits in the Worker's pending
      // queue until the next absorb-feedback run (driven by the weekly
      // cron). Surfacing this is the difference between "nothing
      // happened" and "received, will land next week" — Ray's 2026-05-28
      // session flagged the gap explicitly ("not clear what happens
      // after I add feedback").
      //
      // Ray 2026-09-17: the banner text is tier-specific (see pendingBannerText).
      const lifecycleNote = document.createElement("p");
      lifecycleNote.className = "popover-history-note";
      lifecycleNote.textContent = pendingBannerText(submitted.map((e) => e.kind));
      history.appendChild(lifecycleNote);

      const ul = document.createElement("ul");
      ul.className = "popover-history-list";
      const ordered = [...submitted].sort((a, b) =>
        a.submitted_at < b.submitted_at ? 1 : a.submitted_at > b.submitted_at ? -1 : 0,
      );
      for (const ev of ordered) {
        const li = document.createElement("li");
        li.className = "popover-history-item";
        // Top line: kind label on the left, Undo control pinned top-right.
        const topline = document.createElement("div");
        topline.className = "popover-history-topline";
        const head = document.createElement("div");
        head.className = "popover-history-kind";
        head.textContent = KIND_LABEL[ev.kind] ?? ev.kind;
        topline.appendChild(head);
        // B7: Undo a still-pending event (not yet drained by poll-portal).
        // Hidden once absorbed — the inverse correction (Reopen / rename back)
        // is the path then. `pending !== false` keeps it shown for legacy /
        // fixture rows that omit the flag; the Worker 409s if it's wrong.
        // Two-step (Undo → Confirm/Cancel) so a stray click can't undo.
        if (ev.pending !== false) {
          topline.appendChild(buildUndoControl(ev, li));
        }
        li.appendChild(topline);
        const preview = describePayload(ev.kind, ev.payload);
        if (preview) {
          const p = document.createElement("div");
          p.className = "popover-history-preview";
          p.textContent = preview;
          li.appendChild(p);
        }
        const ts = document.createElement("div");
        ts.className = "popover-history-ts";
        ts.textContent = relativeTime(ev.submitted_at);
        ts.title = ev.submitted_at;
        li.appendChild(ts);
        ul.appendChild(li);
      }
      history.appendChild(ul);
      body.appendChild(history);

      const sectionHeading = document.createElement("h3");
      sectionHeading.className = "popover-section-heading";
      sectionHeading.textContent = "Add another";
      body.appendChild(sectionHeading);
    }

    // Slice 7 → Ray 6/30 dogfood: the "Close N open items first" advisory
    // no longer renders as an always-on banner at the top of the picker
    // (it read as noise). It stays on the gated close button's hover
    // tooltip, and expands inline UNDER that button if the recipient
    // clicks the greyed-out button anyway (see makeKindButton).

    const makeKindButton = (kind: string): HTMLButtonElement => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "btn-action";
      // Decision 3B (close-out 2026-05-25): "Add X" vs "Edit X" based
      // on whether the field is populated on the Action target. Same
      // kind submitted regardless — label-only change to help the
      // client see whether their click will fill a blank or edit
      // existing text. Driven by spec.populatedFields.
      btn.textContent = labelForKind(
        kind, spec.populatedFields, (spec.reportCascadeActions ?? []).length,
        (spec.associationCascadeActions ?? []).length,
        (spec.priorityCascadeActions ?? []).length,
      );
      // A gated close kind (thread_close with open Items, work_item_close
      // with open Actions, #38 subcategory_close with open members — B9):
      // the Worker would 409 the close. It renders greyed with the reason
      // on hover; clicking the greyed button toggles the reason INLINE
      // under it (instead of the old always-on banner), and never opens
      // the reject-bound form.
      const gated = Boolean(spec.notice) && (kind === "thread_close"
        || kind === "work_item_close" || kind === "subcategory_close");
      if (gated) {
        btn.classList.add("btn-action-disabled");
        btn.title = spec.notice!;
        btn.setAttribute("aria-disabled", "true");
        btn.addEventListener("click", () => {
          const sib = btn.nextElementSibling;
          if (sib && sib.classList.contains("popover-gate-reason")) {
            sib.remove();
            return;
          }
          const reason = document.createElement("p");
          reason.className = "popover-gate-reason";
          reason.textContent = spec.notice!;
          btn.insertAdjacentElement("afterend", reason);
        });
      } else {
        btn.addEventListener("click", () => showForm(kind as FeedbackKind));
      }
      return btn;
    };

    // #93 close-suggestion pair — Close + "Keep open (dismiss proposal)"
    // boxed together and highlighted so both responses to the proposal sit
    // adjacent (Ray couldn't find keep-open in the flat wall).
    const makeCloseSuggestion = (close: string, dismiss: string): HTMLElement => {
      const group = document.createElement("div");
      group.className = "popover-close-suggestion";
      const label = document.createElement("div");
      label.className = "popover-close-suggestion-label";
      label.textContent = "Close suggested";
      group.appendChild(label);
      group.appendChild(makeKindButton(close));
      group.appendChild(makeKindButton(dismiss));
      return group;
    };

    const list = document.createElement("div");
    list.className = "popover-actions";
    const rowEl = (row: MenuRow): HTMLElement =>
      "pair" in row
        ? makeCloseSuggestion(row.pair[0], row.pair[1])
        : makeKindButton(row.kind);

    // R2 (2026-07): thin DOM consumer of the pure planMenuLayout — labeled
    // section groups for a loaded menu, flat for small ones (one "" group).
    // #107: labeled sections are collapsible roll-ups — a toggle header (chevron
    // + count) over a container we hide when collapsed. Client item B
    // (2026-08-12): EVERY labeled section starts collapsed and nothing is
    // persisted, so the menu rolls up fully on every open. The flat "" bucket
    // never collapses.
    for (const group of planMenuLayout(spec.kinds)) {
      if (!group.label) {
        for (const row of group.rows) list.appendChild(rowEl(row));
        continue;
      }

      const kindCount = group.rows.reduce((n, r) => n + ("pair" in r ? 2 : 1), 0);
      let open = menuSectionIsOpen(group.id);

      const heading = document.createElement("button");
      heading.type = "button";
      heading.className = "popover-group-heading";
      heading.setAttribute("aria-expanded", String(open));

      const chevron = document.createElement("span");
      chevron.className = "popover-group-chevron";
      chevron.setAttribute("aria-hidden", "true");
      const labelEl = document.createElement("span");
      labelEl.className = "popover-group-label";
      labelEl.textContent = group.label;
      const countEl = document.createElement("span");
      countEl.className = "popover-group-count";
      countEl.textContent = String(kindCount);
      heading.append(chevron, labelEl, countEl);
      list.appendChild(heading);

      const container = document.createElement("div");
      container.className = "popover-group";
      for (const row of group.rows) container.appendChild(rowEl(row));
      list.appendChild(container);

      const applyOpen = (): void => {
        container.hidden = !open;
        heading.setAttribute("aria-expanded", String(open));
        heading.classList.toggle("collapsed", !open);
      };
      applyOpen();
      // Client item B: no persistence — the toggle is in-session DOM state only,
      // so the menu opens fully rolled up every time.
      heading.addEventListener("click", () => {
        open = !open;
        applyOpen();
      });
    }
    body.appendChild(list);
  }

  function showForm(kind: FeedbackKind): void {
    body.innerHTML = "";
    body.appendChild(buildForm(spec, kind, onSubmitted, showPicker));
  }

  function onSubmitted(event: SubmittedEvent): void {
    submitted.push(event);
    refreshTrigger();
    showPicker();
  }

  // B7: the per-row Undo control — a two-step affordance so a stray click
  // can't undo. Renders an "Undo" trigger; clicking it swaps to a
  // Confirm/Cancel pair (Cancel restores the trigger). Confirm runs the
  // actual undo via the module-level retractFeedbackEvent, then drops the
  // event from this closure's Submitted list. Edit = undo + resubmit; there is
  // no edit kind.
  function buildUndoControl(ev: SubmittedEvent, li: HTMLElement): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "popover-history-undo";

    const trigger = document.createElement("button");
    trigger.type = "button";
    trigger.className = "popover-history-undo-trigger";
    trigger.textContent = "Undo";
    trigger.title = "Undo this pending change";
    trigger.addEventListener("click", arm);

    function reset(): void {
      wrap.innerHTML = "";
      wrap.appendChild(trigger);
    }

    function arm(): void {
      wrap.innerHTML = "";
      const confirm = document.createElement("button");
      confirm.type = "button";
      confirm.className = "popover-history-undo-confirm";
      confirm.textContent = "Confirm";
      confirm.title = "Confirm undo";
      confirm.addEventListener("click", () => doUndo(confirm));
      const cancel = document.createElement("button");
      cancel.type = "button";
      cancel.className = "popover-history-undo-cancel";
      cancel.textContent = "Cancel";
      cancel.addEventListener("click", reset);
      wrap.appendChild(confirm);
      wrap.appendChild(cancel);
    }

    async function doUndo(confirmBtn: HTMLButtonElement): Promise<void> {
      confirmBtn.disabled = true;
      confirmBtn.textContent = "Undoing…";
      try {
        await retractFeedbackEvent(spec.runId, ev.event_id, ev.kind, spec.targetId);
        const idx = submitted.findIndex((e) => e.event_id === ev.event_id);
        if (idx !== -1) submitted.splice(idx, 1);
        refreshTrigger();
        showPicker(); // rebuild the list (+ the "Add another" header if empty)
      } catch (err) {
        // 409 carries a server message: either "already absorbed" (poll-portal
        // drained it between list + delete) or #64's dependent-close guard
        // ("retract the Item/Category close first"). Prefer the server's
        // wording — the two cases need different recipient instructions.
        reset();
        const note = document.createElement("div");
        note.className = "popover-history-undo-note";
        const serverMessage =
          err instanceof ApiError
          && typeof (err.body as { message?: unknown } | null)?.message === "string"
            ? ((err.body as { message: string }).message)
            : null;
        note.textContent = serverMessage
          ?? (err instanceof ApiError && err.status === 409
            ? "Already applied — reverse it with the matching action (e.g. Reopen)."
            : "Couldn't undo — try again.");
        li.appendChild(note);
      }
    }

    reset();
    return wrap;
  }

  function open(): void {
    popover.classList.remove("open-up");
    popover.style.maxHeight = "";
    popover.classList.remove("hidden");
    showPicker();
    // #106: collision-aware placement. Measure the trigger + the now-rendered
    // menu, then flip UP and/or cap the height so the menu always fits its side
    // of the viewport and scrolls internally — never pushing the page. See
    // computePopoverPlacement for the rule + tests.
    const rect = trigger.getBoundingClientRect();
    const placement = computePopoverPlacement(
      rect.top, rect.bottom, window.innerHeight, popover.offsetHeight);
    if (placement.openUp) popover.classList.add("open-up");
    popover.style.maxHeight = `${placement.maxHeight}px`;
    // Register closers on the next tick so the click that opened the
    // popover doesn't immediately close it.
    setTimeout(() => {
      outsideHandler = (ev: MouseEvent) => {
        if (popover.contains(ev.target as Node)) return;
        if (ev.target === trigger || trigger.contains(ev.target as Node)) return;
        close();
      };
      escHandler = (ev: KeyboardEvent) => {
        if (ev.key === "Escape") close();
      };
      document.addEventListener("click", outsideHandler, true);
      document.addEventListener("keydown", escHandler);
    }, 0);
  }

  function toggle(e: Event): void {
    e.stopPropagation();
    if (popover.classList.contains("hidden")) open();
    else close();
  }

  trigger.addEventListener("click", toggle);
  closeBtn.addEventListener("click", close);

  wrapper.appendChild(trigger);
  wrapper.appendChild(popover);
  return wrapper;
}

function buildForm(
  spec: FormControlSpec,
  kind: FeedbackKind,
  onSuccess: (event: SubmittedEvent) => void,
  onBack: () => void,
): HTMLElement {
  const form = document.createElement("form");
  form.className = "feedback-form";
  form.dataset.targetId = spec.targetId;
  form.dataset.kind = kind;

  const heading = document.createElement("p");
  heading.className = "form-heading";
  heading.textContent = KIND_LABEL[kind] ?? kind;
  form.appendChild(heading);

  // Per-kind helper text — addresses Ray's 2026-05-28 confusion on
  // missing_item / comment / flag_next_week ("not sure what this is
  // for"). One sentence; tells the recipient what the operator will
  // see and when it will land. Returns "" when the kind is
  // self-explanatory.
  const helper = helperTextForKind(kind, spec.targetType);
  if (helper) {
    const help = document.createElement("p");
    help.className = "form-helper";
    help.textContent = helper;
    form.appendChild(help);
  }

  // Cascade-close: read-only confirmation list of the open children this
  // submit will close alongside the Item — the recipient sees the full scope
  // before confirming (no silent cascade).
  if ((kind === "work_item_close_cascade" || kind === "work_item_mark_completed")
      && (spec.cascadeOpenActions ?? []).length) {
    const list = document.createElement("ul");
    list.className = "cascade-close-list";
    for (const child of spec.cascadeOpenActions!) {
      const li = document.createElement("li");
      li.textContent = child.label;
      list.appendChild(li);
    }
    form.appendChild(list);
  }

  // W3 CATEGORY-CASCADE-CLOSE: read-only confirmation list of every Item + its
  // open Actions this submit will close alongside the Category — the recipient
  // sees the full scope before confirming (no silent cascade). Actions nest
  // under their Item.
  if (kind === "thread_close_cascade" && (spec.cascadeOpenItems ?? []).length) {
    const list = document.createElement("ul");
    list.className = "cascade-close-list";
    for (const item of spec.cascadeOpenItems!) {
      const li = document.createElement("li");
      li.textContent = item.label;
      if (item.open_action_targets.length) {
        const sub = document.createElement("ul");
        sub.className = "cascade-close-list cascade-close-sublist";
        for (const act of item.open_action_targets) {
          const ali = document.createElement("li");
          ali.textContent = act.label;
          sub.appendChild(ali);
        }
        li.appendChild(sub);
      }
      list.appendChild(li);
    }
    form.appendChild(list);
  }

  // Decision 8 (close-out 2026-05-25): `select` widget for enum fields
  // (priority status, invoice status). Decision 3C: `visibleWhen`
  // conditional reveal so the combined action_priority_change form
  // can show reason / impact / off_hold_condition only when the
  // selected priority requires them.
  type FieldSpec = {
    name: string;
    label: string;
    type: "text" | "textarea" | "date" | "select" | "checkbox" | "multiselect" | "hidden";
    required?: boolean;
    /** Pre-filled initial value (edit forms — show the current value to edit). */
    value?: string;
    /** multiselect: option values to pre-check (edit forms whose current value
     *  is a SET — e.g. report_tags. `value` is single-valued, so it can't carry
     *  a set). */
    selectedValues?: string[];
    // B3-merge: `subProject` / `item` are optional per-option filter keys — the
    // multiselect renderer stamps them as data-sub-project / data-item on each
    // checkbox so a leading Sub-project (and, for Actions, Item) <select> can
    // show/hide options WITHOUT rebuilding the list (checked options stay).
    // F2: `category` is the parent Category slug — a third show/hide axis for the
    // work_item_merge multiselect (stamped as data-category).
    options?: Array<{
      value: string; label: string;
      subProject?: string | null; item?: string | null;
      category?: string | null;
    }>;
    /**
     * When set, this field renders hidden until the value of
     * `visibleWhen.field` matches one of `visibleWhen.equals`. Used
     * for combined-form conditional reveals (Decision 3C).
     */
    visibleWhen?: { field: string; equals: readonly string[] };
    /** E-5 (Ray 2026-09-16): conditional requiredness — the field is required
     *  ONLY when its `visibleWhen` driver value is in this list, regardless of
     *  `required`. Lets one conditional field be required for some driver values
     *  but optional for others (e.g. priority `reason` is required for Critical /
     *  Delay Potential but OPTIONAL for On Hold, per Ray's ask). An empty list =
     *  shown but never required. When set it OVERRIDES `required`. Honored in the
     *  conditional-reveal apply() (which is where the `required` attribute is
     *  synced with visibility anyway). No effect on fields without `visibleWhen`. */
    requiredWhen?: readonly string[];
    /** B10: render the input disabled (e.g. the "Also close" checkbox when
     *  the Item still has open Actions) with an optional explanatory note. */
    disabled?: boolean;
    note?: string;
  };
  const fields: FieldSpec[] = [];

  switch (kind) {
    case "thread_approve":
    case "thread_confirm":
    case "not_relevant":
    case "flag_next_week":
      fields.push({ name: "note", label: "Note (optional)", type: "textarea" });
      break;
    case "thread_promote":
      // A³ (2026-06-03): the recipient may rename the candidate while
      // promoting it. Pre-filled with the suggested name (spec.current.name).
      fields.push({
        name: "name", label: "Category name", type: "text", required: true,
      });
      break;
    case "thread_reject":
      fields.push({
        name: "reason",
        label: "Reason (optional — e.g. 'off-topic', 'wrong project')",
        type: "textarea",
      });
      fields.push({
        name: "evidence_item_ids",
        label: "Evidence item IDs (optional, comma-separated). Each ID also gets reclassified as 'rejected' for this project.",
        type: "text",
      });
      break;
    case "responsible_party_assign":
      fields.push({
        name: "name",
        label: "Owner name",
        type: "text",
        required: true,
      });
      fields.push({
        name: "email",
        label: "Email (optional)",
        type: "text",
      });
      fields.push({
        name: "note",
        label: "Note (optional — context for future operators)",
        type: "textarea",
      });
      fields.push({
        name: "is_new_person",
        label: "Add to project roster (so they're recognized in future briefings)",
        type: "checkbox",
      });
      break;
    case "comment":
      fields.push({
        name: "text",
        label: "Comment",
        type: "textarea",
        required: true,
      });
      break;
    case "thread_rename":
      fields.push({
        name: "new_name",
        label: "New name",
        type: "text",
        required: true,
      });
      break;
    case "thread_merge": {
      // F2 (2026-09-24): on a multi-axis project, prepend a Sub-project <select>
      // that scopes the "Merge into Category" datalist (was a flat project-wide
      // list with no filter at all — worse than its item_move_to_category
      // sibling). Flat → no select, full list (byte-identical). Mirrors
      // candidate_to_item's Sub-project → Category datalist.
      const tmFilter = mergeSubProjectFilterOptions(
        spec.threadOptions ?? [], spec.subProjectOptions);
      if (tmFilter.length) {
        fields.push({
          name: "filter_sub_project", label: "Sub-project", type: "select",
          options: tmFilter, value: SUBPROJECT_ALL,
        });
      }
      fields.push({
        name: "merge_into",
        label: "Merge into Category",
        type: "text",
        required: true,
      });
      break;
    }
    // Decision 2 (close-out): thread_retag, review_clarification, and
    // date_correction are operator-only — removed from the SPA. The
    // type union + absorber + CLI paths stay so existing corrections
    // and direct CLI use continue to work.
    case "missing_item":
      fields.push({
        name: "text",
        label: "What's missing?",
        type: "textarea",
        required: true,
      });
      break;
    case "thread_close":
    // W3: same optional reason (rides the CATEGORY close only; Items + Actions
    // carry fixed "Closed with …" markers — see the submit pre-loop).
    case "thread_close_cascade":
    case "work_item_close":
    // Cascade-close: same optional reason (rides the ITEM close only; children
    // carry a fixed "Closed with Item <N>" marker — see the submit pre-loop).
    case "work_item_close_cascade":
    // W6: same optional reason field (the Item stays open; its Actions carry a
    // fixed "Item work completed" marker — see the submit pre-loop).
    case "work_item_mark_completed":
    case "action_close":
      // Decision 12.8: action_close uses the same reason field as
      // work_item_close. Pre-fills NOTHING — if the recipient is
      // approving a closure_candidate, the candidate's reason is
      // already on record from synth; the optional reason field
      // here captures any *additional* note (e.g. "client confirmed
      // on call 5/30").
      fields.push({
        name: "reason",
        label: "Reason (optional — e.g. 'completed', 'scope cut')",
        type: "textarea",
      });
      break;
    case "thread_reopen":
    case "work_item_reopen":
    case "action_reopen":
      fields.push({
        name: "reason",
        label: "Reason (optional)",
        type: "textarea",
      });
      break;
    case "work_item_acknowledge":
    case "action_acknowledge":
      // B2: acknowledge ("seen, in-project, NOT tracked") — drops the row into
      // the Closed Archive's "Acknowledged / Not Tracked" section, NEVER
      // recorded as completed work. The optional note captures the *why* for
      // the determination log (e.g. "FYI only", "handled off-system").
      fields.push({
        name: "reason",
        label: "Note (optional — why is this not tracked?)",
        type: "textarea",
      });
      break;
    case "work_item_not_relevant":
    case "action_not_relevant":
      // B2 PR-1: "not relevant" (not this project) — a rejection. Drops the row
      // from the briefing for good (not archived, not resurfaceable) and records
      // a `rejected` training signal. The optional note captures the *why*
      // (e.g. "belongs to a different project", "spam").
      fields.push({
        name: "reason",
        label: "Note (optional — why isn’t this relevant?)",
        type: "textarea",
      });
      break;
    case "work_item_set_narrative":
    case "action_set_narrative":
      // R2-3: direct narrative edit — prefilled with the current narrative
      // (PREFILL_KINDS / spec.current.narrative) so the recipient edits what's
      // there. The verbatim text is reasserted every run (overrides the synth +
      // the agent pin). Blank submit clears the narrative.
      fields.push({
        name: "narrative",
        label: "Narrative",
        type: "textarea",
      });
      break;
    case "set_executive_summary": {
      // R2-3 fast-follow: direct exec-summary edit — prefilled with the current
      // summary (PREFILL_KINDS / spec.current.summary) so the recipient edits
      // what's there. Reasserted every run (overrides the synth's §14 summary).
      // Blank submit clears it.
      // R2: a band control passes spec.current.sub_project_slug → carry it as a
      // hidden field so the SAME kind targets that scope's summary (the absorber
      // routes on payload.sub_project_slug; absent → the project singleton).
      const spSlug = spec.current?.sub_project_slug;
      fields.push({
        name: "summary",
        label: typeof spSlug === "string" && spSlug
          ? "Sub-project summary" : "Executive summary",
        type: "textarea",
      });
      if (typeof spSlug === "string" && spSlug) {
        fields.push({ name: "sub_project_slug", type: "hidden", label: "", value: spSlug });
      }
      break;
    }
    case "item_set_report_tags":
    case "action_set_report_tags": {
      // P1r (R reports): report-inclusion is a SET {558, summary}, REPLACE-WHOLE
      // — the form carries the COMPLETE desired set. Pre-checked from the row's
      // current report_tags (spec.current.report_tags, comma-joined by
      // briefings.ts since spec.current is string-typed). PSU membership is the
      // separate kil_mark toggle and never appears here.
      const cur = typeof spec.current?.report_tags === "string"
        && spec.current.report_tags
        ? spec.current.report_tags.split(",").map((s) => s.trim()).filter(Boolean)
        : [];
      fields.push({
        name: "report_tags",
        label: "Include in client reports",
        type: "multiselect",
        selectedValues: cur,
        options: [
          { value: "558", label: "558 report" },
          { value: "summary", label: "Scale Summary" },
        ],
      });
      break;
    }
    case "action_report_inclusion": {
      // Slice 2 (2026-09-09) → B (client 2026-09-16, PSU→item-only): the Action
      // report-inclusion control governs ONLY the Key Issue Log (558) now — the
      // PSU is item-keyed and lives on the Item pencil. Pre-checked from
      // spec.current.report_tags; decomposed at submit (collectReportInclusionEdits)
      // into action_set_report_tags. REPLACE-WHOLE.
      const curTags = typeof spec.current?.report_tags === "string"
        && spec.current.report_tags
        ? spec.current.report_tags.split(",").map((s) => s.trim()).filter(Boolean)
        : [];
      const selected: string[] = [];
      if (curTags.includes("558")) selected.push("558");
      fields.push({
        name: "report_inclusion",
        label: "Include this action in",
        type: "multiselect",
        selectedValues: selected,
        options: [
          { value: "558", label: "Key Issue Log" },
        ],
      });
      break;
    }
    case "item_report_inclusion": {
      // Slice 2 (2026-09-09) → B (client 2026-09-16, PSU→item-only): the Item-tier
      // report control now carries TWO checkboxes of DIFFERENT scope (D4 — the
      // label carries the meaning):
      //  - "Project status update" → the ITEM's OWN mark (item_kil_mark/unmark);
      //  - "Key issue log — all N actions" → a CASCADE over the Item's open Actions
      //    (per-Action action_set_report_tags).
      // Pre-checked from spec.current.report_inclusion (comma-joined: "psu" iff the
      // Item's own mark is set, "558" iff every open Action is in the KIL); at submit
      // collectItemReportCascade routes each to the right tier.
      const cur = typeof spec.current?.report_inclusion === "string"
        && spec.current.report_inclusion
        ? spec.current.report_inclusion.split(",").map((s) => s.trim()).filter(Boolean)
        : [];
      const n = (spec.reportCascadeActions ?? []).length;
      fields.push({
        name: "report_inclusion",
        label: "Report inclusion",
        type: "multiselect",
        selectedValues: cur,
        options: [
          { value: "psu", label: "Project Status Update — this item" },
          {
            value: "558",
            label: n === 1
              ? "Key Issue Log — this Item's action"
              : `Key Issue Log — all ${n} actions`,
          },
        ],
      });
      break;
    }
    case "item_set_associations":
    case "action_set_associations": {
      // P4d-1b (R association axis): the association set is REPLACE-WHOLE (the
      // form carries the COMPLETE desired set), pre-checked from the row's
      // current association_tags (spec.current.association_tags, comma-joined by
      // briefings.ts). Options are the PER-PROJECT axis (spec.associationOptions,
      // from projectAssociationSlugs) — unlike report_tags' hardcoded set.
      // A2 (Ray 2026-09-15): at the ITEM tier this is a CASCADE — the pre-check is
      // the Item's rollup (union of its open Actions) and submit sets that whole
      // set on EVERY open Action (collectItemAssociationCascade), overwriting any
      // per-Action refinement. The note makes that explicit (operator 2026-09-15).
      // The ACTION tier is a plain per-Action set.
      const cur = typeof spec.current?.association_tags === "string"
        && spec.current.association_tags
        ? spec.current.association_tags.split(",").map((s) => s.trim()).filter(Boolean)
        : [];
      const isItemTier = kind === "item_set_associations";
      const nActs = (spec.associationCascadeActions ?? []).length;
      fields.push({
        name: "association_tags",
        label: isItemTier
          ? (nActs === 1
              ? "Associations — set on this Item's 1 action"
              : `Associations — set on all ${nActs} of this Item's actions`)
          : "Associations",
        type: "multiselect",
        selectedValues: cur,
        options: spec.associationOptions ?? [],
        ...(isItemTier
          ? { note: "Applies to every open action under this item, replacing their current associations. Adjust an individual action afterward with its own ✎." }
          : {}),
      });
      break;
    }
    case "item_set_priority": {
      // E-5 (Ray 2026-09-16): the Item-tier "Set priority" control — a single
      // priority <select> (dropdown-only; no reason/impact/off-hold at this tier)
      // whose choice CASCADES to every open Action under the Item at submit
      // (collectItemPriorityCascade → per-Action action_priority_change). No
      // pre-fill: the Item has no single priority (its actions may differ), so the
      // recipient always makes an explicit choice. Same enum as the Action form.
      const nActs = (spec.priorityCascadeActions ?? []).length;
      fields.push({
        name: "new_priority",
        label: nActs === 1
          ? "Priority — set on this Item's 1 action"
          : `Priority — set on all ${nActs} of this Item's actions`,
        type: "select",
        required: true,
        options: [
          { value: "Critical", label: "Critical" },
          { value: "Delay Potential", label: "Delay Potential" },
          { value: "Active", label: "Active" },
          { value: "On Hold", label: "On Hold" },
        ],
        note: "Applies to every open action under this item, replacing their current priority. Adjust an individual action afterward with its own ✎.",
      });
      break;
    }
    case "item_set_point": {
      // KIL-POINT (Ray 2026-10-28): the 558 "Point" (accountable/coordinating
      // firm/s, e.g. "Scale / KAMM") — a single plain-string UPDATE on the Item
      // (replace-whole, like set_narrative; absorber reads payload.point). Prefilled
      // with the current Point (PREFILL_KINDS / spec.current.point); blank submit
      // clears it. The <datalist> autocomplete (spec.pointOptions) is wired below
      // the generic render so a recipient reuses an existing string instead of a
      // typo variant that would fragment the Key Issue Log. Open-vocab → free text
      // still submits (no `required`, no options restriction).
      fields.push({
        name: "point",
        label: "KIL Point (responsible firm/s)",
        type: "text",
      });
      break;
    }
    case "action_dismiss_closure_candidate":
    // B2 Track-this: a one-click confirmation — the absorber needs only the
    // target (derived from targetId); it flips acknowledged -> open + deletes
    // the entity_acknowledged row.
    case "work_item_track":
    case "action_track":
      // No fields — this is a confirmation-style action. The form
      // submits with an empty payload; the absorber needs only the
      // target ai_slug (already derived from targetId).
      break;

    // --- v4 (Task #9) Action priority + metadata --------------------------
    // Decision 3C: combined form. Selecting a new priority reveals
    // exactly the metadata fields that priority requires. Selecting
    // Active hides everything. Decision 8: priority is a <select>,
    // not free-text — clients can't typo "Delay potential" anymore.
    case "action_priority_change":
      fields.push({
        name: "new_priority",
        label: "New priority",
        type: "select",
        required: true,
        options: [
          { value: "Critical", label: "Critical" },
          { value: "Delay Potential", label: "Delay Potential" },
          { value: "Active", label: "Active" },
          { value: "On Hold", label: "On Hold" },
        ],
      });
      fields.push({
        name: "reason",
        label: "Reason",
        type: "textarea",
        // E-5 (Ray 2026-09-16): reason stays REQUIRED for Critical / Delay
        // Potential (the Critical-priority guard) but is OPTIONAL for On Hold
        // (client ask — shown for reporting, not required). Still shown for all
        // three (visibleWhen unchanged); requiredWhen scopes the requirement.
        requiredWhen: ["Critical", "Delay Potential"],
        visibleWhen: {
          field: "new_priority",
          equals: ["Critical", "Delay Potential", "On Hold"],
        },
      });
      fields.push({
        name: "impact",
        label: "Impact",
        type: "textarea",
        required: true,
        visibleWhen: {
          field: "new_priority",
          equals: ["Critical", "Delay Potential"],
        },
      });
      fields.push({
        name: "off_hold_condition",
        label: "Off-hold condition",
        type: "textarea",
        // E-5 (Ray 2026-09-16): shown for On Hold but no longer REQUIRED —
        // recipient can add an off-hold condition for reporting or leave it
        // blank (client ask). Empty requiredWhen = shown, never required.
        requiredWhen: [],
        visibleWhen: {
          field: "new_priority",
          equals: ["On Hold"],
        },
      });
      fields.push({
        name: "due",
        label: "Due date",
        type: "date",
        visibleWhen: {
          field: "new_priority",
          equals: ["Critical", "Delay Potential", "Active"],
        },
      });
      break;
    case "action_set_reason":
      fields.push({
        name: "reason", label: "Reason", type: "textarea", required: true,
      });
      break;
    case "action_set_impact":
      fields.push({
        name: "impact", label: "Impact", type: "textarea", required: true,
      });
      break;
    case "action_set_off_hold_condition":
      fields.push({
        name: "off_hold_condition", label: "Off-hold condition",
        type: "textarea", required: true,
      });
      break;
    case "action_set_due":
      fields.push({
        name: "due", label: "Due date (leave blank to clear)", type: "date",
      });
      break;
    case "action_edit_details": {
      // Round-4 #2: ONE form for the priority-relevant metadata fields
      // (prefilled via PREFILL_KINDS from spec.current). None required —
      // the recipient edits a subset; collectActionDetailEdits submits only
      // what changed, as the per-field wire kinds. Same field set as the
      // kindsForActionV4 metaByPriority map; unknown priority shows all four
      // (the pre-Decision-3A defensive posture).
      const p = spec.priorityStatus;
      const showReason = p !== "Active";
      const showImpact = p === "Critical" || p === "Delay Potential" || !p;
      const showOffHold = p === "On Hold" || !p;
      const showDue = p !== "On Hold";
      if (showReason) {
        fields.push({ name: "reason", label: "Reason", type: "textarea" });
      }
      if (showImpact) {
        fields.push({ name: "impact", label: "Impact", type: "textarea" });
      }
      if (showOffHold) {
        fields.push({
          name: "off_hold_condition", label: "Off-hold condition",
          type: "textarea",
        });
      }
      if (showDue) {
        fields.push({
          name: "due", label: "Due date (leave blank to clear)", type: "date",
        });
      }
      break;
    }

    // --- v4 Open questions ---------------------------------------------
    case "action_open_question_add":
      fields.push({
        name: "text", label: "Question", type: "textarea", required: true,
      });
      fields.push({
        name: "raised_by", label: "Raised by (optional)", type: "text",
      });
      break;
    case "action_open_question_dismiss":
      // questionFormShape(kind, ...) === "none".
      // Client 2026-08-12: dispose of a question without answering it. Ray had
      // no way to clear one he judged irrelevant — this form will not submit
      // empty — so he typed "Do not track" into three, fabricating an answer
      // that the agent then fed to the narrative judge as his own words.
      //
      // NO FIELDS. Offered only from the per-question ✎, so `questionContext`
      // already identifies the target and the submit handler injects the qid.
      // There is deliberately nothing to type: a dismissal that asked for a
      // reason would recreate the friction it exists to remove.
      break;

    case "action_open_question_resolve": {
      // Scoped to ONE question (the per-question ✎) — no picker, because the
      // question is on screen and `questionContext` carries its qid.
      if (questionFormShape(kind, spec.questionContext) === "answer-only") {
        fields.push({
          name: "answer", label: "Answer", type: "textarea", required: true,
        });
        break;
      }
      // #52: identify the question by its TEXT (stable across the synth's
      // run-to-run re-derivation), not a positional index. A <select> shows what
      // is being resolved; value = the question text itself. Falls back to a
      // free-text field if the spec didn't carry the questions.
      const oqOpts = spec.openQuestionOptions ?? [];
      if (oqOpts.length > 0) {
        fields.push({
          name: "question_text", label: "Which question",
          type: "select", options: oqOpts, required: true,
        });
      } else {
        fields.push({
          name: "question_text", label: "Question", type: "text", required: true,
        });
      }
      fields.push({
        name: "answer", label: "Answer", type: "textarea", required: true,
      });
      break;
    }

    // --- v4 KIL marks (no fields — instant toggle) ----------------------
    case "action_kil_mark":
    case "action_kil_unmark":
    case "item_kil_mark":
    case "item_kil_unmark":
      // Pure toggle; no fields. The form's submit still emits an
      // empty payload, which is fine — the absorber doesn't read
      // payload on these kinds.
      break;

    // --- v4 Add affordances --------------------------------------------
    // Decision 7B (close-out 2026-05-25): slug fields removed.
    // The client should never see a kebab-case slug. `reshapePayload-
    // ForKind` derives slugs from `name`/`text` via `slugify()`;
    // collisions get a numeric suffix server-side.
    case "item_add":
      fields.push({
        name: "name", label: "Item name", type: "text", required: true,
      });
      fields.push({
        name: "narrative", label: "Narrative (2-3 lines)", type: "textarea",
      });
      fields.push({
        name: "responsible_party_name",
        label: "Responsible (defaults to project PM)", type: "text",
      });
      fields.push({
        name: "start_date",
        label: "Start date (defaults to today)", type: "date",
      });
      break;
    case "category_add":
      fields.push({
        name: "name", label: "Category name", type: "text", required: true,
      });
      // N4b (Ray 2026-09-22): collect the placement so a new Category lands
      // where the recipient chose, not the null/null "(No sub-project)/General"
      // default. Sub-project only on a sub-projected project (empty options →
      // omit the select, form stays name+description there); the leading
      // "(No sub-project)" option (empty value) is the safe default so an
      // ignored field preserves today's landing while the choice is visible.
      // Phase always (all projects band); the value IS the wire `phase` enum,
      // mirroring category_move_to_phase.
      if ((spec.subProjectOptions?.length ?? 0) > 0) {
        fields.push({
          name: "sub_project_slug",
          label: "Sub-project",
          type: "select",
          options: [
            { value: "", label: "(No sub-project)" },
            ...(spec.subProjectOptions ?? []),
          ],
          value: "",
        });
      }
      fields.push({
        name: "phase",
        label: "Phase",
        type: "select",
        options: [
          { value: "pre_development", label: "Pre-Development" },
          { value: "pre_construction", label: "Pre-Construction" },
          { value: "construction", label: "Construction" },
          { value: "general", label: "General" },
        ],
        value: "general",
      });
      fields.push({
        name: "description", label: "Description (optional)", type: "textarea",
      });
      break;
    case "action_add":
      fields.push({
        name: "text", label: "Action title", type: "text", required: true,
      });
      fields.push({
        name: "priority_status",
        label: "Priority",
        type: "select",
        options: [
          { value: "Active", label: "Active" },
          { value: "Critical", label: "Critical" },
          { value: "Delay Potential", label: "Delay Potential" },
          { value: "On Hold", label: "On Hold" },
        ],
      });
      fields.push({
        name: "narrative", label: "Narrative (1-2 lines)", type: "textarea",
      });
      fields.push({
        name: "reason", label: "Reason", type: "textarea",
        visibleWhen: {
          field: "priority_status",
          equals: ["Critical", "Delay Potential", "On Hold"],
        },
      });
      fields.push({
        name: "impact", label: "Impact", type: "textarea",
        visibleWhen: {
          field: "priority_status",
          equals: ["Critical", "Delay Potential"],
        },
      });
      fields.push({
        name: "off_hold_condition",
        label: "Off-hold condition", type: "textarea",
        visibleWhen: {
          field: "priority_status", equals: ["On Hold"],
        },
      });
      fields.push({
        name: "due", label: "Due date", type: "date",
        visibleWhen: {
          field: "priority_status",
          equals: ["Active", "Critical", "Delay Potential"],
        },
      });
      fields.push({
        name: "assignee", label: "Assignee (optional)", type: "text",
      });
      break;

    // --- #60 PR4-v2: the two consolidated placement verbs ---------------
    // "Place as New" — one button, a tier select reveals that tier's real
    // form (Category = name+desc; Item = parent Category + name + narrative +
    // required first Action + priority, Decision 12.5; Action = parent Item +
    // text + priority). The "Already complete" checkbox makes it born-closed.
    // name/text prefill from the row (set after build from bucketContext).
    // Resolves to the real wire kind at submit (resolvePlaceNewKind).
    case "place_new":
      fields.push({
        name: "place_tier", label: "Place as a new…", type: "select",
        options: [
          { value: "item", label: "Item (under a Category)" },
          { value: "action", label: "Action (under an Item)" },
          { value: "category", label: "Category" },
        ],
      });
      // The row's text/narrative prefill the name + narrative fields (#1).
      // (Re-applied on tier switch below — visibleWhen clears hidden fields.)
      const placePrefill = spec.bucketContext?.entryText;
      const placeNarrative = spec.bucketContext?.entryNarrative;
      // Category tier
      fields.push({
        name: "name", label: "Category name", type: "text", required: true,
        value: placePrefill,
        visibleWhen: { field: "place_tier", equals: ["category"] },
      });
      fields.push({
        name: "description", label: "Description (optional)", type: "textarea",
        value: placeNarrative,
        visibleWhen: { field: "place_tier", equals: ["category"] },
      });
      // Item tier (#1b deep-placement, 2026-06-19): cascade picker — pick the
      // parent Category, then optionally a Sub-category of it, so the new Item
      // is filed straight into the group in one triage step ("(Ungrouped)" =>
      // directly under the Category). Mirrors the #1a move cascade; the
      // Category <select> drives the Sub-category <select> (wired below). Falls
      // back to a typed Category text input when no destinations are wired.
      // B3 (2026-09-15): prepend the Sub-project pre-filter on multi-axis, gated
      // to the Item tier (Ray's "place item into a sub-project"). Flat → no
      // leading select, full flat Category list — byte-identical.
      const placePf = buildMovePreFilter(spec);
      if (placePf.filterOptions.length) {
        fields.push({
          name: "place_sub_project", label: "Sub-project", type: "select",
          required: true, options: placePf.filterOptions,
          value: placePf.defaultSubProject,
          visibleWhen: { field: "place_tier", equals: ["item"] },
        });
      }
      const placeCats = placePf.categoryOptions;
      if (placeCats.length) {
        fields.push({
          name: "new_item_thread_slug", label: "Under Category", type: "select",
          required: true, options: placeCats,
          visibleWhen: { field: "place_tier", equals: ["item"] },
        });
        fields.push({
          name: "new_item_subcategory_slug", label: "Sub-category",
          type: "select",
          options: placeSubcategoryOptions(
            spec.threadOptions ?? [], placeCats[0]!.value,
          ),
          visibleWhen: { field: "place_tier", equals: ["item"] },
        });
      } else {
        fields.push({
          name: "new_item_thread_slug", label: "Under Category", type: "text",
          required: true, visibleWhen: { field: "place_tier", equals: ["item"] },
        });
      }
      fields.push({
        name: "new_item_name", label: "Item name", type: "text",
        required: true, value: placePrefill,
        visibleWhen: { field: "place_tier", equals: ["item"] },
      });
      fields.push({
        name: "new_item_narrative", label: "Item narrative (2-3 lines, optional)",
        type: "textarea", value: placeNarrative,
        visibleWhen: { field: "place_tier", equals: ["item"] },
      });
      fields.push({
        name: "new_item_action_text", label: "First action (short title)", type: "text",
        // C2 (Ray 2026-10-18): this is the Action's NAME/title, not its narrative.
        // Before the narrative field below existed, Ray typed the narrative here and
        // it became the slug (a full sentence — "airstron-met-with-fire-alarm…"),
        // forcing an action_rename + a separate action_set_narrative. The pair keeps
        // the title short and captures the narrative in one pass.
        // Required for an OPEN Item (Decision 12.5); relaxes when 'complete'
        // (a closed Item can stand alone as a tombstone) — synced below.
        required: true, visibleWhen: { field: "place_tier", equals: ["item"] },
      });
      fields.push({
        name: "new_item_action_narrative",
        label: "First action narrative (1-2 lines, optional)",
        type: "textarea",
        visibleWhen: { field: "place_tier", equals: ["item"] },
      });
      fields.push({
        name: "new_item_action_priority", label: "Action priority",
        type: "select", options: SETTABLE_PRIORITY_OPTIONS,
        visibleWhen: { field: "place_tier", equals: ["item"] },
      });
      // Action tier (parent Item picker wired below from itemOptions)
      // B3 (2026-09-15): a Sub-project <select> scopes the "Under Item" datalist,
      // gated to the Action tier. No "current" here → default "All". Flat → none.
      if (placePf.filterOptions.length) {
        fields.push({
          name: "filter_sub_project", label: "Sub-project", type: "select",
          options: mergeSubProjectFilterOptions(
            spec.itemOptions ?? [], spec.subProjectOptions),
          value: SUBPROJECT_ALL,
          visibleWhen: { field: "place_tier", equals: ["action"] },
        });
      }
      // F2 (2026-09-24): a dependent Category <select> narrows the "Under Item"
      // datalist to one Category. Offered whenever ≥2 open Categories exist,
      // independent of the Sub-project gate (flat projects benefit most — Ray's
      // ask). ≤1 Category → omitted → byte-identical.
      if (categoryFilterHasChoice(spec.threadOptions ?? [])) {
        fields.push({
          name: "filter_category", label: "Category", type: "select",
          options: categoryFilterOptions(
            spec.threadOptions ?? [], SUBPROJECT_ALL,
            new Set((spec.subProjectOptions ?? []).map((s) => s.value))),
          value: CATEGORY_ALL,
          visibleWhen: { field: "place_tier", equals: ["action"] },
        });
      }
      fields.push({
        name: "target_work_item_slug", label: "Under Item", type: "text",
        required: true, visibleWhen: { field: "place_tier", equals: ["action"] },
      });
      fields.push({
        name: "text", label: "Action (short title)", type: "text", required: true,
        value: placePrefill,
        visibleWhen: { field: "place_tier", equals: ["action"] },
      });
      fields.push({
        // Place-as-Action narrative (Ray 2026-10-21, follow-up to C2): the Action
        // tier mirrors the Item tier's name + narrative pair. Prefill from the
        // unfiled row's own narrative (entryNarrative, via placeNarrative) — the
        // row's body describes the thing being placed, which becomes this Action.
        // Was dropped entirely before (the row's narrative had nowhere to land, and
        // a narrative needed a separate action_set_narrative after placing).
        name: "narrative", label: "Action narrative (1-2 lines, optional)",
        type: "textarea", value: placeNarrative,
        visibleWhen: { field: "place_tier", equals: ["action"] },
      });
      fields.push({
        name: "priority_status", label: "Priority", type: "select",
        options: SETTABLE_PRIORITY_OPTIONS,
        visibleWhen: { field: "place_tier", equals: ["action"] },
      });
      // The "file as closed" toggle (Case A).
      fields.push({
        name: "complete", type: "checkbox",
        label: "Already complete (file as closed)",
      });
      break;
    // "Merge Into Existing" — fold this row INTO an existing entity (Case B,
    // dedup; never creates active work). Pickers below offer OPEN and CLOSED
    // targets per tier; labels resolve to slugs at submit
    // (resolveMergeTargetPicks). text/source_ref/entry_id from bucketContext.
    case "merge_into_existing":
      fields.push({
        name: "target_tier", label: "Merge into an existing…", type: "select",
        options: [
          { value: "work_item", label: "Item" },
          { value: "action_item", label: "Action" },
          { value: "thread", label: "Category" },
        ],
      });
      // The Open/Closed toggle filters the picker below. Not a wire field —
      // stripped at submit; drives which targets the datalist offers.
      fields.push({
        name: "merge_scope", label: "Show", type: "select",
        options: [
          { value: "open", label: "Open" },
          { value: "closed", label: "Closed" },
        ],
      });
      // B3 (2026-09-15): a Sub-project <select> scopes the Item, Category AND
      // Action datalists (combined with the Open/Closed toggle). The Action tier
      // also gets an Item <select> (Sub-project → Item, like the action merges).
      // No "current" → default "All". Flat → no selects.
      {
        const mieFilter = mergeSubProjectFilterOptions(
          [...(spec.mergeThreadOptions ?? []), ...(spec.mergeItemOptions ?? [])],
          spec.subProjectOptions);
        if (mieFilter.length) {
          fields.push({
            name: "filter_sub_project", label: "Sub-project", type: "select",
            options: mieFilter, value: SUBPROJECT_ALL,
            visibleWhen: {
              field: "target_tier", equals: ["work_item", "thread", "action_item"],
            },
          });
          // Second-level Item filter, Action tier only. Options rebuild from the
          // picked sub-project in the mount (mergeActionOptions → distinct Items).
          fields.push({
            name: "filter_item", label: "Item", type: "select",
            options: mergeItemOptionsForSubProject(
              spec.mergeActionOptions ?? [], SUBPROJECT_ALL),
            value: MERGE_ITEM_ALL,
            visibleWhen: { field: "target_tier", equals: ["action_item"] },
          });
        }
      }
      // F2 (2026-09-24): a dependent Category <select> narrows the "Existing
      // Item" datalist to one Category (Ray's P0 — his own report was a flat
      // project-wide Item list). Work_item tier only (the Category tier IS a
      // Category; the Action tier already has a finer Sub-project → Item filter).
      // Offered whenever ≥2 open Categories exist, INDEPENDENT of the Sub-project
      // gate so it renders on flat projects (where the pain is worst). ≤1
      // Category → omitted → byte-identical.
      if (categoryFilterHasChoice(spec.threadOptions ?? [])) {
        fields.push({
          name: "filter_category", label: "Category", type: "select",
          options: categoryFilterOptions(
            spec.threadOptions ?? [], SUBPROJECT_ALL,
            new Set((spec.subProjectOptions ?? []).map((s) => s.value))),
          value: CATEGORY_ALL,
          visibleWhen: { field: "target_tier", equals: ["work_item"] },
        });
      }
      fields.push({
        name: "target_work_item_slug", label: "Existing Item", type: "text",
        required: true, visibleWhen: { field: "target_tier", equals: ["work_item"] },
        note: "Open or closed. A see-also note is added under it (shown in Closed Items).",
      });
      fields.push({
        name: "target_action_pick", label: "Existing Action", type: "text",
        required: true, visibleWhen: { field: "target_tier", equals: ["action_item"] },
        note: "Recorded for the operator; no new entry appears in the portal.",
      });
      fields.push({
        name: "target_thread_slug", label: "Existing Category", type: "text",
        required: true, visibleWhen: { field: "target_tier", equals: ["thread"] },
        note: "Rarely the right pick — a Category is a container. Usually 'Place as New Item' is what you want.",
      });
      fields.push({
        name: "note", label: "Note (optional)", type: "textarea",
      });
      break;
    case "item_move_to_category": {
      // #1a UX (2026-06-19): cascade picker — pick a Category, then a second
      // <select> surfaces THAT Category's sub-categories (repopulated below).
      // Replaces the single flattened "Category › Sub-cat" list (functional but
      // undiscoverable when moving fast — operator feedback). to_thread_slug
      // stays the composite the submit reads (bare slug or slug/subslug) →
      // planDeepMoveDestination + the wire are unchanged. Falls back to a typed
      // Category text input when no destinations are wired (fixture).
      // B3 (2026-09-15): on a multi-axis project, prepend a Sub-project <select>
      // that pre-filters the Category list (47 Categories → ~9). Flat projects
      // get no leading select and the full flat list — byte-identical.
      const movePf = buildMovePreFilter(spec);
      if (movePf.filterOptions.length) {
        fields.push({
          name: "move_sub_project", label: "Sub-project", type: "select",
          required: true, options: movePf.filterOptions,
          value: movePf.defaultSubProject,
        });
      }
      const moveCats = movePf.categoryOptions;
      if (moveCats.length) {
        fields.push({
          name: "to_category_pick", label: "Category", type: "select",
          required: true, options: moveCats,
        });
        fields.push({
          name: "to_thread_slug", label: "Sub-category", type: "select",
          required: true,
          options: moveCascadeSubcategoryOptions(
            spec.threadOptions ?? [], moveCats[0]!.value,
          ),
        });
      } else {
        fields.push({
          name: "to_thread_slug",
          label: "Move to Category", type: "text", required: true,
        });
      }
      // B10: optional "Move & Close" for a done-but-misfiled Item. Fires
      // work_item_close alongside the move (submit handler) so it lands in
      // the target Category's Recently Closed. Gated to closeable Items
      // (0 open Actions); disabled + B9 note when the Item has open work.
      fields.push({
        name: "also_close",
        label: "Also close this Item after moving (→ Recently Closed)",
        type: "checkbox",
        disabled: spec.itemCloseable === false,
        note: spec.itemCloseable === false
          ? "Close this Item's open Actions first, then it can be moved & closed."
          : undefined,
      });
      break;
    }

    // --- Task 3: work-item structural affordances ----------------------
    case "candidate_to_item":
      // Place this candidate suggestion as an Item under an existing
      // Category. target_thread_slug renders as a Category datalist
      // (wired below); work_item_slug is derived from name on submit.
      // B3 (2026-09-15): a Sub-project <select> scopes the Category datalist.
      // No "current" for a candidate → default "All sub-projects". Flat → none.
      {
        const c2iFilter = mergeSubProjectFilterOptions(
          spec.threadOptions ?? [], spec.subProjectOptions);
        if (c2iFilter.length) {
          fields.push({
            name: "filter_sub_project", label: "Sub-project", type: "select",
            options: c2iFilter, value: SUBPROJECT_ALL,
          });
        }
      }
      fields.push({
        name: "target_thread_slug",
        label: "Place under Category", type: "text", required: true,
      });
      fields.push({
        name: "name", label: "Item name", type: "text", required: true,
      });
      fields.push({
        name: "narrative",
        label: "Item narrative (2-3 lines, optional)", type: "textarea",
      });
      fields.push({
        name: "initial_action_text",
        label: "First action (optional — otherwise the Item starts with an informational note)",
        type: "textarea",
      });
      fields.push({
        name: "initial_action_priority",
        label: "First action priority",
        type: "select",
        options: [
          { value: "Active", label: "Active" },
          { value: "Critical", label: "Critical" },
          { value: "Delay Potential", label: "Delay Potential" },
          { value: "On Hold", label: "On Hold" },
        ],
      });
      break;
    case "work_item_rename":
      fields.push({
        name: "to_name", label: "New Item name", type: "text", required: true,
      });
      break;
    case "action_rename":
      fields.push({
        name: "to_text", label: "New Action text", type: "text", required: true,
      });
      break;
    case "work_item_merge": {
      // Multi-select of sibling Items to fold INTO this one (the menu is
      // open on the target). Options come from spec.itemOptions, target
      // excluded. The absorber skips any source == target defensively.
      const targetWi = (spec.targetId.split("work_item:")[1] ?? "").trim();
      const sources = (spec.itemOptions ?? []).filter((i) => i.slug !== targetWi);
      // B3-merge (2026-09-15): Sub-project pre-filter over the project-wide Item
      // list. The leading <select> hides non-matching UNCHECKED boxes (checked
      // stay visible; see wireMultiselectSubProjectFilter). Flat → no select,
      // full list, no data-attributes → byte-identical.
      const wiMergeFilter =
        mergeSubProjectFilterOptions(sources, spec.subProjectOptions);
      const wiKnown = new Set((spec.subProjectOptions ?? []).map((s) => s.value));
      const wiDefaultSp = wiMergeFilter.length
        ? defaultMoveSubProject(wiMergeFilter, spec.currentSubProjectSlug)
        : SUBPROJECT_ALL;
      if (wiMergeFilter.length) {
        fields.push({
          name: "merge_sub_project", label: "Show sub-project", type: "select",
          options: wiMergeFilter,
          value: wiDefaultSp,
        });
      }
      // F2 (2026-09-24): a dependent Category <select> narrows the source
      // multiselect to one Category (hides non-matching UNCHECKED boxes, like the
      // Sub-project filter). Offered whenever ≥2 open Categories exist, independent
      // of the Sub-project gate (works on flat). ≤1 Category → omitted →
      // byte-identical (no merge_category, no data-category attribute).
      const wiHasCat = categoryFilterHasChoice(spec.threadOptions ?? []);
      if (wiHasCat) {
        fields.push({
          name: "merge_category", label: "Show category", type: "select",
          options: categoryFilterOptions(spec.threadOptions ?? [], wiDefaultSp, wiKnown),
          value: CATEGORY_ALL,
        });
      }
      const opts = sources.map((i) => ({
        value: i.slug,
        label: [i.display_number, i.name].filter(Boolean).join(" "),
        subProject: wiMergeFilter.length
          ? resolveSubProjectBucket(i.sub_project_slug, wiKnown) : undefined,
        category: wiHasCat ? (i.thread_slug ?? undefined) : undefined,
      }));
      fields.push({
        name: "source_work_item_slugs",
        label: "Items to merge into this one (pick one or more)",
        type: "multiselect",
        required: true,
        options: opts,
      });
      fields.push({
        name: "target_name",
        label: "Rename the merged Item (optional)", type: "text",
      });
      break;
    }
    case "item_merge_into": {
      // #73 reverse merge: single-select of the EXISTING primary to fold THIS
      // Item up into (open Items project-wide, this one excluded). A <select>
      // (small fixed set; value IS the slug, no label→slug mapping). At submit
      // (resolveItemMergeInto) it becomes a work_item_merge whose target is the
      // picked primary and whose single source is this child.
      const mergeIntoSources = (spec.itemOptions ?? [])
        .filter((i) => i.slug !== (spec.targetId.split("work_item:")[1] ?? "").trim());
      // B3-merge: Sub-project pre-filter. When the agent pre-selected a primary
      // (matter proposal), default to "All sub-projects" so that pre-selection is
      // visible; otherwise default to this Item's sub-project. The Category-style
      // cascade rebuilds the primary <select> on sub-project change.
      const preselected = spec.current?.merge_primary_item;
      const miMergeFilter =
        mergeSubProjectFilterOptions(mergeIntoSources, spec.subProjectOptions);
      const miDefaultSp = defaultMoveSubProject(
        miMergeFilter,
        preselected ? SUBPROJECT_ALL : spec.currentSubProjectSlug,
      );
      const miKnown = new Set((spec.subProjectOptions ?? []).map((s) => s.value));
      if (miMergeFilter.length) {
        fields.push({
          name: "merge_sub_project", label: "Show sub-project", type: "select",
          options: miMergeFilter, value: miDefaultSp,
        });
      }
      // F2 (2026-09-24): a dependent Category <select> narrows the primary-Item
      // <select> to one Category. Defaults to "All categories" so a preselected
      // matter primary stays visible. Offered whenever ≥2 open Categories exist,
      // independent of the Sub-project gate (works on flat). ≤1 → byte-identical.
      if (categoryFilterHasChoice(spec.threadOptions ?? [])) {
        fields.push({
          name: "merge_category", label: "Show category", type: "select",
          options: categoryFilterOptions(
            spec.threadOptions ?? [],
            miMergeFilter.length ? miDefaultSp : SUBPROJECT_ALL, miKnown),
          value: CATEGORY_ALL,
        });
      }
      const opts = miMergeFilter.length
        ? mergeIntoItemOptions(spec, miDefaultSp)
        : mergeIntoItemOptions(spec, SUBPROJECT_ALL);
      fields.push({
        name: "merge_primary_item",
        label: "Merge this Item into (pick the primary)",
        type: "select",
        required: true,
        options: opts,
        // N1 (2026-07-14): when the agent surfaced a "Possibly part of X" matter
        // hint on this Item, pre-select X (spec.current.merge_primary_item) so
        // the recipient just CONFIRMS the proposed merge — accept-the-proposal,
        // mirroring the closure_candidate affordance. A slug not in `opts` (the
        // target isn't open/rendered this week) leaves the first option — a safe
        // no-op — and the recipient picks manually as before.
        value: spec.current?.merge_primary_item ?? undefined,
      });
      break;
    }
    case "action_merge": {
      // B8: multi-select of open Actions to fold INTO this one — now
      // project-wide (any Item), not just siblings. Each option's value is
      // its full target id (so a cross-Item pick carries its origin Item);
      // the submit handler re-parents cross-Item picks before merging. The
      // target itself is excluded; a cross-Item option is labelled with its
      // parent Item so the recipient sees where it comes from.
      const targetWi = spec.targetId.match(/work_item:([^/]+)/)?.[1] ?? "";
      const amSources = (spec.actionOptions ?? [])
        .filter((a) => (a.target_id ?? a.ai_slug) !== spec.targetId)
        .filter((a) => a.ai_slug !== ""
          && !(a.work_item_slug === targetWi && a.ai_slug === (
            spec.targetId.split("action_item:")[1] ?? "").trim()));
      // B3-merge (2026-09-15): two-level Sub-project → Item pre-filter over the
      // project-wide Action list (the longest list). Options carry data-sub-project
      // + data-item; the two leading <select>s hide non-matching UNCHECKED boxes.
      // Flat → no selects, no data-attributes → byte-identical.
      const amMergeFilter =
        mergeSubProjectFilterOptions(amSources, spec.subProjectOptions);
      const amKnown = new Set((spec.subProjectOptions ?? []).map((s) => s.value));
      const amDefaultSp =
        defaultMoveSubProject(amMergeFilter, spec.currentSubProjectSlug);
      if (amMergeFilter.length) {
        fields.push({
          name: "merge_sub_project", label: "Show sub-project", type: "select",
          options: amMergeFilter, value: amDefaultSp,
        });
        fields.push({
          name: "merge_item", label: "Show item", type: "select",
          options: mergeItemOptionsForSubProject(amSources, amDefaultSp, amKnown),
          value: MERGE_ITEM_ALL,
        });
      }
      const opts = amSources.map((a) => {
        const base = [a.display_number, a.text].filter(Boolean).join(" ");
        const crossItem = !!a.work_item_slug && a.work_item_slug !== targetWi;
        return {
          value: a.target_id ?? a.ai_slug,
          label: crossItem && a.item_label ? `${base} · from ${a.item_label}` : base,
          subProject: amMergeFilter.length
            ? resolveSubProjectBucket(a.sub_project_slug, amKnown) : undefined,
          item: amMergeFilter.length ? (a.work_item_slug ?? undefined) : undefined,
        };
      });
      fields.push({
        name: "source_action_item_slugs",
        label: "Actions to merge into this one (pick one or more)",
        type: "multiselect",
        required: true,
        options: opts,
      });
      fields.push({
        name: "target_text",
        label: "Rename the merged Action (optional)", type: "text",
      });
      break;
    }
    case "action_merge_into": {
      // B1 / #73 reverse merge: single-select of the EXISTING primary Action to
      // fold THIS Action up into (open Actions project-wide, this one excluded).
      // A <select> (value IS the full action_item target id, so the picked
      // primary's Item is known at submit for the POST target + cross-Item
      // re-parent). At submit (resolveActionMergeInto) it becomes an action_merge
      // whose target is the picked primary and whose single source is this child.
      // B3-merge: two-level Sub-project → Item pre-filter, rebuilding the primary
      // <select> on either change. Default to this Action's sub-project + all its
      // items. Flat → no pre-filter selects, full list (byte-identical).
      const aiSources = (spec.actionOptions ?? [])
        .filter((a) => (a.target_id ?? a.ai_slug) !== spec.targetId && a.ai_slug !== "");
      const aiMergeFilter =
        mergeSubProjectFilterOptions(aiSources, spec.subProjectOptions);
      const aiKnown = new Set((spec.subProjectOptions ?? []).map((s) => s.value));
      const aiDefaultSp =
        defaultMoveSubProject(aiMergeFilter, spec.currentSubProjectSlug);
      if (aiMergeFilter.length) {
        fields.push({
          name: "merge_sub_project", label: "Show sub-project", type: "select",
          options: aiMergeFilter, value: aiDefaultSp,
        });
        fields.push({
          name: "merge_item", label: "Show item", type: "select",
          options: mergeItemOptionsForSubProject(aiSources, aiDefaultSp, aiKnown),
          value: MERGE_ITEM_ALL,
        });
      }
      const opts = aiMergeFilter.length
        ? mergeIntoActionOptions(spec, aiDefaultSp, MERGE_ITEM_ALL)
        : mergeIntoActionOptions(spec, SUBPROJECT_ALL, MERGE_ITEM_ALL);
      fields.push({
        name: "merge_primary_action",
        label: "Merge this Action into (pick the primary)",
        type: "select",
        required: true,
        options: opts,
      });
      break;
    }
    case "action_move_to_item": {
      // B8: re-parent this Action to a different Item. The destination is
      // picked from a project-wide Item datalist (spec.itemOptions); the
      // value submitted is the Item slug.
      // B3 (2026-09-15): a Sub-project <select> scopes the Item datalist's
      // suggestions (default this Action's sub-project). Flat → no select.
      const amtiFilter = mergeSubProjectFilterOptions(
        spec.itemOptions ?? [], spec.subProjectOptions);
      const amtiKnown = new Set((spec.subProjectOptions ?? []).map((s) => s.value));
      const amtiDefaultSp = amtiFilter.length
        ? defaultMoveSubProject(amtiFilter, spec.currentSubProjectSlug)
        : SUBPROJECT_ALL;
      if (amtiFilter.length) {
        fields.push({
          name: "filter_sub_project", label: "Sub-project", type: "select",
          options: amtiFilter,
          value: amtiDefaultSp,
        });
      }
      // F2 (2026-09-24): a dependent Category <select> narrows the Item datalist
      // to one Category. Options seed from the default sub-project (repopulated
      // on a Sub-project change). ≤1 Category → omitted → byte-identical.
      if (categoryFilterHasChoice(spec.threadOptions ?? [])) {
        fields.push({
          name: "filter_category", label: "Category", type: "select",
          options: categoryFilterOptions(
            spec.threadOptions ?? [], amtiDefaultSp, amtiKnown),
          value: CATEGORY_ALL,
        });
      }
      fields.push({
        name: "to_work_item_slug",
        label: "Move to Item", type: "text", required: true,
      });
      break;
    }
    case "action_to_item": {
      // #76: promote this Action to a NEW Item. Name prefills with the Action
      // text (PREFILL_KINDS / spec.current.name) — edit or keep it; reshape
      // derives new_work_item_slug from it. Destination is a <select> of open
      // Categories (value IS the slug — no label→slug mapping).
      fields.push({
        name: "name",
        label: "Name for the new Item", type: "text", required: true,
      });
      // B3 (2026-09-15): on a multi-axis project, prepend a Sub-project <select>
      // that pre-filters the destination Category list (same cascade as the move
      // picker — the to_thread_slug select IS a Category select here). Flat → no
      // leading select, full list (byte-identical).
      const promotePf = buildMovePreFilter(spec);
      if (promotePf.filterOptions.length) {
        fields.push({
          name: "promote_sub_project", label: "Sub-project", type: "select",
          required: true, options: promotePf.filterOptions,
          value: promotePf.defaultSubProject,
        });
      }
      const promoteCats = promotePf.categoryOptions;
      fields.push({
        name: "to_thread_slug",
        label: "Under Category", type: "select", required: true,
        options: promoteCats,
      });
      // `a` (Ray 2026-07-22): sub-category cascade — pick the destination Category,
      // then optionally a Sub-category of it, so the promoted Item files straight
      // into the group ("(Ungrouped)" => directly under the Category). Mirrors the
      // place cascade (#1b); the to_thread_slug <select> drives this one (wired
      // below). Bare-slug values; the absorber reads new_item_subcategory_slug.
      if (promoteCats.length) {
        fields.push({
          name: "new_item_subcategory_slug", label: "Sub-category", type: "select",
          options: placeSubcategoryOptions(spec.threadOptions ?? [], promoteCats[0]!.value),
        });
      }
      break;
    }

    // --- #38 sub-category grouping -------------------------------------
    case "subcategory_add":
      // Mints an unnumbered grouping under this Category. The slug is
      // collision-minted at submit (mintSubcategorySlug); only the display
      // name is typed here.
      fields.push({
        name: "name", label: "Sub-category name", type: "text", required: true,
      });
      break;
    case "subcategory_rename":
      // Pre-filled with the current name (spec.current.to_name, PREFILL_KINDS).
      fields.push({
        name: "to_name", label: "New sub-category name", type: "text",
        required: true,
      });
      break;
    case "subcategory_close":
      fields.push({
        name: "reason",
        label: "Reason (optional — e.g. 'work complete')", type: "textarea",
      });
      break;
    case "subcategory_delete":
      // #74b: no close-gate — delete ungroups a non-empty group on purpose.
      // The helper text explains members fall back to ungrouped (nothing
      // closed). Optional reason for the audit log, like close.
      fields.push({
        name: "reason",
        label: "Reason (optional — e.g. 'grouped these by mistake')",
        type: "textarea",
      });
      break;
    case "subcategory_reopen":
      fields.push({
        name: "reason", label: "Reason (optional)", type: "textarea",
      });
      break;
    case "subcategory_move": {
      // E-7a: relocate the whole group (+ its Items, Actions, tags) to another
      // Category. The <select> lists every OTHER open Category — the source is
      // excluded (moving to self is a no-op). Only offered when a target exists
      // (kindsForSubcategoryV4 gate), so the list is non-empty. Submit fans out
      // via collectSubcategoryMove; the value IS the slug (no label→slug map).
      const srcThread = spec.targetId.match(/^thread:([^/]+)/)?.[1] ?? "";
      const opts = (spec.threadOptions ?? [])
        .filter((t) => t.slug !== srcThread)
        .map((t) => ({
          value: t.slug,
          label: t.display_number ? `${t.display_number}. ${t.name}` : t.name,
        }));
      fields.push({
        name: "to_thread_slug", label: "Move to Category", type: "select",
        required: true, options: opts,
      });
      break;
    }
    case "item_move_to_subcategory": {
      // Closed-set picker: this Category's OPEN groups + an explicit
      // "(ungrouped)". A <select> (not a datalist) since the option set is
      // small and fixed — the value IS the slug, no label→slug mapping. Not
      // `required`: every option (incl. "(ungrouped)" = "") is a valid pick.
      // Pre-selects the Item's current group via f.value (spec.current).
      const openGroups = (spec.subcategoryOptions ?? []).filter((s) => !s.closed);
      fields.push({
        name: "to_subcategory_slug",
        label: "Move to sub-category",
        type: "select",
        options: [
          ...openGroups.map((s) => ({ value: s.slug, label: s.name })),
          { value: "", label: "(ungrouped)" },
        ],
        value: spec.current?.to_subcategory_slug ?? "",
      });
      break;
    }
    case "category_move_to_phase": {
      // #37: re-band this Category. Closed 4-value enum <select> (no label→
      // value mapping — the value IS the wire `to_phase` enum). Pre-selects
      // the Category's current phase; a null/legacy phase defaults to General.
      fields.push({
        name: "to_phase",
        label: "Move to phase",
        type: "select",
        required: true,
        options: [
          { value: "pre_development", label: "Pre-Development" },
          { value: "pre_construction", label: "Pre-Construction" },
          { value: "construction", label: "Construction" },
          { value: "general", label: "General" },
        ],
        value: spec.current?.phase ?? "general",
      });
      break;
    }
    case "category_move_to_subproject": {
      // P4d-3: file this Category under a different sub-project. The options are
      // the project's registry (spec.subProjectOptions, {slug,name}); the value
      // IS the wire `to_sub_project_slug`. Pre-selects the Category's current
      // sub-project. Offered only on a sub-projected project, so the option list
      // is always non-empty here.
      fields.push({
        name: "to_sub_project_slug",
        label: "Move to sub-project",
        type: "select",
        required: true,
        options: spec.subProjectOptions ?? [],
        value: spec.current?.sub_project_slug ?? "",
      });
      break;
    }
    case "item_move": {
      // Feedback 2.1: ONE Move form when both destinations exist. The tier
      // select reveals the matching picker (visibleWhen, the Decision-3C
      // reveal); resolveItemMoveKind maps to the real wire kind at submit —
      // the place_new pseudo-kind pattern. Category tier reuses the
      // item_move_to_category fields verbatim (datalist + B10 Move & Close);
      // Sub-category tier reuses the item_move_to_subcategory <select>.
      const openGroups = (spec.subcategoryOptions ?? []).filter((s) => !s.closed);
      fields.push({
        name: "move_tier",
        label: "Move to",
        type: "select",
        required: true,
        options: [
          { value: "category", label: "Another Category" },
          { value: "subcategory", label: "A sub-category of this Category" },
        ],
      });
      // #1a UX (2026-06-19): the Category tier's destination is the same
      // cascade as item_move_to_category — pick a Category, then its
      // sub-categories (repopulated below). Gated to the "category" move_tier.
      // B3 (2026-09-15): prepend the Sub-project pre-filter on multi-axis (also
      // gated to the "category" move_tier). Flat → no leading select, full list.
      const movePf = buildMovePreFilter(spec);
      if (movePf.filterOptions.length) {
        fields.push({
          name: "move_sub_project", label: "Sub-project", type: "select",
          required: true, options: movePf.filterOptions,
          value: movePf.defaultSubProject,
          visibleWhen: { field: "move_tier", equals: ["category"] },
        });
      }
      const pseudoMoveCats = movePf.categoryOptions;
      if (pseudoMoveCats.length) {
        fields.push({
          name: "to_category_pick", label: "Category", type: "select",
          required: true, options: pseudoMoveCats,
          visibleWhen: { field: "move_tier", equals: ["category"] },
        });
        fields.push({
          name: "to_thread_slug", label: "Sub-category", type: "select",
          required: true,
          options: moveCascadeSubcategoryOptions(
            spec.threadOptions ?? [], pseudoMoveCats[0]!.value,
          ),
          visibleWhen: { field: "move_tier", equals: ["category"] },
        });
      } else {
        fields.push({
          name: "to_thread_slug",
          label: "Destination Category", type: "text", required: true,
          visibleWhen: { field: "move_tier", equals: ["category"] },
        });
      }
      fields.push({
        name: "also_close",
        label: "Also close this Item after moving (→ Recently Closed)",
        type: "checkbox",
        disabled: spec.itemCloseable === false,
        note: spec.itemCloseable === false
          ? "Close this Item's open Actions first, then it can be moved & closed."
          : undefined,
        visibleWhen: { field: "move_tier", equals: ["category"] },
      });
      fields.push({
        name: "to_subcategory_slug",
        label: "Destination sub-category",
        type: "select",
        options: [
          ...openGroups.map((s) => ({ value: s.slug, label: s.name })),
          { value: "", label: "(ungrouped)" },
        ],
        value: spec.current?.to_subcategory_slug ?? "",
        visibleWhen: { field: "move_tier", equals: ["subcategory"] },
      });
      break;
    }

    // --- v4 Billing ----------------------------------------------------
    // Decision 8: status enums become <select>; clients no longer
    // type the literal. Decision 7A: invoice_number on the two
    // status-mutation forms becomes a datalist of invoices on this
    // Item, populated below from spec.invoiceOptions.
    case "invoice_add":
      fields.push({
        name: "number", label: "Invoice number", type: "text", required: true,
      });
      fields.push({
        name: "amount", label: "Amount (numeric)", type: "text",
      });
      fields.push({
        name: "status",
        label: "Status",
        type: "select",
        options: [
          { value: "Received", label: "Received" },
          { value: "Under Review", label: "Under Review" },
          { value: "Approved", label: "Approved" },
          { value: "Paid", label: "Paid" },
          { value: "Disputed", label: "Disputed" },
        ],
      });
      fields.push({
        name: "received_date", label: "Received date", type: "date",
      });
      fields.push({
        name: "due_date", label: "Due date", type: "date",
      });
      fields.push({
        name: "blocking_note", label: "Blocking note (optional)", type: "textarea",
      });
      break;
    case "invoice_status_change":
      fields.push({
        name: "invoice_number", label: "Invoice", type: "text", required: true,
      });
      fields.push({
        name: "new_status",
        label: "New status",
        type: "select",
        required: true,
        options: [
          { value: "Received", label: "Received" },
          { value: "Under Review", label: "Under Review" },
          { value: "Approved", label: "Approved" },
          { value: "Paid", label: "Paid" },
          { value: "Disputed", label: "Disputed" },
        ],
      });
      break;
    case "invoice_blocking_note_set":
      fields.push({
        name: "invoice_number", label: "Invoice", type: "text", required: true,
      });
      fields.push({
        name: "blocking_note",
        label: "Blocking note (leave blank to clear)",
        type: "textarea",
      });
      break;
  }

  // Pre-fill edit forms with the target's current value (spec.current) so the
  // recipient edits what's already there instead of starting from blank. Gated
  // to the explicit edit kinds — "add new" forms (action_add,
  // action_open_question_add) share field names like "text" but must stay empty.
  const PREFILL_KINDS = new Set([
    "action_set_reason", "action_set_impact",
    "action_set_off_hold_condition", "action_set_due",
    "action_priority_change",
    // Rename forms: pre-fill the current Category / Item name to edit
    // (thread_rename's field is "new_name", work_item_rename's is "to_name").
    "thread_rename", "work_item_rename",
    // B6: action_rename pre-fills the current Action text ("to_text").
    "action_rename",
    // #76: action_to_item pre-fills the new Item's name ("name") with the
    // Action text (spec.current.name) — edit or keep it.
    "action_to_item",
    // A³ (2026-06-03): thread_promote pre-fills the candidate's suggested
    // name ("name") so the recipient edits it inline while promoting.
    "thread_promote",
    // #38: subcategory_rename pre-fills the current group name ("to_name").
    "subcategory_rename",
    // R2-3: pre-fill the current narrative ("narrative") so it's an EDIT.
    "work_item_set_narrative", "action_set_narrative",
    // KIL-POINT (Ray 2026-10-28): pre-fill the current 558 Point ("point") so it's
    // an EDIT, not a blank (spec.current.point, threaded by briefings.ts).
    "item_set_point",
    // R2-3 fast-follow: pre-fill the current exec summary ("summary").
    "set_executive_summary",
    // Round-4 #2: the flattened details form pre-fills every shown field
    // (reason / impact / off_hold_condition / due) — change-detection in
    // collectActionDetailEdits compares against these same current values.
    "action_edit_details",
  ]);
  if (spec.current && PREFILL_KINDS.has(kind)) {
    for (const f of fields) {
      if (f.value === undefined
          && (f.type === "text" || f.type === "textarea" || f.type === "date")) {
        const cur = spec.current[f.name];
        if (cur != null && cur !== "") f.value = cur;
      }
    }
  }

  // Track field rows keyed by name so visibleWhen logic can show/hide
  // them after the form is built (Decision 3C). Each row is a single
  // <div.form-row>; toggling display:none is sufficient.
  const fieldRows = new Map<string, HTMLDivElement>();
  for (const f of fields) {
    // R2: a hidden field carries a constant payload value (e.g. the
    // sub_project_slug that scopes a set_executive_summary to a band) through
    // FormData without a visible row.
    if (f.type === "hidden") {
      const hidden = document.createElement("input");
      hidden.type = "hidden";
      hidden.name = f.name;
      if (f.value !== undefined) hidden.value = f.value;
      form.appendChild(hidden);
      continue;
    }
    const row = document.createElement("div");
    row.className = "form-row";
    const label = document.createElement("label");
    label.textContent = f.label;
    let input: HTMLElement;
    if (f.type === "textarea") {
      input = document.createElement("textarea");
      (input as HTMLTextAreaElement).name = f.name;
      (input as HTMLTextAreaElement).rows = 3;
      if (f.required) (input as HTMLTextAreaElement).required = true;
      if (f.value !== undefined) (input as HTMLTextAreaElement).value = f.value;
    } else if (f.type === "select") {
      const sel = document.createElement("select");
      sel.name = f.name;
      if (f.required) sel.required = true;
      for (const o of f.options ?? []) {
        const opt = document.createElement("option");
        opt.value = o.value;
        opt.textContent = o.label;
        sel.appendChild(opt);
      }
      // #38: pre-select an initial value when given (e.g. the Item's current
      // sub-category on the move form). A value matching no option leaves the
      // browser on the first option — no-op for the existing selects that
      // never set f.value.
      if (f.value !== undefined) sel.value = f.value;
      input = sel;
    } else if (f.type === "multiselect") {
      // Checkbox list, NOT a native <select multiple>: long option labels
      // (e.g. "3.2.5 Advise security and assist Orlim with locked-area access")
      // overflow / clip unreliably inside the 320px popover across browsers.
      // Checkboxes let the labels wrap and stay contained. Each shares the
      // field name, so fd.getAll(name) collects the checked values exactly as
      // the old <select multiple> did — the submit path is unchanged.
      const list = document.createElement("div");
      list.className = "multiselect-list";
      for (const o of f.options ?? []) {
        const opt = document.createElement("label");
        opt.className = "multiselect-option";
        // B3-merge: filter keys for the Sub-project (and Action-tier Item)
        // pre-filter — read by wireMultiselectSubProjectFilter to show/hide this
        // option without rebuilding the list (so a checked box survives a scope
        // change). Absent on options without the metadata → never hidden.
        if (o.subProject !== undefined && o.subProject !== null) {
          opt.dataset.subProject = o.subProject;
        }
        if (o.item !== undefined && o.item !== null) {
          opt.dataset.item = o.item;
        }
        if (o.category !== undefined && o.category !== null) {
          opt.dataset.category = o.category;
        }
        const cb = document.createElement("input");
        cb.type = "checkbox";
        cb.name = f.name;
        cb.value = o.value;
        // Pre-check the current set (edit forms — e.g. an Item already tagged
        // for the Summary). Load-bearing for REPLACE-WHOLE kinds: without it,
        // submitting the form would clear tags the recipient never touched.
        if ((f.selectedValues ?? []).includes(o.value)) cb.checked = true;
        const span = document.createElement("span");
        span.textContent = o.label;
        opt.appendChild(cb);
        opt.appendChild(span);
        list.appendChild(opt);
      }
      input = list;
    } else if (f.type === "checkbox") {
      // Checkbox renders inline with its label text to the right —
      // FormData only includes the entry when checked, so the reshape
      // step coerces presence → true, absence → false.
      input = document.createElement("input");
      (input as HTMLInputElement).name = f.name;
      (input as HTMLInputElement).type = "checkbox";
      if (f.disabled) (input as HTMLInputElement).disabled = true;
      const labelText = document.createElement("span");
      labelText.textContent = f.label;
      label.textContent = "";
      label.classList.add("form-checkbox-label");
      label.appendChild(input);
      label.appendChild(labelText);
      row.appendChild(label);
      if (f.note) {
        const note = document.createElement("p");
        note.className = "form-helper";
        note.textContent = f.note;
        row.appendChild(note);
      }
      form.appendChild(row);
      fieldRows.set(f.name, row);
      continue;
    } else {
      input = document.createElement("input");
      (input as HTMLInputElement).name = f.name;
      (input as HTMLInputElement).type = f.type === "date" ? "date" : "text";
      if (f.required) (input as HTMLInputElement).required = true;
      if (f.value !== undefined) (input as HTMLInputElement).value = f.value;
    }
    label.appendChild(input);
    row.appendChild(label);
    // A2 (Ray 2026-09-15): render f.note for these field types too (the checkbox
    // branch renders its own note + continues before here). The item-tier "Set
    // associations" multiselect uses this to warn that set-all overwrites
    // per-action refinements.
    if (f.note) {
      const note = document.createElement("p");
      note.className = "form-helper";
      note.textContent = f.note;
      row.appendChild(note);
    }
    form.appendChild(row);
    fieldRows.set(f.name, row);
  }

  // Decision 3C: conditional reveal. A field marked `visibleWhen`
  // listens to changes on its driver field and shows/hides its row.
  // Hidden rows clear their input value on hide so submission doesn't
  // carry stale data from a different priority.
  const conditionals = fields.filter((f) => f.visibleWhen);
  for (const f of conditionals) {
    const row = fieldRows.get(f.name);
    const driver = form.querySelector<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >(`[name="${f.visibleWhen!.field}"]`);
    if (!row || !driver) continue;
    const apply = (): void => {
      const equals = f.visibleWhen!.equals;
      const matches = equals.includes(driver.value);
      row.style.display = matches ? "" : "none";
      const el = row.querySelector<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >(`[name="${f.name}"]`);
      if (el) {
        // Hidden `required` inputs silently block submit via HTML5
        // validation (the browser tries to focus an invisible field
        // and the user sees nothing). Sync `required` with visibility
        // so the only constraints enforced are the ones the user can
        // actually see.
        // E-5: `requiredWhen` (when set) makes requiredness depend on the
        // driver value, not a static boolean — required iff the current value
        // is listed. Overrides `required`. An empty list = never required.
        // (Pure decision extracted to fieldRequiredForValue for unit testing —
        // this package's test env is node, so apply() itself isn't reachable.)
        const effectiveRequired = fieldRequiredForValue(f, driver.value);
        if (matches && effectiveRequired) {
          el.setAttribute("required", "");
        } else {
          el.removeAttribute("required");
        }
        if (!matches && el.tagName !== "SELECT") el.value = "";
      }
    };
    driver.addEventListener("change", apply);
    driver.addEventListener("input", apply);
    apply();
  }

  // #1a/#1b: cascade the Category → Sub-category pickers — when the Category
  // <select> changes, repopulate the dependent Sub-category <select>. Used by
  // the move forms (to_category_pick → to_thread_slug, composite values) and the
  // place form (new_item_thread_slug → new_item_subcategory_slug, bare slugs).
  // Each is a no-op when its driver/dependent pair isn't present in this form.
  wireSubcategoryCascade(
    form, "to_category_pick", "to_thread_slug",
    (cat) => moveCascadeSubcategoryOptions(spec.threadOptions ?? [], cat),
  );
  wireSubcategoryCascade(
    form, "new_item_thread_slug", "new_item_subcategory_slug",
    (cat) => placeSubcategoryOptions(spec.threadOptions ?? [], cat),
  );
  // `a` (Ray 2026-07-22): the action→item promote form reuses new_item_subcategory_slug
  // but its Category driver is to_thread_slug (no-op on any form lacking that pair).
  wireSubcategoryCascade(
    form, "to_thread_slug", "new_item_subcategory_slug",
    (cat) => placeSubcategoryOptions(spec.threadOptions ?? [], cat),
  );
  // B3 (2026-09-15): the Sub-project → Category pre-filter. Registered AFTER the
  // Category → Sub-category wires above so the chained change re-fires them. Each
  // is a no-op when its leading Sub-project <select> isn't present (flat project
  // / a form without the pre-filter). move_sub_project drives both the standalone
  // item_move_to_category and the combined item_move (shared to_category_pick).
  wireSubProjectCascade(
    form, "move_sub_project", "to_category_pick",
    (sp) => moveCategoryOptionsForSubProject(spec, sp),
  );
  wireSubProjectCascade(
    form, "place_sub_project", "new_item_thread_slug",
    (sp) => moveCategoryOptionsForSubProject(spec, sp),
  );
  // B3-merge: the merge pickers' Sub-project (+ Item, for Actions) pre-filter.
  // Each is a no-op unless its fields are present in this form:
  //  - item_merge_into: Sub-project → Category → the primary-Item <select> (F2);
  //  - work_item_merge / action_merge: hide non-matching UNCHECKED boxes;
  //  - action_merge_into: Sub-project → Item → the primary-Action <select>.
  // F2 (2026-09-24): item_merge_into now has a Sub-project → Category → Item
  // cascade (wireItemMergeIntoCascade), replacing the single-level
  // wireSubProjectCascade so the added merge_category level rebuilds the primary.
  wireItemMergeIntoCascade(form, spec);
  wireMergeMultiselectFilter(form, spec);
  wireActionMergeIntoCascade(form, spec);
  // B3: action_to_item ("Promote to a new Item") — Sub-project → the Category
  // <select> (chains to the existing to_thread_slug → Sub-category cascade).
  wireSubProjectCascade(
    form, "promote_sub_project", "to_thread_slug",
    (sp) => moveCategoryOptionsForSubProject(spec, sp),
  );

  // #60 PR4-v2: on Place-as-New → Item, the first Action is required for an
  // OPEN Item (Decision 12.5) but optional when "Already complete" is ticked
  // (a closed Item can stand alone as a tombstone). Sync `required` off the
  // checkbox, layered on top of the visibleWhen required-sync above.
  if (kind === "place_new") {
    const complete = form.querySelector<HTMLInputElement>('input[name="complete"]');
    const tier = form.querySelector<HTMLSelectElement>('select[name="place_tier"]');
    const firstAction = form.querySelector<HTMLInputElement>(
      'input[name="new_item_action_text"]',
    );
    if (complete && tier && firstAction) {
      const syncFirstAction = (): void => {
        const itemTier = tier.value === "item";
        firstAction.toggleAttribute("required", itemTier && !complete.checked);
      };
      complete.addEventListener("change", syncFirstAction);
      tier.addEventListener("change", syncFirstAction);
      syncFirstAction();
    }
    // #1: re-apply the row's prefill to the active tier's fields on switch —
    // visibleWhen clears hidden fields, so each tier's name/narrative must be
    // re-seeded when it becomes visible (registered AFTER visibleWhen's apply,
    // so it runs second on the same change event). Fill-if-empty: don't
    // clobber a value the user already typed.
    const prefillText = spec.bucketContext?.entryText ?? "";
    const prefillNarr = spec.bucketContext?.entryNarrative ?? "";
    const prefillMap: Record<string, string> = {
      name: prefillText, new_item_name: prefillText, text: prefillText,
      new_item_narrative: prefillNarr, description: prefillNarr,
      // Place-as-Action narrative (Ray 2026-10-21): the Action tier's `narrative`
      // field must be re-seeded when its tier becomes visible too — visibleWhen
      // clears it on load (default tier is "item"), so the build-time value alone
      // is wiped. Mirrors `new_item_narrative`/`text` above.
      narrative: prefillNarr,
    };
    if (tier && (prefillText || prefillNarr)) {
      const reapplyPrefill = (): void => {
        for (const [fieldName, val] of Object.entries(prefillMap)) {
          if (!val) continue;
          const el = form.querySelector<HTMLInputElement | HTMLTextAreaElement>(
            `[name="${fieldName}"]`,
          );
          // Only the active-tier (visible) field, and only if empty.
          if (el && el.offsetParent !== null && !el.value) el.value = val;
        }
      };
      tier.addEventListener("change", reapplyPrefill);
    }
  }

  // Slice 8 Item #5: responsible_party_assign autocomplete via
  // <datalist>. Sourced from the briefing's participant-options
  // collector (see briefings.ts). Selecting an option that has a
  // known email auto-fills the email field. Free-text "Add new
  // person…" path is implicit — typing a name not in the list still
  // submits cleanly.
  if (kind === "responsible_party_assign" && spec.participantOptions?.length) {
    const listId = `participants-${spec.targetId.replace(/[^a-z0-9-]/gi, "_")}`;
    const datalist = document.createElement("datalist");
    datalist.id = listId;
    const emailByName = new Map<string, string>();
    for (const p of spec.participantOptions) {
      const opt = document.createElement("option");
      opt.value = p.name;
      datalist.appendChild(opt);
      if (p.email) emailByName.set(p.name, p.email);
    }
    form.appendChild(datalist);
    const nameInput = form.querySelector<HTMLInputElement>('input[name="name"]');
    const emailInput = form.querySelector<HTMLInputElement>('input[name="email"]');
    if (nameInput) {
      nameInput.setAttribute("list", listId);
      // B3: the browser stacks its own saved-value suggestions under the
      // datalist; suppress them so a stale value typed elsewhere (e.g. the
      // ?fixture demo) never appears as a bogus target. The <datalist> is unaffected.
      nameInput.setAttribute("autocomplete", "off");
      nameInput.setAttribute(
        "placeholder",
        "Pick from list or type a new name",
      );
      // Auto-fill email when the user picks a participant whose email
      // we know. Only writes when the email field is currently empty
      // so we don't clobber a manually-typed value.
      nameInput.addEventListener("input", () => {
        if (!emailInput || emailInput.value.trim()) return;
        const matched = emailByName.get(nameInput.value.trim());
        if (matched) emailInput.value = matched;
      });
    }
  }

  // KIL-POINT (Ray 2026-10-28): autocomplete the "Set KIL Point" field from the
  // project's distinct existing Points, so a recipient reuses the exact string
  // ("Scale / KAMM") instead of a typo variant ("Scle") that fragments the Key
  // Issue Log. Mirrors responsible_party_assign above (native <datalist>);
  // non-restrictive — a genuinely new firm still submits as typed (open vocab).
  if (kind === "item_set_point" && spec.pointOptions?.length) {
    const listId = `points-${spec.targetId.replace(/[^a-z0-9-]/gi, "_")}`;
    const datalist = document.createElement("datalist");
    datalist.id = listId;
    for (const p of spec.pointOptions) {
      const opt = document.createElement("option");
      opt.value = p;
      datalist.appendChild(opt);
    }
    form.appendChild(datalist);
    const pointInput = form.querySelector<HTMLInputElement>('input[name="point"]');
    if (pointInput) {
      pointInput.setAttribute("list", listId);
      // Suppress the browser's own saved-value suggestions stacking under the
      // <datalist> (a stale value typed elsewhere would otherwise appear as a
      // bogus option); the <datalist> itself is unaffected. Mirrors B3.
      pointInput.setAttribute("autocomplete", "off");
      pointInput.setAttribute("placeholder", "Pick an existing Point or type a new one");
    }
  }

  // Thread datalist for thread_merge. thread_retag was dropped from
  // the SPA in Decision 2 close-out (operator-only via CLI); the
  // datalist wiring shrinks to thread_merge alone.
  const threadKinds = new Set(["thread_merge"]);
  if (threadKinds.has(kind) && spec.threadOptions?.length) {
    const listId = `threads-${spec.targetId.replace(/[^a-z0-9-]/gi, "_")}-${kind}`;
    const datalist = document.createElement("datalist");
    datalist.id = listId;
    form.appendChild(datalist);
    // F2 (2026-09-24): scope the Category suggestions by the optional Sub-project
    // filter (was an unfiltered project-wide dump). #63: value is the human label
    // "N name" (mapped back to the stable slug at submit via
    // resolvePickedTargetSlugs — NOT slugify, so a rename survives). Flat → the
    // select is absent and wireDatalistFilter shows the full list (byte-identical).
    const tmKnown = new Set((spec.subProjectOptions ?? []).map((s) => s.value));
    wireDatalistFilter(form, datalist, (sp) =>
      (spec.threadOptions ?? [])
        .filter((t) => matchesSubProject(t.sub_project_slug, sp, tmKnown))
        .map((t) => {
          const lbl = threadOptionLabel(t);
          return { value: lbl, label: lbl };
        }));
    const input = form.querySelector<HTMLInputElement>(
      'input[name="merge_into"]',
    );
    if (input) {
      input.setAttribute("list", listId);
      // B3: the browser stacks its own saved-value suggestions under the
      // datalist; suppress them so a stale value typed elsewhere (e.g. the
      // ?fixture demo) never appears as a bogus target. The <datalist> is unaffected.
      input.setAttribute("autocomplete", "off");
      input.setAttribute("placeholder", "Pick a Category");
    }
  }

  // Decision 7A (close-out 2026-05-25): Category datalist for candidate_to_item.
  // #63: value=NAME (not slug) — mapped back to slug at submit.
  // #1a/#1b (2026-06-19): item_move_to_category, item_move, and place_new's
  // Item tier are now Category <select>s (the cascade), so they no longer use
  // this typed datalist — only candidate_to_item still does.
  const categoryPickerKinds = new Set(["candidate_to_item"]);
  if (categoryPickerKinds.has(kind) && spec.threadOptions?.length) {
    const listId =
      `cats-${spec.targetId.replace(/[^a-z0-9-]/gi, "_")}-${kind}`;
    const datalist = document.createElement("datalist");
    datalist.id = listId;
    form.appendChild(datalist);
    // B3: scope the Category suggestions by the optional Sub-project filter.
    const c2iKnown = new Set((spec.subProjectOptions ?? []).map((s) => s.value));
    wireDatalistFilter(form, datalist, (sp) =>
      (spec.threadOptions ?? [])
        .filter((t) => matchesSubProject(t.sub_project_slug, sp, c2iKnown))
        .map((t) => {
          const lbl = threadOptionLabel(t);
          return { value: lbl, label: lbl };
        }));
    const inputName = "target_thread_slug";
    const input = form.querySelector<HTMLInputElement>(
      `input[name="${inputName}"]`,
    );
    if (input) {
      input.setAttribute("list", listId);
      // B3: the browser stacks its own saved-value suggestions under the
      // datalist; suppress them so a stale value typed elsewhere (e.g. the
      // ?fixture demo) never appears as a bogus target. The <datalist> is unaffected.
      input.setAttribute("autocomplete", "off");
      input.setAttribute("placeholder", "Pick a Category");
    }
  }

  // Item datalist for the Place-as-Action tier (A³/A², 2026-06-03 — was
  // place_from_unfiled's place-under-existing path). Value=slug,
  // label="N.M Item name".
  // B8: action_move_to_item uses the same project-wide Item datalist on a
  // `to_work_item_slug` field; place_as_action uses `target_work_item_slug`.
  // Item datalist (OPEN items). place_as_action / action_move_to_item, and
  // PR4-v2 place_new's Action tier (target_work_item_slug = parent Item).
  if (
    (kind === "place_as_action" || kind === "action_move_to_item"
      || kind === "place_new")
    && spec.itemOptions?.length
  ) {
    const listId = `items-${spec.targetId.replace(/[^a-z0-9-]/gi, "_")}-${kind}`;
    const datalist = document.createElement("datalist");
    datalist.id = listId;
    form.appendChild(datalist);
    // For a move, exclude the Action's current parent Item from the picker.
    const currentWi = ((spec.targetId.split("work_item:")[1] ?? "")
      .split("/")[0] ?? "").trim();
    // B3: scope the Item suggestions by the optional Sub-project filter. #63:
    // value=friendly label ("N.M name (Category)"), not the slug —
    // resolvePickedTargetSlugs maps it back via the same itemOptionLabel.
    // F2 (2026-09-24): add the dependent Category axis — the datalist now scopes
    // by Sub-project AND the picked Category, and a Sub-project change repopulates
    // the Category <select>. Either <select> may be absent (flat / ≤1-Category) →
    // wireSubProjectCategoryDatalist degrades to the wider scope (byte-identical).
    const itemDlKnown = new Set((spec.subProjectOptions ?? []).map((s) => s.value));
    wireSubProjectCategoryDatalist(
      form, datalist,
      (sp) => categoryFilterOptions(spec.threadOptions ?? [], sp, itemDlKnown),
      (sp, cat) =>
        scopeItemOptions(spec.itemOptions ?? [], sp, cat, itemDlKnown)
          .filter((i) => !(kind === "action_move_to_item" && i.slug === currentWi))
          .map((i) => {
            const lbl = itemOptionLabel(i);
            return { value: lbl, label: lbl };
          }));
    const fieldName = kind === "action_move_to_item"
      ? "to_work_item_slug" : "target_work_item_slug";
    const input = form.querySelector<HTMLInputElement>(
      `input[name="${fieldName}"]`,
    );
    if (input) {
      input.setAttribute("list", listId);
      // B3: the browser stacks its own saved-value suggestions under the
      // datalist; suppress them so a stale value typed elsewhere (e.g. the
      // ?fixture demo) never appears as a bogus target. The <datalist> is unaffected.
      input.setAttribute("autocomplete", "off");
      input.setAttribute("placeholder", "Pick an Item");
    }
  }

  // #60 PR4-v2 Merge Into Existing: one datalist per tier, REPOPULATED by the
  // Open/Closed `merge_scope` toggle. Targets are OPEN+CLOSED (merge*Options),
  // each carrying `closed`; the toggle filters to one. Separate source from
  // the open-only Place pickers. #63 labels; resolveMergeTargetPicks maps back.
  if (kind === "merge_into_existing") {
    const idSafe = spec.targetId.replace(/[^a-z0-9-]/gi, "_");
    const scopeSel = form.querySelector<HTMLSelectElement>(
      'select[name="merge_scope"]',
    );
    // B3: the optional Sub-project filter, combined with the Open/Closed scope.
    const mieFilterSel = form.querySelector<HTMLSelectElement>(
      'select[name="filter_sub_project"]');
    const mieKnown = new Set((spec.subProjectOptions ?? []).map((s) => s.value));
    const mieItemSel = form.querySelector<HTMLSelectElement>(
      'select[name="filter_item"]');
    // F2 (2026-09-24): the optional dependent Category filter (work_item tier).
    const mieCatSel = form.querySelector<HTMLSelectElement>(
      'select[name="filter_category"]');
    // (fieldName, listId, all options w/ closed flag + sub-project bucket + item).
    // subProject undefined → not sub-project-scoped; item undefined → not
    // item-scoped (only the Action picker carries an item, for the 2nd level).
    // F2: category undefined → not Category-scoped (only the Item picker carries
    // one — the Category tier IS a Category; the Action tier uses filter_item).
    const pickers: Array<{
      field: string; listId: string; placeholder: string;
      opts: Array<{
        value: string; closed?: boolean; subProject?: string; item?: string;
        category?: string;
      }>;
    }> = [
      {
        field: "target_work_item_slug", listId: `merge-items-${idSafe}`,
        opts: (spec.mergeItemOptions ?? []).map((i) => ({
          value: itemOptionLabel(i), closed: i.closed,
          subProject: resolveSubProjectBucket(i.sub_project_slug, mieKnown),
          category: i.thread_slug ?? undefined,
        })),
        placeholder: "Pick an existing Item",
      },
      {
        field: "target_thread_slug", listId: `merge-cats-${idSafe}`,
        opts: (spec.mergeThreadOptions ?? []).map((t) => ({
          value: threadOptionLabel(t), closed: t.closed,
          subProject: resolveSubProjectBucket(t.sub_project_slug, mieKnown),
        })),
        placeholder: "Pick an existing Category",
      },
      {
        field: "target_action_pick", listId: `merge-actions-${idSafe}`,
        opts: (spec.mergeActionOptions ?? []).map((a) => ({
          value: a.label, closed: a.closed,
          subProject: resolveSubProjectBucket(a.sub_project_slug, mieKnown),
          item: a.work_item_slug,
        })),
        placeholder: "Pick an existing Action",
      },
    ];
    for (const p of pickers) {
      const datalist = document.createElement("datalist");
      datalist.id = p.listId;
      form.appendChild(datalist);
      const input = form.querySelector<HTMLInputElement>(`input[name="${p.field}"]`);
      if (input) {
        input.setAttribute("list", p.listId);
        // B3: the browser stacks its own saved-value suggestions under the
        // datalist; suppress them so a stale value typed elsewhere (e.g. the
        // ?fixture demo) never appears as a bogus target. The <datalist> is unaffected.
        input.setAttribute("autocomplete", "off");
        input.setAttribute("placeholder", p.placeholder);
      }
    }
    const repopulate = (): void => {
      const wantClosed = scopeSel?.value === "closed";
      const spVal = mieFilterSel ? mieFilterSel.value : SUBPROJECT_ALL;
      const itemVal = mieItemSel ? mieItemSel.value : MERGE_ITEM_ALL;
      const catVal = mieCatSel ? mieCatSel.value : CATEGORY_ALL;
      for (const p of pickers) {
        const datalist = form.querySelector<HTMLDataListElement>(`#${CSS.escape(p.listId)}`);
        const input = form.querySelector<HTMLInputElement>(`input[name="${p.field}"]`);
        if (!datalist) continue;
        datalist.replaceChildren();
        for (const o of p.opts) {
          if (!!o.closed !== wantClosed) continue;
          // B3: sub-project scope (skip only when this picker IS sub-project-
          // scoped). The Action picker adds a second-level Item scope.
          if (o.subProject !== undefined && spVal !== SUBPROJECT_ALL
              && o.subProject !== spVal) continue;
          if (o.item !== undefined && itemVal !== MERGE_ITEM_ALL
              && o.item !== itemVal) continue;
          // F2: Category scope (only the Item picker carries a category bucket).
          if (o.category !== undefined && catVal !== CATEGORY_ALL
              && o.category !== catVal) continue;
          const opt = document.createElement("option");
          opt.value = o.value;
          opt.label = o.value;
          opt.textContent = o.value;
          datalist.appendChild(opt);
        }
        // Scope change invalidates a prior pick from the other scope.
        if (input && input.value) input.value = "";
      }
    };
    scopeSel?.addEventListener("change", repopulate);
    // B3/F2: a sub-project change rebuilds the Action-tier Item filter AND the
    // work_item-tier Category filter (both scoped to that sub-project) before
    // re-filtering the datalists.
    mieFilterSel?.addEventListener("change", () => {
      if (mieItemSel) {
        setSelectOptions(
          mieItemSel,
          mergeItemOptionsForSubProject(
            spec.mergeActionOptions ?? [], mieFilterSel.value, mieKnown),
          MERGE_ITEM_ALL,
        );
      }
      if (mieCatSel) {
        setSelectOptions(
          mieCatSel,
          categoryFilterOptions(spec.threadOptions ?? [], mieFilterSel.value, mieKnown),
          CATEGORY_ALL,
        );
      }
      repopulate();
    });
    mieItemSel?.addEventListener("change", repopulate);
    mieCatSel?.addEventListener("change", repopulate);
    repopulate();
  }

  // Decision 7A: Invoice datalist for invoice_status_change and
  // invoice_blocking_note_set. Value=invoice number string;
  // label combines number + amount + status for disambiguation.
  const invoicePickerKinds = new Set([
    "invoice_status_change", "invoice_blocking_note_set",
  ]);
  if (invoicePickerKinds.has(kind) && spec.invoiceOptions?.length) {
    const listId =
      `inv-${spec.targetId.replace(/[^a-z0-9-]/gi, "_")}-${kind}`;
    const datalist = document.createElement("datalist");
    datalist.id = listId;
    for (const inv of spec.invoiceOptions) {
      const opt = document.createElement("option");
      opt.value = inv.number;
      opt.label = inv.label;
      opt.textContent = inv.label;
      datalist.appendChild(opt);
    }
    form.appendChild(datalist);
    const input = form.querySelector<HTMLInputElement>(
      'input[name="invoice_number"]',
    );
    if (input) {
      input.setAttribute("list", listId);
      // B3: the browser stacks its own saved-value suggestions under the
      // datalist; suppress them so a stale value typed elsewhere (e.g. the
      // ?fixture demo) never appears as a bogus target. The <datalist> is unaffected.
      input.setAttribute("autocomplete", "off");
      input.setAttribute("placeholder", "Pick an invoice");
    }
  }

  const actions = document.createElement("div");
  actions.className = "form-actions";

  const submit = document.createElement("button");
  submit.type = "submit";
  submit.className = "btn-submit";
  // Cascade-close: the button carries the composite's scope ("Close Item +
  // 2 Actions") so the click is unambiguous about what it fires.
  const cascadeCount = kind === "work_item_close_cascade"
    ? (spec.cascadeOpenActions ?? []).length
    : 0;
  // W3: the Category composite's scope — N Items + the total open Actions under them.
  const cascadeItemCount = kind === "thread_close_cascade"
    ? (spec.cascadeOpenItems ?? []).length
    : 0;
  const cascadeItemActionCount = kind === "thread_close_cascade"
    ? (spec.cascadeOpenItems ?? []).reduce((n, i) => n + i.open_action_targets.length, 0)
    : 0;
  // W6: mark-done closes N Actions but keeps the Item open.
  const markDoneCount = kind === "work_item_mark_completed"
    ? (spec.cascadeOpenActions ?? []).length
    : 0;
  submit.textContent = cascadeCount
    ? `Close Item + ${cascadeCount} Action${cascadeCount === 1 ? "" : "s"}`
    : markDoneCount
      ? `Close ${markDoneCount} Action${markDoneCount === 1 ? "" : "s"} (keep Item open)`
      : cascadeItemCount
        ? `Close Category + ${cascadeItemCount} Item${cascadeItemCount === 1 ? "" : "s"}`
          + (cascadeItemActionCount
            ? ` + ${cascadeItemActionCount} Action${cascadeItemActionCount === 1 ? "" : "s"}`
            : "")
        : "Submit";
  actions.appendChild(submit);
  // Cascade-close: children closed by THIS form instance — survives a retry
  // after a partial failure so succeeded closes are never re-POSTed.
  const cascadeClosedChildren = new Set<string>();
  // W3: Items closed by THIS form instance (same retry-safety, one tier up).
  const cascadeClosedItems = new Set<string>();

  const back = document.createElement("button");
  back.type = "button";
  back.className = "btn-link";
  back.textContent = "← Back";
  back.addEventListener("click", onBack);
  actions.appendChild(back);

  form.appendChild(actions);

  const status = document.createElement("p");
  status.className = "form-status";
  form.appendChild(status);

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    submit.disabled = true;
    status.textContent = "Submitting…";
    const raw: Record<string, unknown> = {};
    const fd = new FormData(form);
    for (const [k, v] of fd.entries()) raw[k] = v;
    // <select multiple> emits one FormData entry per selection; the loop
    // above keeps only the last. Re-collect multiselects as arrays.
    form.querySelectorAll<HTMLSelectElement>("select[multiple]").forEach((sel) => {
      raw[sel.name] = fd.getAll(sel.name);
    });
    // Checkbox-list multiselects (.multiselect-list — the v0.4.19 replacement
    // for <select multiple>) ALSO emit one FormData entry per checked box
    // sharing a name; the entries-loop above keeps only the last, so a single
    // selection arrived as a bare string and the absorber's list contract
    // dropped the whole merge event silently. Re-collect as arrays too (deduped
    // by name) — this is the getAll the render site assumed but the submit
    // path never did.
    const seenMultiselect = new Set<string>();
    form
      .querySelectorAll<HTMLInputElement>(".multiselect-list input[type=checkbox]")
      .forEach((cb) => {
        if (seenMultiselect.has(cb.name)) return;
        seenMultiselect.add(cb.name);
        raw[cb.name] = fd.getAll(cb.name);
      });
    // Round-4 #2: action_edit_details decomposes into one wire event per
    // CHANGED field. All but the LAST are POSTed here, each bubbling its own
    // feedback-submitted (the action_merge multi-event pattern); the last
    // rides the generic path below as `wireKind`, so the success / chip /
    // status flow is unchanged. Nothing changed → friendly status, no POST.
    let detailEditLast:
      | { kind: FeedbackKind; payload: Record<string, unknown> }
      | null = null;
    if (kind === "action_edit_details") {
      const edits = collectActionDetailEdits(raw, spec.current);
      if (!edits.length) {
        status.textContent = "No changes to submit.";
        submit.disabled = false;
        return;
      }
      for (const e of edits.slice(0, -1)) {
        const res: { event_id: string } = inFixtureMode()
          ? { event_id: crypto.randomUUID() }
          : await postJson<{ event_id: string }>(
              `/feedback/${encodeURIComponent(spec.runId)}`,
              {
                kind: e.kind,
                target: { type: spec.targetType, id: spec.targetId },
                payload: e.payload,
              },
            );
        document.dispatchEvent(new CustomEvent("feedback-submitted", {
          detail: {
            event_id: res.event_id, kind: e.kind, payload: e.payload,
            submitted_at: new Date().toISOString(),
            targetId: spec.targetId, targetType: spec.targetType,
          },
        }));
      }
      const last = edits[edits.length - 1]!;
      detailEditLast = last;
      // The generic path reshapes `raw` for wireKind — swap in the last
      // edit's clean single-field payload so stray sibling fields (an
      // unchanged prefilled reason, the empty due) never reach the wire.
      for (const k of Object.keys(raw)) delete raw[k];
      Object.assign(raw, last.payload);
    }
    // Slice 2 (2026-09-09): action_report_inclusion decomposes the consolidated
    // control into one EXISTING wire event per CHANGED report (PSU → kil
    // mark/unmark, KIL → action_set_report_tags), the action_edit_details
    // multi-event pattern. All but the LAST POST here (each bubbling its own
    // feedback-submitted, and — for a PSU toggle — a kil-mark-changed so the
    // briefing-row badge flips optimistically); the last rides the generic path
    // below as `wireKind`, whose kil-mark-changed dispatch is keyed on wireKind.
    if (kind === "action_report_inclusion") {
      const edits = collectReportInclusionEdits(raw, spec.current);
      if (!edits.length) {
        status.textContent = "No changes to submit.";
        submit.disabled = false;
        return;
      }
      for (const e of edits.slice(0, -1)) {
        const res: { event_id: string } = inFixtureMode()
          ? { event_id: crypto.randomUUID() }
          : await postJson<{ event_id: string }>(
              `/feedback/${encodeURIComponent(spec.runId)}`,
              {
                kind: e.kind,
                target: { type: spec.targetType, id: spec.targetId },
                payload: e.payload,
              },
            );
        document.dispatchEvent(new CustomEvent("feedback-submitted", {
          detail: {
            event_id: res.event_id, kind: e.kind, payload: e.payload,
            submitted_at: new Date().toISOString(),
            targetId: spec.targetId, targetType: spec.targetType,
          },
        }));
        // B (2026-09-16): the action control is 558-only now (PSU moved to the
        // Item tier), so only action_set_report_tags flows here — no PSU dispatch.
      }
      const last = edits[edits.length - 1]!;
      detailEditLast = last;
      for (const k of Object.keys(raw)) delete raw[k];
      Object.assign(raw, last.payload);
    }
    // Slice 2 (2026-09-09): item_report_inclusion is a self-contained CASCADE over
    // the Item's open Actions — a fan-out of per-Action wire events with DISTINCT
    // targets, so it cannot ride the generic single-target path. It POSTs each
    // event (bubbling feedback-submitted + kil-mark-changed for a PSU toggle) and
    // completes the popover itself.
    if (kind === "item_report_inclusion") {
      const rawSel = raw.report_inclusion;
      const selected = new Set(
        (Array.isArray(rawSel) ? rawSel : rawSel != null ? [rawSel] : [])
          .map((v) => String(v)).filter(Boolean),
      );
      // B (2026-09-16): the item control now hits TWO tiers — the 558 cascade
      // stays per-Action, but the PSU checkbox posts ONE item_kil_mark/unmark to
      // the ITEM itself. Pass the Item's own target + current mark so the
      // decompose can diff PSU at the item tier.
      const casc = collectItemReportCascade(
        selected, spec.reportCascadeActions ?? [],
        { target_id: spec.targetId, kil_marked: spec.current?.kil_marked === "1" },
      );
      if (!casc.length) {
        status.textContent = "No changes to submit.";
        submit.disabled = false;
        return;
      }
      try {
        await Promise.all(casc.map(async (e) => {
          const res: { event_id: string } = inFixtureMode()
            ? { event_id: crypto.randomUUID() }
            : await postJson<{ event_id: string }>(
                `/feedback/${encodeURIComponent(spec.runId)}`,
                {
                  kind: e.kind,
                  target: { type: e.targetType, id: e.target_id },
                  payload: e.payload,
                },
              );
          document.dispatchEvent(new CustomEvent("feedback-submitted", {
            detail: {
              event_id: res.event_id, kind: e.kind, payload: e.payload,
              submitted_at: new Date().toISOString(),
              targetId: e.target_id, targetType: e.targetType,
            },
          }));
          // action_set_report_tags (the 558 gutter chip) echoes via
          // feedback-state-changed → applyFeedbackEventToPayload, which has a
          // report_tags branch.
          document.dispatchEvent(new CustomEvent("feedback-state-changed", {
            detail: { kind: e.kind, targetId: e.target_id, payload: e.payload },
          }));
          // R8 (Ray 2026-09-22): the item-header PSU badge is flipped ONLY by
          // applyKilEventToPayload, reached via kil-mark-changed — NOT by
          // applyFeedbackEventToPayload (which has no item_kil branch despite the
          // kind being listed in OPTIMISTIC_STATE_KINDS). This handler previously
          // dispatched only feedback-state-changed, so an item_kil_unmark reported
          // success but the PSU badge stayed on ("says I updated it, PSU tag still
          // showing"). Mirror the generic single-target path (forms.ts ~6579):
          // dispatch kil-mark-changed for the PSU (work_item) event so the badge
          // clears/sets in-session.
          if (e.kind === "item_kil_mark" || e.kind === "item_kil_unmark") {
            document.dispatchEvent(new CustomEvent("kil-mark-changed", {
              detail: { kind: e.kind, targetId: e.target_id },
            }));
          }
        }));
      } catch (err) {
        console.error("item report-inclusion cascade failed", err);
        status.textContent = "Some actions did not update — submit again to retry.";
        submit.disabled = false;
        return;
      }
      const nActions = casc.filter((e) => e.targetType === "action_item").length;
      const psuChanged = casc.some((e) => e.targetType === "work_item");
      status.textContent = nActions > 0
        ? `Applied to ${nActions} action${nActions === 1 ? "" : "s"}${psuChanged ? " + this Item" : ""}.`
        : "Applied to this Item.";
      submit.textContent = "Done";
      form.querySelectorAll("input, textarea").forEach((el) => {
        (el as HTMLInputElement).disabled = true;
      });
      // Re-render the run so the popover closes + the Action badges reflect the
      // cascade (the per-event kil-mark-changed above flipped PSU via
      // applyKilEventToPayload; the 558 chips via feedback-state-changed).
      document.dispatchEvent(new CustomEvent("rerender-briefing"));
      return;
    }
    // A2 (Ray 2026-09-15): item_set_associations at the ITEM tier is a CASCADE
    // over the Item's open Actions (mirror of item_report_inclusion above) — the
    // "set the association for all this Item's Actions" affordance. It fans out
    // one REPLACE-WHOLE action_set_associations per changed open Action, each
    // echoing via the optimistic layer (feedback-state-changed →
    // applyFeedbackEventToPayload sets association_tags), and the Item chip rolls
    // them up. The ACTION-tier action_set_associations (per-Action ✎) still rides
    // the generic single-target path below — only the Item tier cascades.
    if (kind === "item_set_associations") {
      const rawSel = raw.association_tags;
      const desired = (Array.isArray(rawSel) ? rawSel : rawSel != null ? [rawSel] : [])
        .map((v) => String(v)).filter(Boolean);
      const casc = collectItemAssociationCascade(
        desired, spec.associationCascadeActions ?? [],
      );
      if (!casc.length) {
        status.textContent = "No changes to submit.";
        submit.disabled = false;
        return;
      }
      try {
        await Promise.all(casc.map(async (e) => {
          const res: { event_id: string } = inFixtureMode()
            ? { event_id: crypto.randomUUID() }
            : await postJson<{ event_id: string }>(
                `/feedback/${encodeURIComponent(spec.runId)}`,
                {
                  kind: e.kind,
                  target: { type: "action_item", id: e.target_id },
                  payload: e.payload,
                },
              );
          document.dispatchEvent(new CustomEvent("feedback-submitted", {
            detail: {
              event_id: res.event_id, kind: e.kind, payload: e.payload,
              submitted_at: new Date().toISOString(),
              targetId: e.target_id, targetType: "action_item",
            },
          }));
          // Echo each Action's `.atag` chip in-session (association_set_associations
          // is in OPTIMISTIC_STATE_KINDS); the Item chip recomputes via the rollup.
          document.dispatchEvent(new CustomEvent("feedback-state-changed", {
            detail: { kind: e.kind, targetId: e.target_id, payload: e.payload },
          }));
        }));
      } catch (err) {
        console.error("item set-associations cascade failed", err);
        status.textContent = "Some actions did not update — submit again to retry.";
        submit.disabled = false;
        return;
      }
      const nActions = new Set(casc.map((e) => e.target_id)).size;
      status.textContent = `Applied to ${nActions} action${nActions === 1 ? "" : "s"}.`;
      submit.textContent = "Done";
      form.querySelectorAll("input, textarea").forEach((el) => {
        (el as HTMLInputElement).disabled = true;
      });
      document.dispatchEvent(new CustomEvent("rerender-briefing"));
      return;
    }
    // E-5 (Ray 2026-09-16): the Item-tier "Set priority" cascade — decompose the
    // single chosen priority into one action_priority_change per open Action whose
    // priority differs (collectItemPriorityCascade), POST each, and echo (the kind
    // is in OPTIMISTIC_STATE_KINDS). Mirror of the item_set_associations block: a
    // pure SPA pseudo-kind, item_set_priority never reaches the wire.
    if (kind === "item_set_priority") {
      const newPriority = String(raw.new_priority ?? "");
      const casc = collectItemPriorityCascade(
        newPriority, spec.priorityCascadeActions ?? [],
      );
      if (!casc.length) {
        status.textContent = "No changes to submit.";
        submit.disabled = false;
        return;
      }
      try {
        await Promise.all(casc.map(async (e) => {
          const res: { event_id: string } = inFixtureMode()
            ? { event_id: crypto.randomUUID() }
            : await postJson<{ event_id: string }>(
                `/feedback/${encodeURIComponent(spec.runId)}`,
                {
                  kind: e.kind,
                  target: { type: "action_item", id: e.target_id },
                  payload: e.payload,
                },
              );
          document.dispatchEvent(new CustomEvent("feedback-submitted", {
            detail: {
              event_id: res.event_id, kind: e.kind, payload: e.payload,
              submitted_at: new Date().toISOString(),
              targetId: e.target_id, targetType: "action_item",
            },
          }));
          // Echo each Action's priority pill in-session (action_priority_change
          // is in OPTIMISTIC_STATE_KINDS).
          document.dispatchEvent(new CustomEvent("feedback-state-changed", {
            detail: { kind: e.kind, targetId: e.target_id, payload: e.payload },
          }));
        }));
      } catch (err) {
        console.error("item set-priority cascade failed", err);
        status.textContent = "Some actions did not update — submit again to retry.";
        submit.disabled = false;
        return;
      }
      const nActions = new Set(casc.map((e) => e.target_id)).size;
      status.textContent = `Applied to ${nActions} action${nActions === 1 ? "" : "s"}.`;
      submit.textContent = "Done";
      form.querySelectorAll("input, textarea").forEach((el) => {
        (el as HTMLInputElement).disabled = true;
      });
      document.dispatchEvent(new CustomEvent("rerender-briefing"));
      return;
    }
    // E-7a (Ray 2026-09-16): move a whole sub-category to another Category. A
    // pure SPA pseudo-kind — decomposes into EXISTING wire kinds
    // (collectSubcategoryMove) POSTed IN ORDER (sequential await, NOT
    // Promise.all — subcategory_delete must land after the member moves). Each
    // event echoes via its own optimistic applier (all four kinds are in
    // OPTIMISTIC_STATE_KINDS); monotonic submitted_at keeps the replay order
    // (add → per-member move→regroup → delete). Composite → no Undo
    // (showMoveToast(..., null)), matching the reorg family's composite policy.
    if (kind === "subcategory_move") {
      const srcThread = spec.targetId.match(/^thread:([^/]+)/)?.[1] ?? "";
      const scSlug = spec.targetId.match(/\/subcategory:([^/]+)/)?.[1] ?? "";
      const scName = spec.current?.to_name ?? scSlug;
      const toThread = ensureSlug(String(raw.to_thread_slug ?? ""));
      if (!srcThread || !scSlug || !toThread || toThread === srcThread) {
        status.textContent = "Pick a different Category to move to.";
        submit.disabled = false;
        return;
      }
      const events = collectSubcategoryMove(
        srcThread, scSlug, scName, toThread,
        spec.subcategoryMoveMembers ?? [],
      );
      try {
        let i = 0;
        for (const e of events) {
          const res: { event_id: string } = inFixtureMode()
            ? { event_id: crypto.randomUUID() }
            : await postJson<{ event_id: string }>(
                `/feedback/${encodeURIComponent(spec.runId)}`,
                {
                  kind: e.kind,
                  target: { type: e.targetType, id: e.target_id },
                  payload: e.payload,
                },
              );
          // Monotonic submitted_at so rebuildOptimisticLayer's submitted_at sort
          // preserves the emit order even for events within the same millisecond.
          const submittedAt = new Date(Date.now() + i).toISOString();
          i += 1;
          document.dispatchEvent(new CustomEvent("feedback-submitted", {
            detail: {
              event_id: res.event_id, kind: e.kind, payload: e.payload,
              submitted_at: submittedAt,
              targetId: e.target_id, targetType: e.targetType,
            },
          }));
          document.dispatchEvent(new CustomEvent("feedback-state-changed", {
            detail: { kind: e.kind, targetId: e.target_id, payload: e.payload },
          }));
        }
      } catch (err) {
        console.error("subcategory move failed", err);
        status.textContent = "The move didn't fully apply — submit again to retry.";
        submit.disabled = false;
        return;
      }
      const nMembers = spec.subcategoryMoveMembers?.length ?? 0;
      const toOpt = (spec.threadOptions ?? []).find((t) => t.slug === toThread);
      const toName = toOpt
        ? (toOpt.display_number ? `${toOpt.display_number}. ${toOpt.name}` : toOpt.name)
        : toThread;
      status.textContent = `Moved to ${toName}${nMembers ? ` with ${nMembers} Item${nMembers === 1 ? "" : "s"}` : ""}.`;
      submit.textContent = "Done";
      form.querySelectorAll("input, textarea, select").forEach((el) => {
        (el as HTMLInputElement).disabled = true;
      });
      showMoveToast(`Moved sub-category to ${toName}`, null);
      document.dispatchEvent(new CustomEvent("rerender-briefing"));
      return;
    }
    // Cascade-close: sequentially close the open children FIRST (one EXISTING
    // action_close per child, stamped "Closed with Item <N>"), then fall
    // through to the generic path as a plain work_item_close whose #74a
    // client_events carry the just-POSTed child closes past the Worker's
    // open-children gate (deterministic — the gate merges client_events, no
    // KV list-lag dependence; the proven B10 contingent-close pattern).
    // Mid-loop dispatches feedback-submitted ONLY: a feedback-state-changed
    // re-render here would tear down this popover with the loop in flight.
    // All state-changed echoes ride the post-success block below. Own
    // try/catch (the B8/action_edit_details precedents leave pre-loop
    // failures as unhandled rejections stuck on "Submitting…"): a mid-loop
    // failure reports partial progress and re-enables Submit — the retry
    // skips the already-closed children (each is individually retractable).
    // W6 shares this per-Action close loop: mark-completed closes the same open
    // Actions, only the stamped reason + the FOLLOWING step differ (it dismisses
    // the closure candidate to keep the Item open, rather than closing the Item).
    if (kind === "work_item_close_cascade" || kind === "work_item_mark_completed") {
      const childReason = kind === "work_item_mark_completed"
        ? "Item work completed"
        : cascadeChildReason(spec.movingFromDisplayNumber);
      const pending = pendingCascadeChildren(
        spec.cascadeOpenActions, cascadeClosedChildren,
      );
      try {
        for (const child of pending) {
          const childPayload: Record<string, unknown> = { reason: childReason };
          const res: { event_id: string } = inFixtureMode()
            ? { event_id: crypto.randomUUID() }
            : await postJson<{ event_id: string }>(
                `/feedback/${encodeURIComponent(spec.runId)}`,
                {
                  kind: "action_close",
                  target: { type: "action_item", id: child.target_id },
                  payload: childPayload,
                },
              );
          cascadeClosedChildren.add(child.target_id);
          document.dispatchEvent(new CustomEvent("feedback-submitted", {
            detail: {
              event_id: res.event_id, kind: "action_close",
              payload: childPayload, submitted_at: new Date().toISOString(),
              targetId: child.target_id, targetType: "action_item",
            },
          }));
        }
      } catch {
        const done = cascadeClosedChildren.size;
        const total = (spec.cascadeOpenActions ?? []).length;
        status.textContent =
          `Closed ${done} of ${total} Actions — the Item is still open. `
          + "Submit again to retry the rest.";
        submit.disabled = false;
        return;
      }
    }
    // W3 CATEGORY-CASCADE-CLOSE: close each open Item (its open Actions FIRST, then
    // the Item), then fall through to the generic path as a plain thread_close. Per
    // Item: one EXISTING action_close per open Action (stamped "Closed with Item <N>",
    // unconditional — no gate), then one work_item_close (stamped "Closed with Category
    // <N>") whose #74a client_events carry the just-POSTed Action closes past the
    // Worker's open-Actions gate. The final thread_close (generic path below, a
    // GATE_HINT_KIND) then carries the just-POSTed Item closes past the open-Items gate
    // (effectiveOpenItemsUnderThread). Same mid-loop discipline as the Item cascade:
    // feedback-submitted ONLY (a state-changed re-render would tear down the popover
    // mid-loop); own try/catch; retry-safe via cascadeClosedItems / cascadeClosedChildren.
    if (kind === "thread_close_cascade") {
      const categoryItemReason = cascadeCategoryChildReason(spec.movingFromDisplayNumber);
      const items = spec.cascadeOpenItems ?? [];
      try {
        for (const item of items) {
          if (cascadeClosedItems.has(item.target_id)) continue;
          const actionReason = cascadeChildReason(item.display_number);
          for (const act of pendingCascadeChildren(item.open_action_targets, cascadeClosedChildren)) {
            const actPayload: Record<string, unknown> = { reason: actionReason };
            const aRes: { event_id: string } = inFixtureMode()
              ? { event_id: crypto.randomUUID() }
              : await postJson<{ event_id: string }>(
                  `/feedback/${encodeURIComponent(spec.runId)}`,
                  {
                    kind: "action_close",
                    target: { type: "action_item", id: act.target_id },
                    payload: actPayload,
                  },
                );
            cascadeClosedChildren.add(act.target_id);
            document.dispatchEvent(new CustomEvent("feedback-submitted", {
              detail: {
                event_id: aRes.event_id, kind: "action_close",
                payload: actPayload, submitted_at: new Date().toISOString(),
                targetId: act.target_id, targetType: "action_item",
              },
            }));
          }
          // Close the Item, handing the Worker the recipient's own in-session events
          // (now incl. this Item's Action closes) so the open-Actions gate passes.
          const itemPayload: Record<string, unknown> = { reason: categoryItemReason };
          const iRes: { event_id: string } = inFixtureMode()
            ? { event_id: crypto.randomUUID() }
            : await postJson<{ event_id: string }>(
                `/feedback/${encodeURIComponent(spec.runId)}`,
                {
                  kind: "work_item_close",
                  target: { type: "work_item", id: item.target_id },
                  payload: itemPayload,
                  client_events: gateHintEvents(spec.runId),
                },
              );
          cascadeClosedItems.add(item.target_id);
          document.dispatchEvent(new CustomEvent("feedback-submitted", {
            detail: {
              event_id: iRes.event_id, kind: "work_item_close",
              payload: itemPayload, submitted_at: new Date().toISOString(),
              targetId: item.target_id, targetType: "work_item",
            },
          }));
        }
      } catch {
        const done = cascadeClosedItems.size;
        const total = items.length;
        status.textContent =
          `Closed ${done} of ${total} Items — the Category is still open. `
          + "Submit again to retry the rest.";
        submit.disabled = false;
        return;
      }
    }
    // #60 PR4-v2 / feedback 2.1: resolve the pseudo-kinds (place_new,
    // item_move) to their real wire kind by their tier selects. Everything
    // downstream (reshape, payload injection, the POSTed kind, optimistic
    // decoration) uses `wireKind`, never the pseudo.
    let wireKind: FeedbackKind = detailEditLast
      ? detailEditLast.kind
      : kind === "place_new"
        ? resolvePlaceNewKind(raw)
        : kind === "item_move"
          ? resolveItemMoveKind(raw)
          : kind === "item_merge_into"
            ? "work_item_merge"
            : kind === "action_merge_into"
              ? "action_merge"
              // Cascade-close: the children were closed in the pre-loop above;
              // the Item itself rides the wire as a plain work_item_close.
              : kind === "work_item_close_cascade"
                ? "work_item_close"
                // W3: the Items + Actions were closed in the pre-loop above; the
                // Category itself rides the wire as a plain thread_close (a
                // GATE_HINT_KIND → the generic POST attaches client_events).
                : kind === "thread_close_cascade"
                  ? "thread_close"
                  // W6: the Actions were closed in the pre-loop above; the Item
                  // itself rides the wire as a work_item_dismiss_closure_candidate
                  // (NOT a close) — it stays OPEN and the §6c close-nag is parked.
                  : kind === "work_item_mark_completed"
                    ? "work_item_dismiss_closure_candidate"
                    : kind;
    // #73 reverse merge: the pseudo lives on the CHILD; fold it UP into the
    // picked primary. Override the POST target to that primary (work_item_merge
    // folds the source — this child — INTO the target). Capture the primary's
    // label BEFORE resolveItemMergeInto drops the picker field, for the
    // optimistic "merged into X" closed_reason on the child.
    let effectiveTargetId = spec.targetId;
    let reverseMergePrimaryLabel = "";
    if (kind === "item_merge_into") {
      const primarySlug = String(raw.merge_primary_item ?? "").trim();
      const opt = (spec.itemOptions ?? []).find((i) => i.slug === primarySlug);
      if (opt) {
        reverseMergePrimaryLabel = [opt.display_number, opt.name]
          .filter(Boolean).join(" ");
      }
      const primaryTargetId = resolveItemMergeInto(raw, spec.targetId);
      if (primaryTargetId) effectiveTargetId = primaryTargetId;
    }
    // B1 / #73 reverse merge (Action tier): the pseudo lives on the CHILD
    // Action; fold it UP into the picked primary. Capture the primary's label
    // (from its full target id in actionOptions) BEFORE resolveActionMergeInto
    // drops the picker field, for the optimistic "Merged → X" pill on the child.
    // Override the POST target to the primary so action_merge folds the source
    // (this child) INTO it. resolveActionMergeInto seeds the child's full target
    // id as the sole source, so the existing cross-Item re-parent block below
    // moves the child into the primary's Item when they differ.
    if (kind === "action_merge_into") {
      const primaryId = String(raw.merge_primary_action ?? "").trim();
      const opt = (spec.actionOptions ?? [])
        .find((a) => (a.target_id ?? a.ai_slug) === primaryId);
      if (opt) {
        reverseMergePrimaryLabel = [opt.display_number, opt.text]
          .filter(Boolean).join(" ");
      }
      const primaryTargetId = resolveActionMergeInto(raw, spec.targetId);
      if (primaryTargetId) effectiveTargetId = primaryTargetId;
    }
    // #75: decode the deep-move destination ("Category › Sub-category"). May
    // redirect a same-Category pick to item_move_to_subcategory (no Category
    // move) or, for a cross-Category sub-cat, keep the move + chain a grouping
    // step after it. Runs BEFORE alsoCloseItem (which reads wireKind).
    const currentThreadSlug = spec.targetId.match(/^thread:([^/]+)/)?.[1] ?? "";
    const deepMove = planDeepMoveDestination(raw, currentThreadSlug, wireKind);
    wireKind = deepMove.wireKind;
    // B10: capture the "Move & Close" intent before reshape strips it.
    // FormData only includes a checkbox when checked; gated to closeable
    // Items. On wireKind so the combined Move form's Category tier carries it
    // (resolveItemMoveKind already pruned also_close on the Sub-category tier).
    const alsoCloseItem = wireKind === "item_move_to_category"
      && "also_close" in raw && spec.itemCloseable !== false;
    // B8 cross-Item action_merge: the merge picker offers Actions from other
    // Items, encoded as their full target id. Re-parent each cross-Item
    // source into the target's Item (action_move_to_item) BEFORE the merge,
    // then hand the merge plain ai_slugs (all siblings now). Fired here —
    // before reshape — so the moves are submitted ahead of the merge (absorb
    // order = submit order) and reshape's slug-coercion never sees a target
    // id. Mirrors B10's two-event submit. Each move bubbles a feedback-
    // submitted so it's tracked + (B7) retractable; the move's origin
    // breadcrumb rides former_display_number into next week's render.
    const mergeOriginWis: string[] = [];
    // Gate on wireKind (not kind) so the B1 reverse merge (action_merge_into →
    // action_merge) runs this cross-Item re-parent too: its sole source is the
    // CHILD's full target id, and the destination Item is the PRIMARY's —
    // derived from effectiveTargetId (which the reverse-merge block overrode to
    // the primary; for a forward action_merge it equals spec.targetId, so the
    // behaviour is unchanged). When child and primary already share an Item the
    // wi === targetWi check skips the move, exactly as a same-Item forward merge.
    if (wireKind === "action_merge") {
      const targetWi = effectiveTargetId.match(/work_item:([^/]+)/)?.[1] ?? "";
      const rawSources = Array.isArray(raw.source_action_item_slugs)
        ? (raw.source_action_item_slugs as string[])
        : raw.source_action_item_slugs != null
          ? [String(raw.source_action_item_slugs)]
          : [];
      const aiSlugs: string[] = [];
      for (const val of rawSources) {
        const isTargetId = val.includes("action_item:");
        const ai = isTargetId
          ? (val.match(/action_item:([^/]+)/)?.[1] ?? "")
          : val;
        const wi = isTargetId
          ? (val.match(/work_item:([^/]+)/)?.[1] ?? targetWi)
          : targetWi;
        if (!ai) continue;
        aiSlugs.push(ai);
        if (wi && wi !== targetWi) {
          mergeOriginWis.push(wi);
          const opt = (spec.actionOptions ?? []).find((o) => o.target_id === val);
          const movePayload: Record<string, unknown> = {
            to_work_item_slug: targetWi,
          };
          if (opt?.display_number) {
            movePayload.former_display_number = opt.display_number;
          }
          const moveRes: { event_id: string } = inFixtureMode()
            ? { event_id: crypto.randomUUID() }
            : await postJson<{ event_id: string }>(
                `/feedback/${encodeURIComponent(spec.runId)}`,
                {
                  kind: "action_move_to_item",
                  target: { type: "action_item", id: val },
                  payload: movePayload,
                },
              );
          document.dispatchEvent(new CustomEvent("feedback-submitted", {
            detail: {
              event_id: moveRes.event_id, kind: "action_move_to_item",
              payload: movePayload, submitted_at: new Date().toISOString(),
              targetId: val, targetType: "action_item",
            },
          }));
        }
      }
      // The merge now operates on plain ai_slugs, all under the target Item.
      raw.source_action_item_slugs = aiSlugs;
    }
    // #63: the move/place pickers now submit the friendly label; map it back
    // to the stable target slug BEFORE reshape (which ensureSlug()s the value).
    resolvePickedTargetSlugs(raw, spec.threadOptions, spec.itemOptions);
    // #60 PR4-v2: label→slug mapping for the Merge-Into-Existing pickers.
    if (wireKind === "merge_into_existing") {
      resolveMergeTargetPicks(
        raw, spec.mergeThreadOptions, spec.mergeItemOptions,
        spec.mergeActionOptions,
      );
    }
    // #38: mint the new sub-category's slug + disambiguated display name from
    // the typed name, collision-suffixed against the parent Category's groups
    // (open + closed) PLUS this session's pending adds — all carried in
    // spec.subcategoryOptions, which briefings.ts feeds from the optimistically-
    // mutated payload. Done before reshape so its slugify fallback sees the
    // minted slug and passes through. A collision suffixes BOTH (slug `-N` +
    // name " N") so two same-named groups are visually distinct (feedback 3.4).
    if (wireKind === "subcategory_add" && typeof raw.name === "string") {
      const minted = mintSubcategory(
        raw.name as string,
        (spec.subcategoryOptions ?? []).map((s) => s.slug),
      );
      raw.subcategory_slug = minted.slug;
      raw.name = minted.name;
    }
    const payload = reshapePayloadForKind(wireKind, raw, spec.targetId);
    // A①: thread the merge target's label into the event so the optimistic
    // "Recently Merged → into X" can name it + survive reload. The absorber
    // ignores unknown payload keys, so this is harmless on the agent side.
    if (
      (kind === "work_item_merge" || kind === "action_merge")
      && spec.mergeTargetLabel
    ) {
      (payload as Record<string, unknown>).target_label = spec.mergeTargetLabel;
    }
    // #73 reverse merge (Item + B1 Action tier): the primary is the PICKED
    // entity, not spec's (the menu is open on the child). Use its captured label
    // so the child grays as "Merged → <primary>".
    if (
      (kind === "item_merge_into" || kind === "action_merge_into")
      && reverseMergePrimaryLabel
    ) {
      (payload as Record<string, unknown>).target_label = reverseMergePrimaryLabel;
    }
    // B8: thread the cross-Item sources' origin Items so the optimistic
    // action_merge can gray them in place (they live under their origin Item
    // until next run's re-parent applies). Absorber ignores the key. wireKind
    // catches the B1 reverse merge (action_merge_into → action_merge), whose
    // single cross-Item source is the child being folded up.
    if (wireKind === "action_merge" && mergeOriginWis.length) {
      (payload as Record<string, unknown>).merge_origin_work_item_slugs =
        [...new Set(mergeOriginWis)];
    }
    injectQuestionContext(
      payload as Record<string, unknown>, wireKind, spec.questionContext,
    );
    // A³/A² (2026-06-03): inject the bucket entry's source_ref + id for the
    // Place tiers (not visible form fields). place_from_unfiled keys the
    // entry as `unfiled_id`; the new tiers use `entry_id`.
    if (
      (wireKind === "place_from_unfiled" || wireKind === "place_as_category"
        || wireKind === "place_as_action")
      && spec.bucketContext
    ) {
      const p = payload as Record<string, unknown>;
      p.source_ref = spec.bucketContext.sourceRef;
      if (wireKind === "place_from_unfiled") {
        p.unfiled_id = spec.bucketContext.entryId;
      } else {
        p.entry_id = spec.bucketContext.entryId;
      }
      // #1.4: a candidate-folded row retires its inferred thread on placement.
      if (spec.bucketContext.candidateSlug) {
        p.candidate_slug = spec.bucketContext.candidateSlug;
      }
    }
    // #60 PR4-v2 merge_into_existing: inject the entry's identity + text
    // snapshot (source_ref drives next-run bucket suppression; text becomes
    // the born-closed see-also note's text agent-side; candidate_slug retires
    // a candidate-folded row's inferred thread).
    if (wireKind === "merge_into_existing" && spec.bucketContext) {
      const p = payload as Record<string, unknown>;
      p.source_ref = spec.bucketContext.sourceRef;
      p.entry_id = spec.bucketContext.entryId;
      if (spec.bucketContext.entryText) p.text = spec.bucketContext.entryText;
      if (spec.bucketContext.candidateSlug) {
        p.candidate_slug = spec.bucketContext.candidateSlug;
      }
    }
    // not_relevant on a bucket entry rejects its INGEST source (the absorber
    // reads source_ref for unfiled/informational targets); informational
    // acknowledge stores the source_ref for audit. Both inject from context.
    if (
      (wireKind === "not_relevant" || wireKind === "informational_acknowledge")
      && spec.bucketContext
    ) {
      (payload as Record<string, unknown>).source_ref = spec.bucketContext.sourceRef;
    }
    // Decision 3.6: thread the moving entity's former path into the move
    // event so next week's render can show the "(formerly N.M.K, ...)"
    // breadcrumb. The absorber ignores it on legacy/absent → no crumb.
    // On wireKind so the combined Move form's Category tier gets its crumb;
    // a Sub-category move deliberately carries NONE (pinned policy 5 —
    // grouping never renumbers, so there is nothing "former" to show).
    if (
      (wireKind === "item_move_to_category" || wireKind === "action_move_to_item")
      && spec.movingFromDisplayNumber
    ) {
      (payload as Record<string, unknown>).former_display_number =
        spec.movingFromDisplayNumber;
    }
    try {
      const targetType =
        (wireKind === "comment" || wireKind === "missing_item")
        && spec.targetType !== "thread"
          ? "section"
          : spec.targetType;
      // Fixture mode: there's no Worker to talk to (the static server
      // returns 501 for POST). Short-circuit to a fake event_id so the
      // SPA's optimistic Submitted-list update + lifecycle UX is
      // exercisable in /?fixture=2. Mirrors the fetchFeedback / sendReceipt
      // fixture-mode stubs in app.ts.
      const body: Record<string, unknown> = {
        kind: wireKind,
        // #73: effectiveTargetId is the picked primary for an item_merge_into
        // (reverse merge); spec.targetId for every other kind.
        target: { type: targetType, id: effectiveTargetId },
        payload,
      };
      // #74a: on a gated kind, hand the Worker the recipient's own in-session
      // events so the close-gate replays a just-POSTed move-out that KV's
      // eventually-consistent list() may not have indexed yet — fixes the
      // "can't close a sub-category I just emptied" blocker. Transport-only.
      if (GATE_HINT_KINDS.has(wireKind)) {
        body.client_events = gateHintEvents(spec.runId);
      }
      const res: { event_id: string } = inFixtureMode()
        ? { event_id: crypto.randomUUID() }
        : await postJson<{ event_id: string }>(
            `/feedback/${encodeURIComponent(spec.runId)}`,
            body,
          );
      status.textContent = "Submitted. Thanks.";
      submit.textContent = "Done";
      form.querySelectorAll("input, textarea").forEach((el) => {
        (el as HTMLInputElement).disabled = true;
      });
      const event: SubmittedEvent = {
        event_id: res.event_id,
        kind: wireKind,
        payload,
        submitted_at: new Date().toISOString(),
        // B7: a just-submitted event is by definition not yet absorbed, so
        // it's immediately retractable.
        pending: true,
      };
      // Real-time feedback #1 (Part A): bubble the submission up to
      // renderRun so it can fold the event into the SHARED `feedback`
      // array. Without this, the optimistic Submitted-list + pending
      // count live only in this control's local closure and get wiped
      // by the next viewer re-render (KIL toggle / compactness chevron),
      // which rebuilds every control from the stale fetched snapshot.
      // Carries `target` (the local SubmittedEvent omits it) because the
      // shared index keys on target id. Dispatched FIRST so a re-render
      // triggered by feedback-state-changed below sees the pushed event.
      document.dispatchEvent(
        new CustomEvent("feedback-submitted", {
          detail: {
            event_id: event.event_id,
            kind: wireKind,
            payload,
            submitted_at: event.submitted_at,
            // #73: a reverse merge is recorded against the picked primary.
            targetId: effectiveTargetId,
            targetType: spec.targetType,
          },
        }),
      );
      // KIL events drive optimistic SPA-side payload mutation —
      // dispatch a CustomEvent the viewer (app.ts::renderRun) listens
      // for and flips `kil_marked` on the in-memory record before
      // re-rendering. The Worker side already mutates the kil-marks
      // snapshot for the KIL tab (worker/src/kil_mutate.ts); this
      // bubbles the same intent to the briefing-row badge so the
      // operator sees the orange "KIL" chip appear/disappear in the
      // same session.
      // Slice 2: keyed on `wireKind`, not `kind`, so a PSU toggle submitted via
      // the `action_report_inclusion` pseudo-kind (whose last event resolves to a
      // kil kind) still flips the row badge. For a direct kil kind wireKind ===
      // kind, so this is behavior-preserving.
      if (
        wireKind === "item_kil_mark" || wireKind === "item_kil_unmark"
        || wireKind === "action_kil_mark" || wireKind === "action_kil_unmark"
      ) {
        document.dispatchEvent(
          new CustomEvent("kil-mark-changed", {
            detail: { kind: wireKind, targetId: spec.targetId },
          }),
        );
      }
      // Real-time feedback #1 (Part B): lifecycle/priority kinds drive an
      // optimistic in-payload mutation (status / closure_candidate /
      // priority) the same way KIL does. renderRun applies it to the
      // in-memory record.payload and re-renders, so the Action/Item dims
      // + moves to Recently Closed, the "(Agent proposes closing)" suffix
      // clears, the Category gets its "Closed" pill, or the priority pill
      // re-colors — without waiting for the next cron.
      // On wireKind (not the pseudo): the combined Move form's Sub-category
      // tier resolves to item_move_to_subcategory, which IS optimistic — the
      // pseudo "item_move" never appears in OPTIMISTIC_STATE_KINDS. For every
      // non-pseudo kind wireKind === kind, so this is behavior-preserving.
      if (OPTIMISTIC_STATE_KINDS.includes(wireKind)) {
        document.dispatchEvent(
          new CustomEvent("feedback-state-changed", {
            // #73: effectiveTargetId = the picked primary for a reverse merge
            // (work_item_merge closes the source by payload slug, so the
            // applier is targetId-agnostic — this keeps the event coherent).
            detail: { kind: wireKind, targetId: effectiveTargetId, payload },
          }),
        );
      }
      // P-5 fan-out (Ray 2026-09-16): for a MOVE, show a bottom snackbar naming
      // the destination + offering Undo — the standard "Moved to X · Undo"
      // pattern (Gmail/Material). A move splices the row out of its old spot AND
      // the feedback-state-changed re-render above tears down THIS popover (its
      // anchor row is gone), so an in-popover confirmation would vanish — the
      // toast lives on document.body and survives. It's the move-confidence
      // signal when the destination row is scrolled off. Undo reuses the B7
      // retract (retractFeedbackEvent → feedback-retracted → rebuildOptimisticLayer
      // reverts the splice, so the row returns to its source). Only the two move
      // kinds — every other kind keeps the in-popover "Submitted. Thanks."
      const reorgMsg = reorgToastMessage(
        wireKind, payload as Record<string, unknown>, {
          itemOptions: spec.itemOptions,
          threadOptions: spec.threadOptions,
          subProjectOptions: spec.subProjectOptions,
          subcategoryOptions: spec.subcategoryOptions,
          mergeTargetLabel: spec.mergeTargetLabel || reverseMergePrimaryLabel,
        },
      );
      if (reorgMsg) {
        // The toast's Undo retracts only the PRIMARY event. A COMPOSITE op fires
        // more than one: an item deep move chains item_move_to_subcategory
        // (below), B10 "Move & Close" chains work_item_close, and a cross-Item
        // action_merge fires preceding action_move_to_item(s) (mergeOriginWis).
        // A single-event retract on those would partially revert, so name the
        // target but withhold Undo — the recipient reverts via the inverse action
        // / the per-row history Undo. Single-event ops (category re-band,
        // sub-category move, item merge, promote, plain move) keep Undo.
        const singleEvent = !alsoCloseItem && !deepMove.chainSubcategory
          && mergeOriginWis.length === 0;
        showMoveToast(
          reorgMsg,
          singleEvent
            ? () => retractFeedbackEvent(
                spec.runId, event.event_id, wireKind, effectiveTargetId)
            : null,
        );
      }
      // #75: a cross-Category deep move chains a grouping step — file the moved
      // Item into the destination Category's sub-category. POSTed after the move
      // so absorb order = submit order: the Item is in the destination Category
      // by the time the group move applies + is validated against it.
      if (deepMove.chainSubcategory) {
        const movedItemSlug = spec.targetId.match(/work_item:([^/]+)/)?.[1] ?? "";
        const groupTargetId =
          `thread:${deepMove.chainThread}/work_item:${movedItemSlug}`;
        const groupPayload: Record<string, unknown> = {
          to_subcategory_slug: deepMove.chainSubcategory,
        };
        const groupRes: { event_id: string } = inFixtureMode()
          ? { event_id: crypto.randomUUID() }
          : await postJson<{ event_id: string }>(
              `/feedback/${encodeURIComponent(spec.runId)}`,
              {
                kind: "item_move_to_subcategory",
                target: { type: "work_item", id: groupTargetId },
                payload: groupPayload,
                client_events: gateHintEvents(spec.runId),
              },
            );
        document.dispatchEvent(new CustomEvent("feedback-submitted", {
          detail: {
            event_id: groupRes.event_id, kind: "item_move_to_subcategory",
            payload: groupPayload, submitted_at: new Date().toISOString(),
            targetId: groupTargetId, targetType: "work_item",
          },
        }));
        document.dispatchEvent(new CustomEvent("feedback-state-changed", {
          detail: {
            kind: "item_move_to_subcategory", targetId: groupTargetId,
            payload: groupPayload,
          },
        }));
      }
      // B10: "Move & Close" — fire a second work_item_close after the move so
      // the (closeable, 0-open-Action) Item lands in the target Category's
      // Recently Closed. Same target; both events bubble + drive optimistics.
      if (alsoCloseItem) {
        const closePayload: Record<string, unknown> = {};
        const closeRes: { event_id: string } = inFixtureMode()
          ? { event_id: crypto.randomUUID() }
          : await postJson<{ event_id: string }>(
              `/feedback/${encodeURIComponent(spec.runId)}`,
              {
                kind: "work_item_close",
                target: { type: "work_item", id: spec.targetId },
                payload: closePayload,
                // #74a: the move was POSTed (and bubbled) just above; carry it
                // so this contingent close isn't blocked by KV list lag.
                client_events: gateHintEvents(spec.runId),
              },
            );
        document.dispatchEvent(new CustomEvent("feedback-submitted", {
          detail: {
            event_id: closeRes.event_id, kind: "work_item_close",
            payload: closePayload, submitted_at: new Date().toISOString(),
            targetId: spec.targetId, targetType: "work_item",
          },
        }));
        document.dispatchEvent(new CustomEvent("feedback-state-changed", {
          detail: {
            kind: "work_item_close", targetId: spec.targetId, payload: closePayload,
          },
        }));
      }
      // Cascade-close: the Item's own state-changed rode the generic
      // OPTIMISTIC dispatch above; now echo each child close so the Actions
      // gray into "Recently Closed Actions" in the same visual settle. Safe
      // here — every POST is done, so a re-render can no longer orphan an
      // in-flight loop.
      if (kind === "work_item_close_cascade") {
        const childReason = cascadeChildReason(spec.movingFromDisplayNumber);
        for (const id of cascadeClosedChildren) {
          document.dispatchEvent(new CustomEvent("feedback-state-changed", {
            detail: {
              kind: "action_close", targetId: id,
              payload: { reason: childReason },
            },
          }));
        }
      }
      // W6: the Item's dismiss (work_item_dismiss_closure_candidate) rode the generic
      // OPTIMISTIC dispatch above (it is in OPTIMISTIC_STATE_KINDS → clears the
      // "(Agent proposes closing)" suffix), and the Item stays OPEN. Now echo each
      // Action close so they gray into "Recently Closed Actions" in the same settle.
      if (kind === "work_item_mark_completed") {
        for (const id of cascadeClosedChildren) {
          document.dispatchEvent(new CustomEvent("feedback-state-changed", {
            detail: {
              kind: "action_close", targetId: id,
              payload: { reason: "Item work completed" },
            },
          }));
        }
      }
      // W3: the Category's own state-changed rode the generic OPTIMISTIC dispatch
      // above (thread_close ∈ OPTIMISTIC_STATE_KINDS); now echo each Item + Action
      // close so the whole subtree grays into "Recently Closed" in the same visual
      // settle. Safe here — every POST is done, so a re-render can't orphan the loop.
      if (kind === "thread_close_cascade") {
        const categoryItemReason = cascadeCategoryChildReason(spec.movingFromDisplayNumber);
        for (const id of cascadeClosedItems) {
          document.dispatchEvent(new CustomEvent("feedback-state-changed", {
            detail: {
              kind: "work_item_close", targetId: id,
              payload: { reason: categoryItemReason },
            },
          }));
        }
        for (const id of cascadeClosedChildren) {
          document.dispatchEvent(new CustomEvent("feedback-state-changed", {
            detail: { kind: "action_close", targetId: id, payload: {} },
          }));
        }
      }
      // Swap the popover back to the picker (now leads with this new entry
      // in the Submitted list) after a brief pause so the user can read
      // the success message.
      setTimeout(() => onSuccess(event), 1200);
    } catch (err) {
      // N1 (2026-07-14, adversarial trace): the one-click Accept path renders this
      // form hidden and requestSubmit()s it. On a POST failure the error + retry
      // Submit below would otherwise be painted inside a display:none form — a
      // blank popover with the merge silently not applied (false "succeeded",
      // against the #118 no-false-success bar). Un-hide so the message + Submit
      // are visible and retryable. No-op for the normal (already-visible) path.
      form.hidden = false;
      submit.disabled = false;
      if (err instanceof ApiError) {
        const body = err.body as { message?: unknown } | null;
        const msg = body && typeof body.message === "string" ? body.message : "";
        status.textContent = msg || `Error: ${err.status}`;
      } else {
        status.textContent = "Network error.";
      }
    }
  });

  return form;
}
